// Job detail + digital job sheet. Fully offline: the job (and its PPM checklist) is
// read from the cache, and completing it queues the sheet in the outbox (it syncs on
// reconnect). The engineer can fill this in a basement with no signal and it is never
// lost.
//
// The maintenance checklist is the structured per-lift-type PPM checklist bundled
// with the job from the server (so it is available offline). Each item is marked
// Pass / Fail / Advisory / N/A; a Fail or Advisory accepts a note and raises a
// Defect on completion (the server grades the severity). The free-text defect,
// advisory and engineer-note fields remain for additional observations.
import { cloneElement, isValidElement, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useEngineerData } from "./EngineerDataContext";
import WorkDaysPanel from "./WorkDaysPanel";
// Session AGG: extracted to its own file so the training screen signs attendees with the same pad.
import { SignaturePad } from "./SignaturePad";
import { SafedTestSheet } from "./SafedTestSheet";
import { ProjectSurveySheet } from "./ProjectSurveySheet";
import { InsuranceCheckItems, firstInsuranceGap, insurancePayload, seedInsuranceAnswers } from "./InsuranceCheckItems";
// Session AOB: the lone worker safety check-in panel, in its own file so its pure display helpers
// test without mounting the whole job sheet.
import { LoneWorkerCheckIn } from "./LoneWorkerCheckIn";
// Session APF: the POP tag's two in-flow steps ("tap the tag as you arrive", "tap it as you leave").
// A way of keeping time and nothing else: it shares no code, no state and no wording with the panel
// imported above, and popTagLoneWorkerSeparation.test.ts holds the two apart.
import { PopTagStep } from "./PopTagStep";
// Session APF: whether this phone can read a tag at all. PopTagStep renders NOTHING where it cannot,
// so the submit bar asks the same question before it offers to take the engineer to the leaving step
// (a button that scrolled to a card which is not on the screen would be a button that does nothing).
import { isPopTagScanSupported } from "../../lib/popTag";
import type {
  CompletionPayload,
  JobView,
  ChecklistTemplate,
  ChecklistStatus,
  CoveredItemStatus,
  CoveredItemCheck,
  LolerItemOutcome,
  LolerItemCheck,
  InsuranceAnswerDraft,
  ChecklistCategory,
  SignatureSkipReason,
  PartUsedEntry,
  QueuedPhoto,
  CompleteJobSheetDraftFields,
  JobSheetDraft,
  JobSheetDraftFields,
  TapStamp,
} from "../../lib/offline/types";
import { queuePhoto, queuedPhotosForJob, attachPhotoGeo } from "../../lib/offline/photosStore";
import { retryEscalated } from "../../lib/offline/jobsStore";
// Session APF: the per-job draft of the open sheet. Every call is best-effort by contract (see the
// head of draftStore.ts): none of them rejects, so the sheet needs no guard against a failed read
// or write. The load is caught once more where it is used all the same, because it is the one call
// with something waiting on it: saving stays shut until the restore attempt has settled.
import { loadDraft, saveDraft, clearDraft, draftFieldsWithDefaults, draftHasContent, draftHasWork } from "../../lib/offline/draftStore";
import { ContactValue } from "../../components/ContactValue";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { photoOrderFromDrag, dragHasFiles } from "../../lib/photoDrag";
import { getPositionSafe } from "../../lib/geo";
import { isRemedialJobType, remedialJobNoun } from "../../lib/jobType";
import JobTypeBadge from "../../components/JobTypeBadge";
import { StatusPill } from "../../components/StatusPill";
import { primaryStatePill } from "../../lib/statusRegistry";
import { api } from "../../lib/api";
import type { EngineerSiteDocument } from "../../lib/api";
import {
  LIFT_STATUS_OPTIONS,
  addDaysYmd,
  defaultVisitYmd,
  isoToLocalHHMM,
  isoToLocalYmd,
  localYmd,
  parseTypedTime,
  pastVisitCompletedAt,
  resolveVisitTimes,
  shiftHHMM,
  visitDayLabel,
  type ResolvedVisitTimes,
} from "../../lib/calloutSheet";
import {
  LATE_REASONS,
  LATE_REASON_CODES,
  LATE_REASON_TEXT_MAX,
  VISIT_DATE_HARD_DAYS,
  VISIT_DATE_SOFT_DAYS,
  calendarDaysBetween,
  lateReasonGiven,
  visitAgeBand,
  type LateReasonCode,
} from "../../lib/visitDateWindow";
import { buildChecklistResults, filterChecklistByDriveType, groupChecklistItems, firstFailMissingNote } from "../../lib/checklist";
import { buildAreaGroups, allAreasConfirmed, countFlaggedIssues, composeEngineerNotes } from "../../lib/serviceAreas";
import { filterChecklistByFrequency, frequencyLabelForVisits, checklistScopeLine, scopeLineSuffix } from "../../lib/checklistFrequency";
import { isImageFile, prepareImageForUpload } from "../../lib/imageUpload";
import { AreaChecklist } from "../../components/AreaChecklist";
// Session APF, THE DIRECTOR RULE (21 September 2026): no price, charge, rate or cost figure belongs
// on an engineer's job sheet. calculateCallOutCharge / pence and gbpExact (../../lib/format) fed the
// call-out form's "Indicative charge" box and the parts list's per-item cost and running total; both
// are gone with the figures they drew, so neither import is used here any more. Nothing else outside
// the engineer tree stopped needing calculateCallOutCharge/pence: the office's CalloutReportModal.tsx
// and CalloutChargeModal.tsx still import them directly from ../../lib/calloutCharge, so that module
// is untouched.
import { MapPin } from "lucide-react";

// Compose the site address line, appending the postcode ONLY when the address does not already end
// with it. Migrated addresses often already carry the postcode, which produced "RH4 1UP, RH4 1UP".
// Case- and whitespace-insensitive; used for BOTH the on-screen line and the maps destination so
// they can never disagree. Pure (unit tested).
// "Wed 3 Sep, 09:00" for the acceptance prompt, or a plain sentence when the job has no date (a
// repair, a condition report and a site inspection are all born undated). Local to this screen: the
// job list's own label is private to that file and this one wants the weekday, which matters when
// somebody is deciding whether they can actually be there.
export function scheduledForLabel(iso: string | null): string {
  if (!iso) return "No date set yet.";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "No date set yet.";
  return d.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

// Append a flagged line to the sheet's own "Other defects found (one per line)" box (Session ANU).
// That box is ONE DEFECT PER LINE (buildDefectRows splits on the array the sheet submits), so the
// join has to be a newline and the existing text has to keep its own lines intact: appending to a
// box the engineer has already half-filled must never merge two observations into one defect. Pure
// (unit tested) rather than inline, because "adds a line without eating the previous one" is
// exactly the kind of small string rule that quietly regresses.
export function appendDefectLine(current: string, line: string): string {
  const existing = current.replace(/\s+$/, "");
  const next = line.trim();
  if (!next) return current;
  return existing ? existing + "\n" + next : next;
}

export function siteAddressWithPostcode(address: string, postcode: string | null | undefined): string {
  const addr = address.trim();
  const pc = (postcode ?? "").trim();
  if (!pc) return addr;
  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
  return norm(addr).endsWith(norm(pc)) ? addr : `${addr}, ${pc}`;
}

// The key the server bundles the ELE-MS-002 equipment checklist under (mirrors the server
// EQUIPMENT_CHECKLIST_KEY). A care-equipment job whose bundled checklist carries this key is
// presented exactly as a lift checklist; anything else (a crossed/stale lift template) is ignored.
const EQUIPMENT_CHECKLIST_KEY = "care_equipment";

// The one client-facing ELE number, shown when a stuck job sheet needs the office (Session AEK).
const OFFICE_PHONE = "0203 576 4760";

// Fallback for a CARE EQUIPMENT job cached before the ELE-MS-002 checklist shipped (Session ACU):
// no bundled equipment checklist, so it completes as a plain job sheet (defects, notes, parts,
// photos, signature) submitting an empty checklist, exactly as it did under Session ACR. A stale
// cached LIFT checklist on an equipment job also falls back here (isEquipment wins).
const EQUIPMENT_FALLBACK_TEMPLATE: ChecklistTemplate = { liftType: "care_equipment", name: "Equipment service", items: [] };

// Session ALW: the template an OBSERVATIONAL visit falls back to when the server sent none -- an
// EMPTY one, so the sheet renders its free-text report and no checklist at all. Deliberately not
// FALLBACK_CHECKLIST below, which is a generic MAINTENANCE list: a site inspection is a look, and
// handing its engineer nine service checks to tick would impose a structure the Director explicitly
// did not want on it. The name is what the sheet shows beside the section heading.
const OBSERVATIONAL_FALLBACK_TEMPLATE: ChecklistTemplate = { liftType: "observational", name: "Written report", items: [] };

// Fallback for a job cached before checklists shipped (no bundled template). A
// generic list so an older cached job is still completable offline.
const FALLBACK_CHECKLIST: ChecklistTemplate = {
  liftType: "general",
  name: "General maintenance",
  items: [
    { id: "doors", description: "Car and landing doors", category: "safety" },
    { id: "door_op", description: "Door operation and safety edges", category: "safety" },
    { id: "alarm", description: "Emergency alarm and phone", category: "safety" },
    { id: "lighting", description: "Car and landing lighting", category: "electrical" },
    { id: "levelling", description: "Levelling accuracy", category: "mechanical" },
    { id: "ropes", description: "Ropes and suspension", category: "mechanical" },
    { id: "safety_gear", description: "Safety gear and overspeed governor", category: "safety" },
    { id: "controller", description: "Controller and machine room", category: "electrical" },
    { id: "general", description: "Lubrication and general condition", category: "mechanical" },
  ],
};

// The most photos kept per job, enforced here on the PWA and matched by the PDF embed cap
// (server MAX_PDF_PHOTOS) so the customer report shows every photo the engineer attached.
const MAX_JOB_PHOTOS = 6;

// Tap order for a checklist item. Everything defaults to Pass; a tap cycles to the next.
const CHECKLIST_CYCLE: ChecklistStatus[] = ["pass", "fail", "advisory", "na"];

// Per-status presentation: a subtle green tick for Pass, a prominent coloured chip for
// the exceptions (canonical: fail=danger, advisory=amber, na=purple).
// Session AQB (D78): the three answers for an additional item the contract covers. Deliberately the
// engineer's own words, not the checklist's: a gate is not inspected against ELE-MS-001, it is looked
// at. "Checked" is the default and needs no tap, so the routine visit is unchanged in length.
const COVERED_STATUS_OPTIONS: Array<{ value: CoveredItemStatus; label: string; on: string }> = [
  { value: "pass", label: "Checked", on: "border-ele-green text-ele-green" },
  { value: "fail", label: "Needs attention", on: "border-amber-400 text-amber-400" },
  { value: "na", label: "No access", on: "border-ele-field-focus text-ele-text" },
];

// Session BAQ: the three answers for an examiner's LOLER item. Nothing is pre-selected: an item the
// engineer did not answer is not sent, so an untouched item can never read as "checked".
const LOLER_OUTCOME_OPTIONS: Array<{ value: LolerItemOutcome; label: string; on: string }> = [
  { value: "checked", label: "Checked", on: "border-ele-green text-ele-green" },
  { value: "needs_work", label: "Needs work", on: "border-amber-400 text-amber-400" },
  { value: "no_access", label: "No access", on: "border-ele-field-focus text-ele-text" },
];
const LOLER_OUTCOME_LABEL: Record<LolerItemOutcome, string> = {
  checked: "Checked",
  needs_work: "Needs work",
  no_access: "No access",
};
const formatShortDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const STATUS_STYLE: Record<ChecklistStatus, { label: string; border: string; chip: string; chipText: string }> = {
  pass: { label: "Pass", border: "border-ele-green", chip: "", chipText: "text-ele-green" },
  fail: { label: "Fail", border: "border-ele-danger", chip: "bg-ele-danger", chipText: "text-ele-bg" },
  advisory: { label: "Advisory", border: "border-amber-500", chip: "bg-amber-500", chipText: "text-ele-bg" },
  na: { label: "N/A", border: "border-ele-purple", chip: "bg-ele-purple", chipText: "text-ele-white" },
};

const CATEGORY_ORDER: ChecklistCategory[] = ["safety", "mechanical", "electrical", "hydraulic", "documentation"];
const CATEGORY_LABEL: Record<string, string> = {
  safety: "Safety",
  mechanical: "Mechanical",
  electrical: "Electrical",
  hydraulic: "Hydraulic",
  documentation: "Documentation",
};
const categoryLabel = (c: string) => CATEGORY_LABEL[c] ?? "Other";

// Session ANT. One wording for both travel fields and both submit gates, so the engineer is told the
// same thing wherever the figure was typed. House style: no double hyphen on an engineer-facing
// string.
const TRAVEL_RANGE_ERROR = "Enter your travel time as a whole number of minutes, between 1 and 1440, or leave it blank.";

// Session APF. How long the sheet waits after the last change before writing its draft. Long enough
// that typing a sentence is one write rather than forty, short enough that there is next to nothing
// to lose; and whatever is still waiting is written at once when the sheet is left or the app is
// hidden, which is exactly when a debounce would otherwise cost the last few keystrokes.
const DRAFT_SAVE_DEBOUNCE_MS = 400;

// What a draft on the screen is compared with what is in the store by, so an unchanged sheet is
// never rewritten. The opened-at time is deliberately NOT part of it: it is not something the
// engineer enters, and it travels with the next real change.
function draftKey(areasFingerprint: string, fields: JobSheetDraftFields): string {
  return JSON.stringify([areasFingerprint, fields]);
}

// Session APF. Is this element one the engineer TYPES into (or picks a value in)? The submit bar at
// the foot of the sheet is fixed to the bottom of the screen, and wherever an Android browser makes
// room for the on-screen keyboard by shrinking the page, a fixed bottom bar rides up on top of the
// keyboard and sits over the very box being typed in. So the bar steps out of the way while one of
// these has focus, and only these: a text, number or time input, a textarea or a select. A checkbox,
// a radio button, a button-like input or the hidden photo picker opens no keyboard, so focusing one
// must NOT hide the bar (ticking "Out of hours" would otherwise make the submit button vanish). Pure
// (unit tested), and handed `unknown` because both of its callers hold an EventTarget that may be
// null or may not be an element at all.
const NON_TYPING_INPUT_TYPES = new Set(["checkbox", "radio", "button", "submit", "reset", "image", "file", "range", "color", "hidden"]);
export function isTypingField(el: unknown): boolean {
  if (!(el instanceof Element)) return false;
  if (el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  if (el.tagName !== "INPUT") return false;
  // An input with no type attribute is a text box.
  return !NON_TYPING_INPUT_TYPES.has((el.getAttribute("type") ?? "text").toLowerCase());
}

// Session APF. Is the on-screen keyboard actually SHOWING? Focus alone cannot answer that, and the
// difference is a trap the first version of the bar walked into: on Android, dismissing the keyboard
// with the back gesture leaves the box FOCUSED. The bar, hidden on focus, then stayed hidden with
// nothing on the screen to explain why, and since the bar is the only way to submit, an engineer who
// typed their travel minutes and swiped the keyboard away was left looking at a sheet with no submit
// button until they happened to tap something else.
//
// So the keyboard is detected directly. `fullHeight` is the tallest the page has been at this width
// (a keyboard that shrinks the PAGE, as older Android Chrome does, lowers innerHeight below it) and
// `visibleHeight` is the visual viewport (a keyboard that only covers the page, as Chrome has done
// since version 108, shrinks that instead). Either way the gap is the keyboard. 120px is far more
// than a collapsing address bar (about 56) and far less than any real keyboard. Pure (unit tested).
const KEYBOARD_MIN_PX = 120;
export function keyboardLooksOpen(fullHeight: number, visibleHeight: number): boolean {
  if (!Number.isFinite(fullHeight) || !Number.isFinite(visibleHeight)) return false;
  return fullHeight - visibleHeight > KEYBOARD_MIN_PX;
}

// An engineer who has asked their phone for reduced motion gets an instant jump instead of a smooth
// scroll. The same guard, for the same reason, as the Fault Assistant's own helper of this name
// (matchMedia is not implemented in every test environment); that one is private to its file.
function scrollBehaviour(): ScrollBehavior {
  const reduce =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return reduce ? "auto" : "smooth";
}

// Session BDA: a SAFed test job has its own phone flow (SafedTestSheet): one gate screen, then one test at a time. Every other
// job type takes the ordinary sheet below, untouched.
export function JobDetail() {
  const { id = "" } = useParams();
  const { jobById } = useEngineerData();
  if (jobById(id)?.type === "safed_test") return <SafedTestSheet jobId={id} />;
  if (jobById(id)?.type === "project_survey") return <ProjectSurveySheet jobId={id} />;
  return <JobDetailSheet />;
}

function JobDetailSheet() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { jobById, complete, loading, syncNow, online } = useEngineerData();

  // ---- Session ALW: an open sheet is never wiped by the office moving the job -------------------
  //
  // THE BUG THIS FIXES, confirmed by Session ALS's own adversarial review and left flagged there.
  // `jobById(id)` resolves from the LIVE /api/jobs/mine list on every render. If the office
  // reassigns, unassigns or completes the job while the engineer has a part-filled sheet open, that
  // job leaves the list, `job` becomes undefined, the reset effect below fires on the changed
  // `job?.id` and every captured field -- the checklist, the notes, the times, the signature -- is
  // gone. The engineer is standing on site and their work has vanished with no explanation.
  //
  // THE FIX. The last successfully resolved job for THIS id is retained in a ref, so `job` never
  // flips back to undefined for an id it has already resolved. The reset effect keys on `job?.id`,
  // which therefore stays stable, and the state survives. The ref is keyed by id so navigating to a
  // different job cannot show the previous one's data.
  //
  // WHAT THE ENGINEER IS TOLD. Silently carrying on would be its own kind of lie, so the banner
  // below says plainly that the office has moved the job, and the sheet stays fully usable: if the
  // work was done, it should still be submitted. The server refuses a completion for a job that is
  // no longer theirs with a 403, which the offline outbox escalates to "Needs the office" rather
  // than discarding -- so a submitted sheet reaches a human either way, and nothing is lost.
  //
  // Assigning a ref during render is the documented caching escape hatch and is idempotent, so a
  // StrictMode double render is harmless.
  const liveJob = jobById(id);
  const lastResolved = useRef<{ id: string; job: JobView } | null>(null);
  if (liveJob) lastResolved.current = { id, job: liveJob };
  const retainedJob = lastResolved.current?.id === id ? lastResolved.current.job : null;
  const job = liveJob ?? retainedJob;
  // True only when we HAD the job and then lost it -- never on a cold deep link, where the honest
  // answer is the existing "not in your list" message.
  const removedFromMyList = !liveJob && retainedJob != null;

  // A call-out is reactive, not a scheduled service: it has no PPM checklist and is
  // filled in with the fault found, work carried out, lift status and on-site times.
  const isCallout = job?.type === "callout";
  // A planned repair (Session AJW: create_planned_repair, or the Category B defect pipeline's
  // createRemedialJob) is ad hoc, not a scheduled PPM visit, and has no maintenance checklist any
  // more than a call-out does. Session ANG: so is a TECH VISIT, which is why this asks the shared
  // remedial question rather than naming one type. Left as `type === "repair"` it would have fallen
  // through to the SCHEDULED MAINTENANCE branch below and handed the engineer on a diagnostic
  // return visit the full 67-to-78-item ELE-MS-001 periodic checklist for that lift -- and, on an
  // uncached job, FALLBACK_CHECKLIST, which is itself a generic nine-item maintenance list. Those
  // ticks then print on the customer's certificate as if a service had been carried out.
  const isRepair = isRemedialJobType(job?.type);
  // The call-out-style capture form (fault found / work carried out / lift status / on-site times,
  // no checklist) is used for BOTH a call-out and a repair. Kept as its own name rather than folded
  // into isCallout because one affordance below (the Fault Assistant link, framed around reactive
  // diagnosis) deliberately stays call-out only: a planned repair already states what work is
  // needed in its own description, so that judgement call is left alone rather than widened here.
  const usesCalloutFields = isCallout || isRepair;
  // Session ALW: the two OBSERVATIONAL visits. Neither is a fault (call-out), known work (repair) or
  // scheduled maintenance, so neither takes the call-out form and neither takes the maintenance
  // checklist. They differ from each other in exactly one way on this screen: a CONDITION REPORT
  // carries the survey areas as a checklist (CONDITION_REPORT_AREAS, bundled with the job), because
  // the paper document it produces has those areas; a SITE INSPECTION carries none at all, because
  // the Director was explicit that it is a look written up in the engineer's own words.
  const isConditionReport = job?.type === "condition_report";
  const isSiteInspection = job?.type === "site_inspection";
  const isObservational = isConditionReport || isSiteInspection;
  // Session BAQ: a follow-up visit to work through the examiner's LOLER items. It has no maintenance
  // checklist (it is not a service) and takes the plain job sheet, with the LOLER items block below.
  const isLolerFollowUp = job?.type === "loler_follow_up";
  // Session BBF: an INSURANCE CHECK. The engineer checks the items an insurer's inspector reported (job.insuranceItems), beside the
  // lift's service. It has no maintenance checklist, no customer signature (it produces no customer document), no parts and no
  // price: it is never charged and nothing is sent to the customer.
  const isInsuranceCheck = job?.type === "insurance_check";
  // A care-equipment service visit. isEquipment WINS over any stale cached lift checklist so a
  // hoist never shows lift checks. Since Session ACU an equipment job carries the ELE-MS-002
  // checklist (bundled under EQUIPMENT_CHECKLIST_KEY): present it exactly as a lift checklist.
  // An equipment job with no equipment checklist bundled (a pre-ACU cache) or a crossed lift
  // template falls back to the plain job sheet (empty template).
  // An observational visit is never "an equipment service", whatever the asset is: a condition
  // report on a hoist is still a condition report, and letting isEquipment win here would hand the
  // engineer the ELE-MS-002 service checklist instead of the survey areas.
  const isEquipment = !usesCalloutFields && !isObservational && !isInsuranceCheck && job?.equipmentAsset === true;
  // Session ANG. A tech visit takes the repair's FORM but not its WORDS. Found by this session's own
  // closing review: widening isRepair gave a tech visit a heading of "Repair report" and a button
  // reading "Submit repair", directly under a job-type pill reading "TECH VISIT" -- the one screen
  // the attending engineer actually works from, contradicting itself.
  //
  // THIS IS NOT THE CUSTOMER-FACING RULE AND MUST NOT BE COLLAPSED INTO IT. The certificate, the
  // portal, the NPS survey and the public tag page all say "Repair" deliberately, because whether
  // the CUSTOMER sees an engineer grade is an open Director question. The ENGINEER is ELE's own, and
  // the entire reason the type exists is so ELE's own people and ELE's own timesheet can tell a tech
  // visit from a repair. remedialJobNoun, NOT jobTypeLabel: the latter returns the locked
  // colour-scheme label, which for a repair is "Remedial" -- right on a pill, wrong in a sentence
  // ("Remedial report", "Submit remedial"). Two existing tests had pinned the real wording, and
  // caught exactly that when the first draft of this fix reached for the pill's label.
  const remedialWord = remedialJobNoun(job?.type);
  const template = isLolerFollowUp
    ? OBSERVATIONAL_FALLBACK_TEMPLATE
    : isEquipment
    ? job?.checklist?.liftType === EQUIPMENT_CHECKLIST_KEY
      ? job.checklist
      : EQUIPMENT_FALLBACK_TEMPLATE
    : isObservational || isInsuranceCheck
      ? // Session ALW (and Session BBF for an insurance check, which has no checklist either): an observational visit falls back to an EMPTY template, never to
        // FALLBACK_CHECKLIST. That fallback exists for a job cached before checklists shipped and is
        // a generic nine-item MAINTENANCE list; a site inspection (which the server deliberately
        // sends no checklist for) would otherwise be asked to service the lift it came to look at.
        (job?.checklist ?? OBSERVATIONAL_FALLBACK_TEMPLATE)
      : (job?.checklist ?? FALLBACK_CHECKLIST);
  // The engineer is shown only the items that apply to this lift's drive type (B plus H
  // for a hydraulic lift, B plus E for an electric traction lift). The rest are recorded
  // as not applicable on the service sheet PDF. A null drive type (older cached job)
  // leaves the full list.
  const driveItems = useMemo(
    () => filterChecklistByDriveType(template.items, job?.driveType ?? null),
    [template, job?.driveType],
  );
  // Session AFF: frequency-filter the drive-type-filtered items to the periodic tasks DUE on this
  // visit, from the contract cadence (visitsPerYear) and which visit of the year it is
  // (visitNumberThisYear), both computed server-side. Identity when either is null (a call-out, an
  // equipment job, a lift with no cadence, or an older cached job) -> the full checklist, the safe
  // default. The engineer records ONLY these items and the stored sheet holds exactly them, so the
  // customer certificate shows what was due, never a not-due task as "N/A".
  const visibleItems = useMemo(
    () => filterChecklistByFrequency(driveItems, job?.visitsPerYear ?? null, job?.visitNumberThisYear ?? null),
    [driveItems, job?.visitsPerYear, job?.visitNumberThisYear],
  );
  // Whether a structured checklist is shown (a lift always has one; an equipment job has the
  // ELE-MS-002 checklist since Session ACU, but a pre-ACU cached equipment job has none and
  // completes as a plain job sheet). Drives the heading, intro copy and submit label.
  const hasChecklist = !usesCalloutFields && visibleItems.length > 0;
  // The 14-area flow (Session ADM): every LIFT service works at inspection-area level (the
  // fourteen ELE-MS-001 areas for a passenger lift; a goods/platform/scissor lift's own groups)
  // with an AFFIRMATIVE confirm per area and one tap for a clean visit. Equipment (ELE-MS-002)
  // keeps the itemised flow unchanged (Director default), as do call-outs.
  // The 14-area ELE-MS-001 flow is for a LIFT SERVICE. A condition report has a checklist but its
  // items are survey AREAS, not maintenance tasks, so it takes the itemised tap-cycle flow the
  // equipment checklist uses: tap a verdict, type the condition.
  const useAreaFlow = hasChecklist && !isEquipment && !isObservational && !isInsuranceCheck;
  // Session AFF: the scope line above the checklist ("Quarterly service: 24 items due this visit"),
  // shown only when this is a frequency-filtered lift service (both cadence fields present). null for
  // an equipment job, a call-out, a lift with no cadence, or an older cached job (full checklist).
  const scopeLine = useMemo(() => {
    const vpy = job?.visitsPerYear ?? null;
    const vn = job?.visitNumberThisYear ?? null;
    if (vpy == null || vn == null) return null;
    const label = frequencyLabelForVisits(vpy);
    if (!label) return null;
    return `${checklistScopeLine(label, visibleItems.length)}${scopeLineSuffix(vpy, vn)}`;
  }, [job?.visitsPerYear, job?.visitNumberThisYear, visibleItems.length]);

  const [statuses, setStatuses] = useState<Record<string, ChecklistStatus>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  // Session AQB (D78): the engineer's answers for the additional items the CONTRACT covers (gates,
  // doors, barriers, shutters that ride this lift's visits). Empty means every item is CHECKED, the
  // same fast default the checklist uses, so the routine visit costs no taps at all.
  const [coveredStatuses, setCoveredStatuses] = useState<Record<string, CoveredItemStatus>>({});
  const [coveredNotes, setCoveredNotes] = useState<Record<string, string>>({});
  // Session BBF: the engineer's answers for an insurance check's items, by item id. Nothing is pre-answered: an unanswered item
  // blocks the submit. Seeded from an earlier submission's answers (a job the office returned) when the job opens.
  const [insuranceAnswers, setInsuranceAnswers] = useState<Record<string, InsuranceAnswerDraft>>({});
  const insuranceItems = job?.insuranceItems ?? [];
  // The items THIS job's contract covers, as the server resolved them from the agreement's own
  // schedule. Absent on any job cached before D78 and on every contract that covers only lifts, and
  // the block below simply does not render.
  const coveredItems = job?.coveredItems ?? [];
  // Session BAQ: the examiner's LOLER items at this lift. NOTHING is pre-answered and nothing is sent
  // for an item the engineer leaves alone; the block never gates the submit.
  const [lolerAnswers, setLolerAnswers] = useState<Record<string, LolerItemOutcome>>({});
  const [lolerNotes, setLolerNotes] = useState<Record<string, string>>({});
  const lolerItems = job?.lolerItems ?? [];
  // Area-flow state (Session ADM): which areas the engineer has AFFIRMATIVELY confirmed, and
  // optional per-area notes. Nothing is recorded as passed until its area is confirmed; the
  // submit gate below blocks completion until every applicable area is.
  const [areaConfirmed, setAreaConfirmed] = useState<Record<string, boolean>>({});
  const [areaNotes, setAreaNotes] = useState<Record<string, string>>({});
  const [defectsText, setDefectsText] = useState("");
  const [advisory, setAdvisory] = useState("");
  const [notesText, setNotesText] = useState("");
  // A clear error shown when completion is blocked (a Fail item with no note describing it).
  const [checklistError, setChecklistError] = useState<string | null>(null);
  // The confirm step before submitting (the job cannot be edited after submission).
  const [confirmingSubmit, setConfirmingSubmit] = useState(false);
  // Session APF: the moment the confirm step was opened, ISO. The panel states the times that are
  // about to be recorded, and a blank Left resolves to "now", so "now" is pinned here when the panel
  // opens and the submit that follows uses the same instant. What the engineer was shown is then
  // exactly what is recorded, rather than a departure a minute later than the one they confirmed.
  const [confirmOpenedAt, setConfirmOpenedAt] = useState<string | null>(null);
  // When the engineer opened this job. A service or observational visit's arrival falls back to it
  // (departure to the submit instant), so the service sheet always shows a duration. A call-out, a
  // repair and a tech visit NEVER fall back to it: see resolveVisitTimes in lib/calloutSheet.ts.
  const openedAtRef = useRef<string>("");
  // The Arrived / Left fields of the Time on site card (Session ADJ): "HH:MM" values the engineer
  // marks with the Now button or types. They ride the completion payload, so they are offline-safe
  // through the existing outbox sync. SINCE SESSION APF THEY ARE THE TIME FIELDS FOR EVERY JOB TYPE:
  // a call-out and a repair used to keep a second pair behind two 65-option dropdowns inside their
  // report, which the PWA audit measured at about 16 seconds a sheet. The "svc" in the names is
  // history, kept because the draft store already spells them this way.
  const [svcArrival, setSvcArrival] = useState("");
  const [svcDeparture, setSvcDeparture] = useState("");
  // Session BAR: the day of the visit the engineer chose ("YYYY-MM-DD"), or "" for the sheet's
  // default (the booked day when that is today or earlier, else today: defaultVisitYmd). Every typed
  // time is put on this day, so a sheet finished the next morning records the visit when it happened.
  const [visitDateChoice, setVisitDateChoice] = useState("");
  // Session BBC: why a visit more than a week old is sent late (a LATE_REASONS code, or ""), and the
  // engineer's own words for Other. Asked only for such a visit; see LateReasonStep.
  const [lateReason, setLateReason] = useState("");
  const [lateReasonText, setLateReasonText] = useState("");
  // Session APF: the POP tag taps made from this sheet, one per end of the visit, or null when that
  // end was not tapped (no tag fitted, no NFC on the phone, no signal, or simply not done: a missing
  // tap never blocks anything). A stamp PRE-FILLS its time field when that field is empty and never
  // replaces what the engineer put there. While the field still shows the stamp's own minute the
  // visit is recorded at the tap's exact instant; once the engineer edits the field, what they typed
  // is the record. That rule lives in ONE place, resolveVisitTimes.
  const [tapArrival, setTapArrival] = useState<TapStamp | null>(null);
  const [tapDeparture, setTapDeparture] = useState<TapStamp | null>(null);
  // Per-job travel (Session ANT). Held as the raw strings the number inputs carry, so a part-typed
  // value is never coerced to a misleading number mid-keystroke; parsed once at submit.
  const [travelMinsText, setTravelMinsText] = useState("");
  const [returnTravelMinsText, setReturnTravelMinsText] = useState("");
  // Call-out sheet fields.
  const [ooh, setOoh] = useState(false);
  const [faultFound, setFaultFound] = useState("");
  const [workCarriedOut, setWorkCarriedOut] = useState("");
  const [liftStatus, setLiftStatus] = useState("");
  const [saving, setSaving] = useState(false);
  // Parts the engineer used on this job (van-stock pick or free-text).
  const [parts, setParts] = useState<PartUsedEntry[]>([]);
  // Customer signature: a PNG data URL once captured, or a skip reason when one
  // cannot be taken. The Complete button requires one or the other.
  const [signature, setSignature] = useState<string | null>(null);
  const [skipReason, setSkipReason] = useState<SignatureSkipReason | "">("");
  const [signerName, setSignerName] = useState(""); // who signed on site (Session AK Item 8)
  const [skipping, setSkipping] = useState(false);
  // Pre-work risk assessment (Session AK Item 9; made OPTIONAL Session AEH, Director decision D10):
  // the generic RAMS (ELE-RAMS-SVC-001) covers planned visits, so a per-visit assessment is opt-in
  // and never gates the sheet. raOpen tracks whether the optional section is expanded (collapsed by
  // default).
  const [raHazards, setRaHazards] = useState<Record<string, boolean>>({});
  const [raOther, setRaOther] = useState("");
  const [raControl, setRaControl] = useState("");
  const [raSignedAt, setRaSignedAt] = useState<string | null>(null);
  const [raError, setRaError] = useState<string | null>(null);
  const [raOpen, setRaOpen] = useState(false);
  // Session AFZ: RAMS acknowledgement. ramsAckLocal is an optimistic local flag so the gate opens the
  // instant the engineer ticks it (works offline); the server value (job.ramsAcknowledgedAt) is the
  // durable record. ramsBusy tracks the live POST; ramsError is a SOFT note (a failed live record
  // never blocks, since the completion payload records it). siteDocs is the best-effort site-document
  // list (online-only fetch; names come from Postgres so the RAMS itself never depends on it).
  // Session ALW: acceptance. acceptedLocal is the optimistic device-side flag, so the gate opens the
  // instant the engineer taps Accept even with no signal; the server value (job.acceptedAt) is the
  // durable record and the completion payload carries the fact as the offline fallback. Exactly the
  // shape the RAMS acknowledgement below already uses, and for the same reason: a client-side gate
  // with no offline recording path WEDGES the engineer it is meant to protect.
  // Session APF (Director decision, 21 September 2026): THE TWO FLAGS ARE NORMALLY SET TOGETHER NOW.
  // Accepting a job whose RAMS is shown on the accept screen records the acceptance AND the RAMS
  // acknowledgement from the one tap (see acceptJob), so ramsAckLocal is usually set there and the
  // tick on the open sheet is the fallback for a job that reached the sheet some other way. They stay
  // two flags because they are two facts, recorded in two columns, and either can be given without
  // the other: a job cached with no RAMS to show is accepted with no acknowledgement, and a job
  // accepted on an older build is acknowledged later, by the tick, without being accepted again.
  const [acceptedLocal, setAcceptedLocal] = useState(false);
  const [acceptBusy, setAcceptBusy] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [decliningOpen, setDecliningOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [ramsAckLocal, setRamsAckLocal] = useState(false);
  const [ramsBusy, setRamsBusy] = useState(false);
  const [ramsError, setRamsError] = useState<string | null>(null);
  const [siteDocs, setSiteDocs] = useState<EngineerSiteDocument[] | null>(null);

  // ---- Session APF: the open sheet is kept as a per-job draft --------------------------------------
  //
  // THE BUG THIS FIXES (docs/reports/PWA-AUDIT-2026-09-21.md, fix list item 4). Everything above is
  // React component state and nothing else, so anything that unmounted this screen took the whole
  // visit's entries with it: a tap on the bottom navigation, the Fault Assistant link, the camera
  // pushing the tab out of memory, or the phone being locked for an hour mid-visit and the PWA being
  // evicted. Session ALW (top of this component) stopped the OFFICE wiping an open sheet; this
  // stops everything else, by writing the sheet to IndexedDB as it is filled in and putting it back
  // when the job is reopened. The three effects that do it sit further down, below the fingerprint
  // guard they have to agree with.
  //
  // TO ADD A FIELD TO THE DRAFT, touch four places: JobSheetDraftFields in lib/offline/types.ts,
  // draftFieldsWithDefaults in lib/offline/draftStore.ts, and the draftFields snapshot and the
  // restore block below. The first two make the compiler insist on the third; the fourth is on you.
  //
  // draftReadyFor is the job id whose restore attempt has SETTLED, whether or not a draft was found.
  // Nothing is ever saved before that: the reset effect below blanks every field whenever a job is
  // opened, and saving that blank state would overwrite the very draft that is about to be loaded.
  // It is a job id rather than a flag ON PURPOSE. When the job changes, the save effect runs in the
  // same pass as the reset and restore effects, before any of their state updates can be seen: the
  // fields it holds are still the PREVIOUS job's, and a plain `true` left over from that job would
  // let it queue them under the new job's id. An id left over from that job simply does not match.
  const [draftReadyFor, setDraftReadyFor] = useState<string | null>(null);
  // Shows the "we kept what you had already entered" note. Dismissible, never persisted.
  const [draftRestored, setDraftRestored] = useState(false);
  // The signature image a draft put back, if any. SignaturePad is an uncontrolled canvas (and is
  // shared with the training screen, so it is not changed here): handed a restored data URL it would
  // show an EMPTY pad while the sheet held a signature. So while `signature` is still this restored
  // image the sheet shows the image itself, with one way out, "Sign again". Compared by value rather
  // than tracked as a flag, so every existing path that clears or replaces the signature ends the
  // restored state on its own and a freshly drawn signature behaves exactly as it always has.
  const [restoredSignature, setRestoredSignature] = useState<string | null>(null);
  // The write waiting on its debounce (draft null = delete the stored row), and its timer. savedAt is
  // left off on purpose: it is stamped when the write happens, not when it was scheduled.
  const pendingDraftRef = useRef<{ jobId: string; key: string | null; draft: Omit<JobSheetDraft, "savedAt"> | null } | null>(null);
  const draftTimerRef = useRef<number | null>(null);
  // Whether the store is believed to hold a row for this job, and what it holds (see draftKey).
  const draftStoredRef = useRef(false);
  const draftKeyRef = useRef<string | null>(null);
  // Set to the job id once its draft has been discarded for good (submitted, or declined), so that
  // nothing which re-renders the sheet on its way off the screen can write the draft back.
  const draftClosedRef = useRef<string | null>(null);

  // Initialise (and reset when the job changes) the checklist state. Statuses start EMPTY:
  // an item with no recorded status is a pass ONLY once the engineer has confirmed its area
  // (the area flow's affirmative gate); the equipment flow's tap-cycle reads the same default.
  useEffect(() => {
    setStatuses({});
    setNotes({});
    setCoveredStatuses({});
    setCoveredNotes({});
    setLolerAnswers({});
    setLolerNotes({});
    setInsuranceAnswers(seedInsuranceAnswers(job?.insuranceItems ?? []));
    setAreaConfirmed({});
    setAreaNotes({});
    setChecklistError(null);
    setConfirmingSubmit(false);
    setConfirmOpenedAt(null);
    // Note the moment the engineer opens the job. A service or observational visit's arrival falls
    // back to it (and its departure to the submit instant), so the service sheet shows a duration.
    openedAtRef.current = new Date().toISOString();
    setSvcArrival("");
    setSvcDeparture("");
    setVisitDateChoice("");
    // Session APF: a tap belongs to the job it was made on, exactly as the times it pre-filled do.
    setTapArrival(null);
    setTapDeparture(null);
    setTravelMinsText("");
    setReturnTravelMinsText("");
    setDefectsText("");
    setAdvisory("");
    setNotesText("");
    // Default the OOH toggle to the basis the call-out was logged on, so the first hour
    // (already billed at intake) and any additional labour share one OOH basis.
    setOoh(job?.calloutOutOfHours ?? false);
    setFaultFound("");
    setWorkCarriedOut("");
    setLiftStatus("");
    setParts([]);
    setSignature(null);
    setSkipReason("");
    setSignerName("");
    setSkipping(false);
    setRaHazards({});
    setRaOther("");
    setRaControl("");
    setRaSignedAt(null);
    setRaError(null);
    setRaOpen(false);
    // The server value (job.ramsAcknowledgedAt) carries the durable acknowledgement state across a
    // job change, so the local optimistic flag resets to false.
    setRamsAckLocal(false);
    setRamsError(null);
    // The server value (job.acceptedAt) carries the durable acceptance across a job change, so the
    // optimistic local flag resets to false alongside the RAMS one.
    setAcceptedLocal(false);
    setAcceptBusy(false);
    setAcceptError(null);
    setDecliningOpen(false);
    setDeclineReason("");
    // Session APF: a restored-draft note and a restored signature image belong to the job they were
    // restored for. The draft itself is loaded by the restore effect further down, which runs AFTER
    // this one, so what it puts back is never blanked by the lines above.
    setDraftRestored(false);
    setRestoredSignature(null);
    // Only re-run when the job identity changes (not on every template ref change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  // Session AFZ: load the job's site documents (best-effort, online only). The names come from
  // Postgres, so an offline engineer simply sees no list here while the RAMS itself (a static file)
  // still opens. Keyed on the job id + connectivity; ignores its own errors.
  useEffect(() => {
    if (!id || !online) {
      setSiteDocs(null);
      return;
    }
    let cancelled = false;
    api.jobs
      .siteDocuments(id)
      .then((r) => {
        if (!cancelled) setSiteDocs(r.documents);
      })
      .catch(() => {
        if (!cancelled) setSiteDocs(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, online]);

  // Group the (drive-type-filtered) items by section for the comprehensive maintenance
  // checklist, or by category for the thin per-lift-type templates. The engineer works
  // through one section at a time (the equipment flow renders these directly).
  const groups = useMemo(() => groupChecklistItems(visibleItems, categoryLabel, CATEGORY_ORDER), [visibleItems]);
  // The inspection areas for the area flow (Session ADM): the fourteen ELE-MS-001 areas for a
  // passenger lift (drive-filtered; an area with nothing applicable shows N/A), else the
  // template's own groups. Items never drop out: anything unmapped lands in "Other checks".
  const areas = useMemo(() => buildAreaGroups(visibleItems, groups), [visibleItems, groups]);
  // Integrity guard (Session ADM adversarial-review fix): a confirmation attests to a SPECIFIC
  // set of items. If the bundled checklist changes under an open sheet (a background job refresh
  // can deliver a template change mid-visit), any standing confirmations are cleared so an item
  // the engineer never saw can never ride a stale confirmation into the record as a pass. The
  // fingerprint is content-based, so the routine refresh (same items, new object) never resets.
  const areasFingerprint = useMemo(() => areas.map((a) => `${a.key}:${a.items.map((i) => i.id).join(",")}`).join("|"), [areas]);
  const prevAreasFingerprint = useRef(areasFingerprint);
  useEffect(() => {
    if (prevAreasFingerprint.current === areasFingerprint) return;
    prevAreasFingerprint.current = areasFingerprint;
    setAreaConfirmed((c) => (Object.keys(c).length > 0 ? {} : c));
  }, [areasFingerprint]);

  const done = useMemo(() => !!job && (job.pendingSync || job.status === "complete"), [job]);

  // Count of items the engineer has flagged, shown so nothing is missed. The area flow counts
  // genuine problems only (Fail/Advisory; an item marked N/A is recorded, not an "issue");
  // the equipment flow keeps its original any-non-pass count.
  const flaggedCount = useMemo(() => {
    if (usesCalloutFields) return 0;
    if (useAreaFlow) return countFlaggedIssues(visibleItems, statuses);
    return visibleItems.filter((i) => (statuses[i.id] ?? "pass") !== "pass").length;
  }, [usesCalloutFields, useAreaFlow, visibleItems, statuses]);

  // Whether every applicable inspection area has been affirmatively confirmed (the area flow's
  // integrity gate: nothing is recorded passed without a deliberate engineer action).
  const areasAllConfirmed = useMemo(
    () => (useAreaFlow ? allAreasConfirmed(areas, areaConfirmed) : true),
    [useAreaFlow, areas, areaConfirmed],
  );

  // ---- Session APF: the per-job draft (its state, and why it exists, are declared further up) ------

  const jobId = job?.id ?? null;
  // The one draft field whose "nothing entered" value is not a constant: the reset effect starts the
  // out-of-hours toggle on the basis the call-out was logged on.
  const oohDefault = job?.calloutOutOfHours ?? false;

  // What the restore has to know about the sheet AT THE MOMENT THE DRAFT ARRIVES, which is a tick or
  // more after the render that asked for it. Kept in a ref the promise callback can read, and
  // refreshed after every commit, so it always describes the sheet that is actually on the screen.
  const sheetFactsRef = useRef({ done, areasFingerprint, oohDefault });
  useEffect(() => {
    sheetFactsRef.current = { done, areasFingerprint, oohDefault };
  });

  // The whole draftable sheet as one object. Typed as the COMPLETE shape, so a field added to
  // JobSheetDraftFields will not compile here until it is actually being saved.
  const draftFields = useMemo<CompleteJobSheetDraftFields>(
    () => ({
      statuses,
      notes,
      coveredStatuses,
      coveredNotes,
      lolerAnswers,
      lolerNotes,
      insuranceAnswers,
      areaConfirmed,
      areaNotes,
      defectsText,
      advisory,
      notesText,
      svcArrival,
      svcDeparture,
      visitDateYmd: visitDateChoice,
      lateReason,
      lateReasonText,
      tapArrival,
      tapDeparture,
      travelMinsText,
      returnTravelMinsText,
      ooh,
      faultFound,
      workCarriedOut,
      liftStatus,
      parts,
      signature,
      skipReason,
      signerName,
      skipping,
      raHazards,
      raOther,
      raControl,
      raSignedAt,
      acceptedLocal,
      ramsAckLocal,
    }),
    [
      statuses,
      notes,
      coveredStatuses,
      coveredNotes,
      lolerAnswers,
      lolerNotes,
      insuranceAnswers,
      areaConfirmed,
      areaNotes,
      defectsText,
      advisory,
      notesText,
      svcArrival,
      svcDeparture,
      visitDateChoice,
      lateReason,
      lateReasonText,
      tapArrival,
      tapDeparture,
      travelMinsText,
      returnTravelMinsText,
      ooh,
      faultFound,
      workCarriedOut,
      liftStatus,
      parts,
      signature,
      skipReason,
      signerName,
      skipping,
      raHazards,
      raOther,
      raControl,
      raSignedAt,
      acceptedLocal,
      ramsAckLocal,
    ],
  );

  // Write whatever is waiting on the debounce, now. Used by the timer itself, and by every moment at
  // which waiting would lose it: leaving the sheet, the app being hidden, the page being torn down.
  // The stamp is taken here rather than when the write was scheduled, so savedAt means "written".
  const flushDraft = useCallback(() => {
    if (draftTimerRef.current != null) {
      window.clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    const pending = pendingDraftRef.current;
    pendingDraftRef.current = null;
    if (!pending) return;
    draftStoredRef.current = pending.draft != null;
    draftKeyRef.current = pending.key;
    if (pending.draft) void saveDraft({ ...pending.draft, savedAt: new Date().toISOString() });
    else void clearDraft(pending.jobId);
  }, []);

  // The draft has done its job (the sheet is in the outbox, or the job has been handed back), or the
  // job turned out to be finished. Drops the stored row AND anything still waiting to be written,
  // which matters as much: a write that landed after the delete would bring the draft back.
  // `forGood` also shuts the draft for the rest of this visit to the screen (draftClosedRef). It is
  // NOT used when the job merely reads as finished, because the office can return a finished job
  // for review while this screen is still open, and the sheet that reopens must be able to draft.
  const discardDraft = useCallback((forJobId: string, forGood: boolean) => {
    if (draftTimerRef.current != null) {
      window.clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    pendingDraftRef.current = null;
    draftStoredRef.current = false;
    draftKeyRef.current = null;
    if (forGood) draftClosedRef.current = forJobId;
    void clearDraft(forJobId);
  }, []);

  // RESTORE. Declared after the reset effect, so on opening a job the fields are blanked first and
  // the draft is put back on top, never the other way round. The load is asynchronous and the job
  // can change while it is in flight, hence the cancelled flag (the siteDocs effect above does the
  // same): a draft for the job just left must never be applied to the job just opened.
  useEffect(() => {
    // A write still waiting on its debounce belongs to the job being LEFT. It carries its own job
    // id, so it is written now, before this job's bookkeeping replaces the refs beneath it.
    flushDraft();
    draftClosedRef.current = null;
    draftStoredRef.current = false;
    draftKeyRef.current = null;
    setDraftReadyFor(null);
    if (!jobId) return;
    let cancelled = false;
    void loadDraft(jobId)
      .catch(() => undefined) // loadDraft never rejects by contract; a draft must not be able to break the sheet even if that slips
      .then((draft) => {
        if (cancelled) return;
        try {
          if (!draft) return;
          const facts = sheetFactsRef.current;
          if (facts.done) {
            // A submitted or finished job has nothing left to fill in. Its draft is ignored, and
            // cleared so it cannot be offered again.
            void clearDraft(jobId);
            return;
          }
          const f = draftFieldsWithDefaults(draft.fields, facts.oohDefault);
          setStatuses(f.statuses);
          setNotes(f.notes);
          setCoveredStatuses(f.coveredStatuses);
          setCoveredNotes(f.coveredNotes);
          setLolerAnswers(f.lolerAnswers);
          setLolerNotes(f.lolerNotes);
          // Session BBF: only when the draft holds answers, so an empty record never wipes what an earlier submission seeded.
          if (Object.keys(f.insuranceAnswers).length > 0) setInsuranceAnswers(f.insuranceAnswers);
          // A confirmation attests to a SPECIFIC set of checklist items (the integrity guard above).
          // So the confirmations come back only if the checklist is still the one they were given
          // against; if it has changed since, everything else is restored and the engineer confirms
          // the areas again, which is one tap. The fingerprint effect cannot then wipe what is
          // restored here: it clears only when the fingerprint CHANGES, and these were checked
          // against the fingerprint it has already seen. A checklist that changes later still
          // clears them, exactly as it would had they been tapped in this session.
          if (draft.areasFingerprint === facts.areasFingerprint) setAreaConfirmed(f.areaConfirmed);
          setAreaNotes(f.areaNotes);
          setDefectsText(f.defectsText);
          setAdvisory(f.advisory);
          setNotesText(f.notesText);
          setSvcArrival(f.svcArrival);
          setSvcDeparture(f.svcDeparture);
          setVisitDateChoice(f.visitDateYmd);
          setLateReason(f.lateReason);
          setLateReasonText(f.lateReasonText);
          // The POP tag taps come back with the times they pre-filled, whether or not the lift
          // still reads as tagged: a tap that was made stays made, and while its field still shows
          // its minute it is the tap's exact instant that gets recorded.
          setTapArrival(f.tapArrival);
          setTapDeparture(f.tapDeparture);
          setTravelMinsText(f.travelMinsText);
          setReturnTravelMinsText(f.returnTravelMinsText);
          setOoh(f.ooh);
          setFaultFound(f.faultFound);
          setWorkCarriedOut(f.workCarriedOut);
          setLiftStatus(f.liftStatus);
          setParts(f.parts);
          setSignature(f.signature);
          setRestoredSignature(f.signature);
          setSkipReason(f.skipReason);
          setSignerName(f.signerName);
          setSkipping(f.skipping);
          setRaHazards(f.raHazards);
          setRaOther(f.raOther);
          setRaControl(f.raControl);
          setRaSignedAt(f.raSignedAt);
          // A half-filled assessment that was never signed off comes back OPEN, the way it was left.
          // Put back collapsed it would look as though it had been lost.
          if (!f.raSignedAt && (Object.values(f.raHazards).some(Boolean) || f.raOther.trim() || f.raControl.trim())) setRaOpen(true);
          // The two on-device acknowledgements only ever go from no to yes here. If the engineer
          // tapped Accept in the instant before the draft arrived, a draft that predates the tap must
          // not shut the gate on them again.
          setAcceptedLocal((cur) => cur || f.acceptedLocal);
          setRamsAckLocal((cur) => cur || f.ramsAckLocal);

          // THE ARRIVAL TIME SURVIVES TOO. A service visit's arrival falls back to when the sheet was
          // opened, so a sheet reopened an hour into a visit has to carry the ORIGINAL time or the
          // record shows a two-minute visit. One exception: a draft holding nothing but the
          // acceptance. Jobs are accepted when they are assigned, often the evening before, and
          // that opening is not an arrival; the time stays with this opening until there is work on
          // the sheet for it to belong to.
          //
          // Session APF: AND THE RAMS ACKNOWLEDGEMENT GOES WITH THE ACCEPTANCE HERE. Since the two
          // were folded into one tap on the accept screen, "a draft holding nothing but the
          // acceptance" holds ramsAckLocal as well, and draftHasWork leaves both out for the same
          // reason (its own comment says why). Both are still restored just above, and
          // draftHasContent still counts both on the save side, so neither is ever lost.
          const hasWork = draftHasWork(f, facts.oohDefault);
          if (hasWork && typeof draft.openedAt === "string" && Number.isFinite(Date.parse(draft.openedAt))) {
            openedAtRef.current = draft.openedAt;
          }
          // The same test decides the note: "we kept what you had entered" would be a strange thing
          // to say over a sheet on which the only thing kept is that the gate stays open.
          setDraftRestored(hasWork);
          draftStoredRef.current = true;
          draftKeyRef.current = draftKey(draft.areasFingerprint, draft.fields);
        } catch {
          // A draft that cannot be put back is simply not put back. The sheet is as usable as it was
          // before there were drafts, and the next change writes a fresh one over it.
        } finally {
          // Settled, draft or no draft. From here on the sheet may be saved.
          setDraftReadyFor(jobId);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, flushDraft]);

  // SAVE, debounced, whenever anything draftable changes. Never before the restore attempt for THIS
  // job has settled, never for a finished job, and never for a sheet on which nothing at all has
  // been entered or accepted (merely opening a job to read its address leaves nothing behind).
  useEffect(() => {
    if (!jobId || draftReadyFor !== jobId || draftClosedRef.current === jobId) return;
    if (done) {
      // Finished while it was open (the office completed it, or it synced from elsewhere). Whatever
      // was being drafted has nowhere left to go.
      if (draftStoredRef.current || pendingDraftRef.current) discardDraft(jobId, false);
      return;
    }
    const key = draftKey(areasFingerprint, draftFields);
    const hasContent = draftHasContent(draftFields, oohDefault);
    if (key === draftKeyRef.current || (!hasContent && !draftStoredRef.current)) {
      // The store already matches the screen (a draft just restored and not yet touched, or a change
      // undone inside the debounce), or there is nothing to store and nothing stored.
      if (draftTimerRef.current != null) {
        window.clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
      }
      pendingDraftRef.current = null;
      return;
    }
    // Something to write. When there is NO content but a row IS stored, the engineer has cleared
    // everything they had entered, and the write is a delete: a row left behind would put the
    // cleared text back on the next open.
    pendingDraftRef.current = {
      jobId,
      key: hasContent ? key : null,
      draft: hasContent ? { jobId, openedAt: openedAtRef.current, areasFingerprint, fields: draftFields } : null,
    };
    if (draftTimerRef.current != null) window.clearTimeout(draftTimerRef.current);
    draftTimerRef.current = window.setTimeout(flushDraft, DRAFT_SAVE_DEBOUNCE_MS);
  }, [jobId, draftReadyFor, done, draftFields, areasFingerprint, oohDefault, flushDraft, discardDraft]);

  // FLUSH when waiting would lose it. A phone that locks, or an app sent to the background, may never
  // run another line of this page: the browser can evict a hidden PWA without warning, which is the
  // very case the draft exists for. So the pending write goes the moment the page is hidden, and
  // again on the way out of this screen (a bottom-nav tap inside the debounce window).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushDraft();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flushDraft);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flushDraft);
      flushDraft();
    };
  }, [flushDraft]);

  // ---- Session APG: taps recorded from somewhere other than this sheet ---------------------------------
  //
  // This sheet learns of a POP tag tap from its own two tag steps and from nowhere else, so a tap made
  // from the Home screen's scan button would be invisible here: the sheet would go on asking for an
  // arrival the platform already holds, and a second tap from it would write a second arrival. The job
  // carries the recorded instants (arrivalTapAt and departureTapAt: derived live on the server, and put
  // on the cached job the moment a Home tap lands), and this hands them to the sheet exactly as its own
  // step would have: as a stamp, which shows that end as recorded and pre-fills its time field if that
  // field is empty. It never replaces a time the engineer typed (the same functional update as a tap
  // from the sheet), and never replaces a stamp the sheet already has (a draft's own tap, or one this
  // sheet has just made), which is why it waits for the draft restore to settle and asks each end only
  // while that end has no stamp. A tap made on ANOTHER PHONE, or before this sheet was ever opened,
  // arrives the same way, on the next job-list refresh.
  const recordedArrivalAt = job?.arrivalTapAt ?? null;
  const recordedDepartureAt = job?.departureTapAt ?? null;
  useEffect(() => {
    if (!jobId || draftReadyFor !== jobId || done) return;
    if (recordedArrivalAt && tapArrival == null) onArrivalStamp({ at: recordedArrivalAt, live: true });
    if (recordedDepartureAt && tapDeparture == null) onDepartureStamp({ at: recordedDepartureAt, live: true });
    // onArrivalStamp and onDepartureStamp are recreated on every render and only ever set state; what
    // must re-run this is the inputs listed, never the functions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, draftReadyFor, done, recordedArrivalAt, recordedDepartureAt, tapArrival, tapDeparture]);

  // ---- Session APF: the submit bar (the bar itself, and why it exists, is SubmitBar further down) ----
  //
  // Whether this phone can read a POP tag at all. Asked once, as PopTagStep's own hook asks it: the
  // answer cannot change while the screen is open.
  const [popTagSupported] = useState(() => isPopTagScanSupported());
  // The two places the bar's first button can take the engineer: the leaving POP tag step, and the
  // customer signature directly beneath it.
  const leavingTagRef = useRef<HTMLDivElement | null>(null);
  const signatureSectionRef = useRef<HTMLElement | null>(null);

  // THE BAR STEPS ASIDE WHILE THE ENGINEER IS TYPING (see isTypingField for why). Focus is followed
  // with focusin / focusout on the sheet's own root, which bubble, so one pair of listeners covers
  // every field on the sheet, including the ones inside the checklist, the parts list and the flag
  // panel, and any field a later session adds.
  //
  // The root is held in STATE through a callback ref, not in a plain ref, on purpose: a deep-linked
  // job renders the no-job branch first (there is no sheet root yet) and the sheet only later, and an
  // effect keyed on a plain ref would have run once, found nothing, and never attached a listener.
  //
  // focusout reads relatedTarget (where focus is GOING), so moving from one box straight into the
  // next keeps the bar away instead of flashing it up between them, and leaving a box for a button
  // or for nothing at all brings it back. A box OUTSIDE the sheet does not count: these listeners
  // would never hear it lose focus again, and the bar would stay away for good.
  const [sheetRoot, setSheetRoot] = useState<HTMLDivElement | null>(null);
  const [typingFocus, setTypingFocus] = useState(false);
  const typingFieldRef = useRef<Element | null>(null);
  useEffect(() => {
    if (!sheetRoot) return;
    const follow = (next: EventTarget | null) => {
      const typing = isTypingField(next) && sheetRoot.contains(next as Node);
      typingFieldRef.current = typing ? (next as Element) : null;
      setTypingFocus(typing);
    };
    const onFocusIn = (e: FocusEvent) => follow(e.target);
    const onFocusOut = (e: FocusEvent) => follow(e.relatedTarget);
    sheetRoot.addEventListener("focusin", onFocusIn);
    sheetRoot.addEventListener("focusout", onFocusOut);
    return () => {
      sheetRoot.removeEventListener("focusin", onFocusIn);
      sheetRoot.removeEventListener("focusout", onFocusOut);
    };
  }, [sheetRoot]);
  // A box that is REMOVED while it has focus never fires focusout: the browser just drops the focus.
  // (The add-a-part form goes when the part is added; the note under a failed check goes when the
  // check is changed back. On a phone that does not move focus to a tapped button, which is every
  // iPhone, the box still has the focus at the moment its own form closes.) Left alone, the bar would
  // then stay hidden with nothing focused, and the bar is the only way to submit.
  //
  // So while a box has the focus, and only then, the sheet is watched for it leaving the page. A
  // MutationObserver rather than a check on each render of this component, because most of those
  // forms close through state of their OWN and never re-render this one: the first version of this
  // checked on render, and the test that closes the parts form walked straight past it.
  useEffect(() => {
    if (!typingFocus || !sheetRoot) return;
    const bringBackIfGone = () => {
      if (typingFieldRef.current && !typingFieldRef.current.isConnected) {
        typingFieldRef.current = null;
        setTypingFocus(false);
      }
    };
    bringBackIfGone(); // it may already have gone by the time this effect runs
    if (typeof MutationObserver === "undefined") return;
    const observer = new MutationObserver(bringBackIfGone);
    observer.observe(sheetRoot, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [typingFocus, sheetRoot]);
  // IS THE KEYBOARD ACTUALLY UP? (see keyboardLooksOpen for the trap this closes). null means this
  // browser cannot say, which is every test environment and any browser without a visual viewport:
  // there the focus rule alone decides, exactly as it did before. Where it CAN say, a focused box
  // with no keyboard showing does not hide the bar, so swiping the keyboard away brings the submit
  // button straight back without the engineer having to know they must tap somewhere else first.
  const [keyboardOpen, setKeyboardOpen] = useState<boolean | null>(null);
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;
    let width = window.innerWidth;
    let fullHeight = window.innerHeight;
    const read = () => {
      // A rotation changes what "full height" means, so start again from the new shape.
      if (window.innerWidth !== width) {
        width = window.innerWidth;
        fullHeight = window.innerHeight;
      }
      fullHeight = Math.max(fullHeight, window.innerHeight);
      // vv.height is in CSS pixels at the current pinch zoom, so scale it back to the page's own.
      setKeyboardOpen(keyboardLooksOpen(fullHeight, vv.height * (vv.scale || 1)));
    };
    read();
    vv.addEventListener("resize", read);
    window.addEventListener("resize", read);
    return () => {
      vv.removeEventListener("resize", read);
      window.removeEventListener("resize", read);
    };
  }, []);
  // The bar steps aside only while a box is focused AND the keyboard is (or may be) showing.
  const barSteppedAside = typingFocus && keyboardOpen !== false;

  // EVERY hook above this line, none below (the Rules of Hooks): a deep-linked job renders the
  // no-job branch FIRST (the cache is still loading) and then re-renders with the job present, so a
  // hook below this early return would change the hook count between renders and crash the sheet
  // (the Session ADM browser gate caught exactly that). While the offline cache is still loading we
  // show a loading line, not the "not in your list" message, so a deep link never flashes that
  // dead-end before the job appears (B4).
  if (!job) {
    return (
      <div>
        <BackLink />
        <div className="mt-4 rounded-lg border border-ele-card-border bg-ele-card p-6 text-center text-ele-body">
          {loading
            ? "Loading this job..."
            : "This job is not in your list. It may already be complete, or it has not synced to this device yet."}
        </div>
      </div>
    );
  }

  function setNote(itemId: string, v: string) {
    setNotes((n) => ({ ...n, [itemId]: v }));
    if (checklistError) setChecklistError(null);
  }
  // Area-flow item setter (Session ADM): an explicit status records an exception; null clears
  // it back to pass-on-confirm. Clearing (or N/A) drops the note so a stale note is never
  // submitted (the same rule the tap-cycle applies).
  function setItemStatus(itemId: string, status: ChecklistStatus | null) {
    setStatuses((s) => {
      const copy = { ...s };
      if (status == null) delete copy[itemId];
      else copy[itemId] = status;
      return copy;
    });
    if (status == null || status === "na") {
      setNotes((n) => {
        if (!n[itemId]) return n;
        const copy = { ...n };
        delete copy[itemId];
        return copy;
      });
    }
    if (checklistError) setChecklistError(null);
  }
  // "Add photo" from an area drill-in: opens the SAME photo picker as the Photos section below,
  // so every capture takes the one offline-outbox path (never a second upload route).
  function openPhotoPicker() {
    document.getElementById(`photo-input-${job!.id}`)?.click();
  }
  // Tap an item to cycle it Pass -> Fail -> Advisory -> N/A -> Pass. Everything starts
  // (and stays) on Pass, so a normal visit needs no taps: the engineer only touches the
  // items that need attention, then confirms.
  function cycleStatus(itemId: string) {
    const cur = statuses[itemId] ?? "pass";
    const next = CHECKLIST_CYCLE[(CHECKLIST_CYCLE.indexOf(cur) + 1) % CHECKLIST_CYCLE.length]!;
    setStatuses((c) => ({ ...c, [itemId]: next }));
    if (checklistError) setChecklistError(null);
    // The note input only shows for fail/advisory. When cycling to a status that hides
    // it (pass/na), clear any note so a stale, now-invisible note is never submitted.
    if (next === "pass" || next === "na") {
      setNotes((n) => {
        if (!n[itemId]) return n;
        const copy = { ...n };
        delete copy[itemId];
        return copy;
      });
    }
  }
  // The service submit label. Area flow: "Complete service visit" (the affirmative pass lives on
  // the Confirm-all-areas button above the list), or "Complete with N issue(s)" once problems are
  // flagged. Equipment keeps the fast-checklist labels ("Confirm all pass" / "Complete with N").
  const serviceSubmitLabel = !hasChecklist
    ? isLolerFollowUp
      ? "Submit LOLER items"
      : "Complete service visit"
    : flaggedCount > 0
      ? `Complete with ${flaggedCount} issue${flaggedCount === 1 ? "" : "s"}`
      : useAreaFlow
        ? "Complete service visit"
        : "Confirm all pass";
  // Session AFZ: the durable server value wins; the optimistic local flag opens the gate at once.
  const ramsAcknowledged = !!job.ramsAcknowledgedAt || ramsAckLocal;
  // Session APF: THIS IS THE FALLBACK PATH NOW. The acknowledgement is normally recorded by
  // acceptJob below, from the accept screen. This is the tick on the open sheet, for a sheet that
  // opened without one (the Compliance card lists who that is), and it is unchanged: same route,
  // same column, same offline behaviour.
  // Record the acknowledgement. Optimistic + offline-safe: it sets the local flag immediately (so the
  // gate opens even with no signal), then best-effort posts to the server WHEN ONLINE (so the office
  // sees it before completion) and re-pulls the job. A failed live record is a soft note, never a
  // block: the completion payload carries the acknowledgement so it still records server-side.
  async function acknowledgeRams() {
    if (!job || ramsAcknowledged || ramsBusy) return;
    setRamsAckLocal(true);
    setRamsError(null);
    if (!online) return;
    setRamsBusy(true);
    try {
      await api.jobs.acknowledgeRams(job.id);
      await syncNow();
    } catch {
      setRamsError("Saved on this device. It will record when you are back online.");
    } finally {
      setRamsBusy(false);
    }
  }

  // ---- Session ALW: accept / decline -----------------------------------------------------------
  //
  // ACCEPTED is the durable server stamp, the optimistic local flag, OR the job already being
  // in_progress. That last arm matters twice. The ONLY thing that puts a job in_progress is the
  // office RETURNING a submitted sheet for review, which means the engineer has already done the
  // work and is being asked to correct it -- gating that behind a fresh acceptance would be absurd.
  // It is also what stops this gate retro-locking every job that was already in flight on the day
  // it shipped, since every existing row has acceptedAt null.
  const accepted = !!job.acceptedAt || acceptedLocal || job.status === "in_progress";
  // Session SCR1: the On hold pill (with its reason) for the header; null when the job is not on hold.
  const onHold = job.status === "parts_pending"
    ? primaryStatePill({ status: job.status, holdReason: job.holdReason })
    : null;
  // The prompt shows only for work that is actually THIS engineer's and still open. A submitted or
  // synced sheet (done) is history, and there is nothing left to accept.
  const needsAcceptance = !done && !accepted;

  // Session AOL: is this job actually THIS account's to accept? An engineer's list is narrowed to
  // their own work, so it is true throughout for them; an admin walking the PWA sees the whole
  // account, and on 8 September 2026 that let one accept a visit assigned to somebody else. Only
  // the ACCEPT control is withheld: declining is a dispatch decision the office may legitimately
  // take for an engineer who has rung in, and the server allows it for exactly that reason.
  //
  // `!== false`, NOT `=== true`. A job cached before this shipped carries no such field, and
  // treating absent as "not mine" would take the button away from an engineer with a stale cache
  // and wedge the sheet behind a gate they could no longer open. Absent means "cannot say", which
  // resolves to the pre-AOL behaviour; the server refuses anybody who should not be there anyway.
  const mine = job.assignedToMe !== false;

  // ---- Session APF: accepting the job also confirms its RAMS (Director decision, 21 September 2026)
  //
  // The RAMS this accept screen puts in front of the engineer, or null when it shows none. Non-null
  // means the gate is the MERGED step: the RAMS block is on the screen, the button reads "Accept and
  // confirm RAMS", and the one tap records both facts. It is ONE value on purpose, read by the block,
  // by the button and by acceptJob alike, so the acknowledgement can only ever be recorded from a
  // screen that showed the statement being agreed to.
  //
  // NULL WHEN THERE IS NO RAMS TO SHOW. A job cached before Session AFZ carries no ramsDoc, and the
  // card on the open sheet tells that engineer the RAMS "will be available when this job next syncs".
  // There is nothing they could have read, so the gate stays a plain "Accept job" that records the
  // acceptance alone, and the tick on the open sheet is what they use afterwards. Never record an
  // acknowledgement of a document that could not be opened.
  //
  // AND NULL WHEN THE JOB IS NOT THIS ACCOUNT'S TO ACCEPT (Session AOL): there is no Accept button in
  // that branch, so there is nothing for the statement to belong to.
  const ramsToConfirm = mine ? (job.ramsDoc ?? null) : null;

  // Optimistic + offline-safe, mirroring acknowledgeRams above. The local flags are set FIRST so the
  // sheet opens at once; the live POST is best-effort, and its failure is a soft note rather than a
  // block, because the completion payload carries the acceptance (and the acknowledgement) regardless.
  async function acceptJob() {
    if (!job || accepted || acceptBusy) return;
    // Decided once, from what is on the screen at the moment of the tap, and used for the local
    // flag, the request and the soft note alike: the three must never disagree about whether this
    // tap confirmed the RAMS.
    const confirmsRams = ramsToConfirm !== null;
    setAcceptedLocal(true);
    if (confirmsRams) {
      setRamsAckLocal(true);
      setRamsError(null);
    }
    setAcceptError(null);
    // Offline, both facts ride the completion payload (accepted / ramsAcknowledged), and the draft
    // keeps both flags if the sheet is left before then.
    if (!online) return;
    setAcceptBusy(true);
    try {
      // THE FLAG IS SENT ONLY WHEN THE STATEMENT WAS SHOWN. The server records the acknowledgement on
      // { ramsAcknowledged: true } and on nothing else, precisely so that a caller which never showed
      // the engineer the statement (an older cached bundle, or the plain gate above) cannot record
      // one by accepting. The plain gate sends no body at all, exactly as this call always has.
      if (confirmsRams) await api.jobs.accept(job.id, { ramsAcknowledged: true });
      else await api.jobs.accept(job.id);
      await syncNow();
    } catch (err) {
      // Session AOL: a REFUSAL is not a lost connection, and reporting one as the other is worse
      // than saying nothing. The server refuses an account that is not the assignee with 403, and
      // the pre-AOL catch answered every failure with "Saved on this device", so a refused
      // acceptance would have told the user it had been recorded, kept the optimistic flag set and
      // opened the job sheet behind it. On a refusal the local flag is rolled back so the gate
      // closes again, and the server's own sentence is shown.
      // Read STRUCTURALLY rather than with `instanceof ApiError`, deliberately. Half the PWA test
      // suite replaces ../../lib/api wholesale with an object literal, so the imported class would
      // be undefined there and `instanceof undefined` throws a TypeError -- INSIDE THE CATCH BLOCK,
      // which would break the accept flow outright rather than degrade it. All this needs is the
      // status number, and reading it this way cannot throw whatever the caller was handed.
      const status = (err as { status?: unknown } | null)?.status;
      const body = (err as { body?: { message?: unknown } } | null)?.body;
      if (status === 403) {
        setAcceptedLocal(false);
        // Session APF: BOTH flags go, whichever gate was on the screen. The server stamped neither
        // (its refusal runs ahead of both claims), and an acknowledgement held on this device for a
        // job this account may not accept must not outlive the refusal any more than the acceptance
        // does: left set, it would ride the draft and later read as an acknowledgement nobody gave.
        // Taking both away is also what empties the draft again. The save effect sees a sheet with
        // nothing on it, so it writes nothing, or deletes the row the optimistic flags had written.
        setRamsAckLocal(false);
        setAcceptError(typeof body?.message === "string" ? body.message : "This job is not yours to accept.");
      } else {
        setAcceptError("Saved on this device. It will record when you are back online.");
        // Session APF: the sheet is open by now, so the line above is no longer on the screen. When
        // this tap also confirmed the RAMS, the same soft note goes where the engineer can see it,
        // under the acknowledged RAMS card, exactly as a failed live tick has always been reported.
        if (confirmsRams) setRamsError("Saved on this device. It will record when you are back online.");
      }
    } finally {
      setAcceptBusy(false);
    }
  }

  // Declining is LIVE ONLY, deliberately. It hands the job back to the office, and the office has to
  // see that now: queueing one offline would leave a job nobody is going to still sitting on the
  // schedule as assigned. With no signal the engineer is told to ring, with the number to ring.
  async function declineJob() {
    if (!job || acceptBusy) return;
    if (!online) {
      setAcceptError(`You need a connection to decline a job. Please call the office on ${OFFICE_PHONE}.`);
      return;
    }
    setAcceptBusy(true);
    setAcceptError(null);
    try {
      await api.jobs.decline(job.id, declineReason);
      // Session APF: the job has gone back to the office, so nothing kept for it on this phone is
      // this engineer's work any more. Done as soon as the decline itself is accepted, not after the
      // re-pull below: a failed re-pull does not un-decline the job.
      discardDraft(job.id, true);
      await syncNow();
      navigate("/engineer", { replace: true });
    } catch {
      setAcceptError("That could not be sent. Check your signal and try again, or call the office.");
      setAcceptBusy(false);
    }
  }

  // The RAMS acknowledgement gates the submit button (Session AFZ), alongside the signature/skip.
  // An insurance check produces no customer document, so it takes no customer signature (Session BBF).
  const submitDisabled = saving || (!isInsuranceCheck && !signature && !skipReason) || !ramsAcknowledged;

  // Risk assessment (Session AK Item 9; optional since Session AEH D10): if the engineer chooses to
  // record a site-specific assessment, control measures are mandatory once any hazard is flagged.
  const anyHazard = Object.values(raHazards).some(Boolean) || raOther.trim().length > 0;
  function signOffRiskAssessment() {
    if (anyHazard && !raControl.trim()) {
      setRaError("Enter the control measures for the hazards you have identified.");
      return;
    }
    setRaError(null);
    setRaSignedAt(new Date().toISOString());
  }
  // Session ANT: whether the platform's own record of the day says this is the engineer's only job,
  // and so whether the return-leg field is asked for. Read from the job, never re-derived here: the
  // phone's cached job list holds only ACTIVE work, so counting today's jobs on this device would
  // make the LAST visit of a three-visit day look like the only one (the first two have already
  // moved to pending_approval and left the list). Absent on a job cached before ANT, which reads as
  // false and simply does not ask.
  const askReturnTravel = job?.onlyJobToday === true;

  // The two travel figures as whole minutes, or null when the field is blank. A value outside
  // 1..1440 resolves to null here and is refused by the submit gate below rather than silently
  // dropped: a typed figure that reaches no timesheet is worse than one the engineer is asked to fix.
  function parsedTravel(text: string): number | null {
    const trimmed = text.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) && Number.isInteger(n) && n >= 1 && n <= 1440 ? n : null;
  }
  /** True when the field holds something that is not a usable figure (blank is fine, "0" is not). */
  function travelFieldInvalid(text: string): boolean {
    return text.trim() !== "" && parsedTravel(text) == null;
  }
  const travelInvalid = travelFieldInvalid(travelMinsText) || (askReturnTravel && travelFieldInvalid(returnTravelMinsText));

  // The travel fields for the completion payload. Omitted entirely when blank, so an engineer who
  // did not state a figure records no travel rather than a zero that would read as "no journey".
  function travelPayload(): { travelMins?: number; returnTravelMins?: number } {
    const out = parsedTravel(travelMinsText);
    const back = askReturnTravel ? parsedTravel(returnTravelMinsText) : null;
    if (out != null) rememberLastTravelMins(out);
    return { ...(out != null ? { travelMins: out } : {}), ...(back != null ? { returnTravelMins: back } : {}) };
  }

  // The risk-assessment fields for the completion payload (service jobs, once signed off).
  function riskAssessmentPayload() {
    return { ...raHazards, otherHazards: raOther.trim() || undefined, controlMeasures: raControl.trim() || undefined };
  }

  // ---- Session APF: the POP tag taps, and what the visit's times resolve to ----------------------
  //
  // PRE-FILL, NEVER REPLACE (Director decision, 21 September 2026). A tap sets its time field only
  // when that field is EMPTY. If the engineer has already put a time in, it is left alone; the card
  // still shows the time of the tap, and the tap itself is on file either way. The functional update
  // judges "empty" against the field as it is at that moment, not as it was when this render began.
  function onArrivalStamp(stamp: TapStamp) {
    setTapArrival(stamp);
    setSvcArrival((current) => current || isoToLocalHHMM(stamp.at));
    if (checklistError) setChecklistError(null);
  }
  function onDepartureStamp(stamp: TapStamp) {
    setTapDeparture(stamp);
    setSvcDeparture((current) => current || isoToLocalHHMM(stamp.at));
    if (checklistError) setChecklistError(null);
  }

  // The arrival and departure this sheet would record if it were submitted at `at`. ONE call, used
  // by the confirm panel (so the engineer sees the times before they become the record) and by the
  // submit itself (so what they saw is what is sent). Every rule is in resolveVisitTimes; the only
  // thing decided here is which of its two rule sets this job takes. A call-out, a repair and a tech
  // visit never have an arrival invented for them; a service or observational visit keeps the
  // automatic capture it has always had.
  // ---- Session BAR: the visit date ----------------------------------------------------------------
  //
  // THE DIRECTOR'S PROBLEM: a job could only be closed on the day it was submitted. Every typed time
  // was put on the phone's date at submission, so a visit finished yesterday and written up this
  // morning was recorded as this morning. The engineer now says which day the visit was (Today,
  // Yesterday, or a date picked from a calendar that offers no future day), and every rule below
  // reads that one value. Today is exactly the sheet it always was; an EARLIER day takes no default
  // from today's clock (see resolveVisitTimes) and asks for the times instead.
  const todayYmd = localYmd(new Date());
  // An OVERDUE job (booked for an earlier day) opens with NO day chosen: opening on the booked day
  // silently recorded the wrong day whenever overdue work was done today. Until the engineer taps a
  // day the sheet cannot be sent, and the rules below read today (nothing is recorded meanwhile).
  const bookedVisitYmd = isoToLocalYmd(job?.scheduledFor ?? null);
  const visitDayUnchosen = !visitDateChoice && !!bookedVisitYmd && bookedVisitYmd < todayYmd;
  const visitYmd = visitDateChoice || (visitDayUnchosen ? todayYmd : defaultVisitYmd(job?.scheduledFor ?? null, new Date()));
  const pastVisit = visitYmd < todayYmd;
  // Session BBC: the visit date window (lib/visitDateWindow.ts). A day more than a week back needs a
  // late reason before Submit enables; a day more than two weeks back is never offered, and one that
  // got here anyway (a draft restored a fortnight later) is refused with words that send them to the
  // office.
  const visitBand = visitAgeBand(calendarDaysBetween(visitYmd, todayYmd));
  const lateReasonMissing = !visitDayUnchosen && visitBand === "late" && !lateReasonGiven(lateReason, lateReasonText);
  const submitBlocked = submitDisabled || lateReasonMissing;

  function visitTimesAt(at: Date): ResolvedVisitTimes {
    return resolveVisitTimes({
      visitYmd,
      nowIso: at.toISOString(),
      openedAtIso: openedAtRef.current || null,
      arrivalHHMM: svcArrival,
      departureHHMM: svcDeparture,
      arrivalStampIso: tapArrival?.at ?? null,
      departureStampIso: tapDeparture?.at ?? null,
      fallBackToOpenAndNow: !usesCalloutFields,
      crossesMidnight: usesCalloutFields && ooh,
      allowNowDefaults: !pastVisit,
    });
  }

  // What stops a sheet for an earlier day being sent, in the words the engineer is shown, or null.
  // Today's sheet is never stopped here: everything it could send it could always send.
  function pastVisitProblem(times: ResolvedVisitTimes, at: Date): string | null {
    if (visitDayUnchosen) return "Tap the day you did this job.";
    if (visitYmd > localYmd(at)) return "The visit date cannot be in the future.";
    if (visitBand === "too_old") return "That visit is more than 2 weeks ago. Contact the office, who will record it.";
    if (lateReasonMissing) return "Choose why this visit is being sent late.";
    if (!pastVisit) return null;
    if (!usesCalloutFields && (!svcArrival || !svcDeparture)) {
      return `For a visit on ${visitDayLabel(visitYmd)}, enter the time you arrived and the time you left.`;
    }
    if (usesCalloutFields && svcArrival && !svcDeparture) {
      return `For a visit on ${visitDayLabel(visitYmd)}, enter the time you left as well.`;
    }
    const later = (iso: string | null) => !!iso && new Date(iso).getTime() > at.getTime();
    if (later(times.arrivalISO) || later(times.departureISO)) {
      return "One of those times has not happened yet. Check the visit date and the times.";
    }
    return null;
  }

  // The Left time has to be after the Arrived time, on every job type (Session APF: a call-out and a
  // repair had no such gate while their times were picked from the dropdowns). The ONE exception is
  // an out-of-hours call-out-style sheet, which may genuinely cross midnight: 23:00 to 00:30 is a
  // real visit there, and resolveVisitTimes puts that departure on the next day.
  // Session BAS: the refusal says how to put it right, not just what is wrong. Now is offered only on
  // today's sheet, so an earlier day's message does not mention it.
  const timesOrderMessage = pastVisit
    ? "The Left time must be after the Arrived time. Check both times for that day."
    : "The Left time must be after the Arrived time. Check both times, or tap Now under Left as you leave.";
  const timesOutOfOrder = !!svcArrival && !!svcDeparture && svcDeparture <= svcArrival && !(usesCalloutFields && ooh);

  // What the confirm panel states is about to be recorded, or null while the panel is shut. Worked
  // out with the same call the submit makes, at the instant the panel opened, and re-read on every
  // render, so a time corrected while the panel is open is reflected in it straight away.
  const confirmTimes = confirmingSubmit ? visitTimesAt(confirmOpenedAt ? new Date(confirmOpenedAt) : new Date()) : null;
  // Session BAR: the confirm step names the DAY being recorded before the times, and says so when a
  // blank Left is about to be recorded as the moment of submission rather than leaving it silent.
  const leftIsSubmitTime = !!confirmTimes?.departureISO && !svcDeparture;
  const confirmVisitLine = `Visit on ${visitDayLabel(visitYmd)}${visitYmd === todayYmd ? " (today)" : ""}.${
    leftIsSubmitTime ? " Left is blank, so it is recorded as the time you submit." : ""
  }`;

  // First tap of "Submit": validate the checklist (every area affirmatively confirmed on the
  // area flow, a Fail needs a note), then show the confirm step. The actual submit happens on
  // the confirm (onComplete).
  function requestSubmit() {
    if (!job || saving) return;
    // Checked for EVERY job type, ahead of the type-specific gates: travel is asked of every visit.
    if (travelInvalid) {
      setChecklistError(TRAVEL_RANGE_ERROR);
      return;
    }
    if (!usesCalloutFields) {
      // Area-flow integrity gate (Session ADM, closes audit F1): nothing is recorded as passed
      // until the engineer has confirmed every applicable inspection area.
      if (useAreaFlow && !areasAllConfirmed) {
        setChecklistError(
          "Confirm every inspection area before submitting. Tap 'Confirm all areas checked and passed', or open an area to record what you found.",
        );
        return;
      }
      const missing = firstFailMissingNote(visibleItems, statuses, notes);
      if (missing) {
        setChecklistError(`Add a note describing the fault for "${missing.description}" before submitting.`);
        return;
      }
    }
    // Session BBF: every insurer finding must be answered, and a "not there" or "could not check" answer carries the engineer's words.
    if (isInsuranceCheck) {
      const gap = firstInsuranceGap(insuranceItems, insuranceAnswers);
      if (gap) {
        setChecklistError(gap);
        return;
      }
    }
    // Every job type since Session APF (see timesOutOfOrder), and still after the checklist gates,
    // so a service sheet reports its problems in the order it always has.
    if (timesOutOfOrder) {
      setChecklistError(timesOrderMessage);
      return;
    }
    // The RAMS gate applies to every job type (a call-out uses the repair RAMS). Belt-and-braces with
    // the disabled submit button, so the confirm step can never be reached without an acknowledgement.
    if (!ramsAcknowledged) {
      setChecklistError("Confirm you have read the applicable RAMS before submitting.");
      return;
    }
    {
      const at = new Date();
      const problem = pastVisitProblem(visitTimesAt(at), at);
      if (problem) {
        setChecklistError(problem);
        return;
      }
    }
    setChecklistError(null);
    // Pin "now" for the confirm panel's times line, and for the submit that follows it.
    setConfirmOpenedAt(new Date().toISOString());
    setConfirmingSubmit(true);
  }

  async function onComplete() {
    if (!job || saving) return;
    // A Fail must describe the fault: block completion until every failed item has a note. On
    // the area flow every applicable area must have been affirmatively confirmed (F1 gate).
    if (travelInvalid) {
      setChecklistError(TRAVEL_RANGE_ERROR);
      setConfirmingSubmit(false);
      return;
    }
    if (!usesCalloutFields) {
      if (useAreaFlow && !areasAllConfirmed) {
        setChecklistError(
          "Confirm every inspection area before submitting. Tap 'Confirm all areas checked and passed', or open an area to record what you found.",
        );
        setConfirmingSubmit(false);
        return;
      }
      const missing = firstFailMissingNote(visibleItems, statuses, notes);
      if (missing) {
        setChecklistError(`Add a note describing the fault for "${missing.description}" before submitting.`);
        setConfirmingSubmit(false);
        return;
      }
    }
    if (isInsuranceCheck) {
      const gap = firstInsuranceGap(insuranceItems, insuranceAnswers);
      if (gap) {
        setChecklistError(gap);
        setConfirmingSubmit(false);
        return;
      }
    }
    // The backstop for a time edited while the confirm panel was open (the fields stay live above it).
    if (timesOutOfOrder) {
      setChecklistError(timesOrderMessage);
      setConfirmingSubmit(false);
      return;
    }
    if (!ramsAcknowledged) {
      setChecklistError("Confirm you have read the applicable RAMS before submitting.");
      setConfirmingSubmit(false);
      return;
    }
    {
      const at = confirmOpenedAt ? new Date(confirmOpenedAt) : new Date();
      const problem = pastVisitProblem(visitTimesAt(at), new Date());
      if (problem) {
        setChecklistError(problem);
        setConfirmingSubmit(false);
        return;
      }
    }
    setChecklistError(null);
    setSaving(true);
    // Session APF: the arrival and departure for BOTH payload shapes come from the one helper, at the
    // instant the confirm panel was opened, so the times the engineer was shown are the times sent.
    // (completedAt below stays the true moment of submission: it is a different fact.) An explicitly
    // marked or typed time (Session ADJ), or one a POP tag tap pre-filled, wins. Left blank, a service
    // or observational visit falls back to when the job was opened and to the submit instant, as it
    // always has, and a call-out-style sheet records no times at all.
    const times = visitTimesAt(confirmOpenedAt ? new Date(confirmOpenedAt) : new Date());
    const timesPayload = {
      ...(times.arrivalISO ? { arrivalTime: times.arrivalISO } : {}),
      ...(times.departureISO ? { departureTime: times.departureISO } : {}),
    };
    // Session BAR: completedAt is THE VISIT. For today it stays the moment of submission, as it always
    // has. For an earlier day it is that day (the recorded departure, else midday), because the office
    // approval stamps it as the lift's last service, the PPM anchor, and the service record prints it;
    // the real moment of submission travels beside it as submittedAt, with the visit date itself.
    const submitNow = new Date();
    const visitStamp = pastVisit
      ? {
          completedAt: pastVisitCompletedAt(visitYmd, times.departureISO, submitNow.toISOString()),
          visitDate: visitYmd,
          submittedAt: submitNow.toISOString(),
          ...(visitBand === "late" && lateReasonGiven(lateReason, lateReasonText)
            ? {
                lateReason: lateReason as LateReasonCode,
                ...(lateReason === "other" ? { lateReasonText: lateReasonText.trim().slice(0, LATE_REASON_TEXT_MAX) } : {}),
              }
            : {}),
        }
      : null;
    let payload: CompletionPayload;
    if (usesCalloutFields) {
      const now = new Date();
      payload = {
        checklist: [],
        defects: [],
        partsUsed: parts,
        outOfHours: ooh,
        // Both times, or neither: a call-out-style sheet with no arrival records no times at all,
        // and never one invented from when the sheet was opened (see resolveVisitTimes for why).
        ...timesPayload,
        ...travelPayload(),
        ...(faultFound.trim() ? { faultFound: faultFound.trim() } : {}),
        ...(workCarriedOut.trim() ? { workCarriedOut: workCarriedOut.trim() } : {}),
        ...(liftStatus ? { liftStatusOnLeaving: liftStatus } : {}),
        ...(signature ? { signatureData: signature } : {}),
        ...(skipReason ? { signatureSkipReason: skipReason } : {}),
        ...(signature && signerName.trim() ? { signerName: signerName.trim() } : {}),
        completedAt: now.toISOString(),
        ...(visitStamp ?? {}),
        ramsAcknowledged, // Session AFZ: records the acknowledgement server-side, incl. an offline-only visit.
        accepted, // Session ALW: records the acceptance server-side, incl. an offline-only visit.
      };
    } else {
      // Every untouched (visible) item is recorded as a pass (the fast-checklist
      // behaviour). Items hidden by drive type are recorded as not applicable on the PDF.
      const checklist = buildChecklistResults(visibleItems, statuses, notes);
      const now = new Date().toISOString();
      // Engineer notes: the engineer's own free text, plus any per-area notes from the area
      // flow's drill-ins as "Area: note" lines (the same offline-safe payload field).
      const engineerNotes = useAreaFlow ? composeEngineerNotes(notesText, areas, areaNotes) : notesText.trim() || undefined;
      // Session AQB (D78): what the engineer recorded for the contract's additional items. Every item is
      // sent, including the untouched ones, because "checked" is a recorded answer and not a silence:
      // the certificate states each item, so each needs a verdict behind it.
      const additionalItemsChecked: CoveredItemCheck[] = coveredItems.map((item) => ({
        id: item.id,
        description: item.description,
        ...(item.maintenanceOnly ? { maintenanceOnly: true as const } : {}),
        status: coveredStatuses[item.id] ?? "pass",
        ...(coveredNotes[item.id]?.trim() ? { note: coveredNotes[item.id]!.trim() } : {}),
      }));
      // Session BAQ: ONLY the LOLER items the engineer answered. An unanswered item sends nothing.
      const lolerItemsChecked: LolerItemCheck[] = lolerItems
        .filter((item) => lolerAnswers[item.id] !== undefined)
        .map((item) => {
          const note = lolerNotes[item.id]?.trim();
          return { id: item.id, outcome: lolerAnswers[item.id]!, ...(note ? { note: note.slice(0, 2000) } : {}) };
        });
      payload = {
        checklist,
        ...(additionalItemsChecked.length > 0 ? { additionalItemsChecked } : {}),
        ...(lolerItemsChecked.length > 0 ? { lolerItemsChecked } : {}),
        // Session BBF: the engineer's answer for each insurer finding, on an insurance check only.
        ...(isInsuranceCheck ? { insuranceItemsChecked: insurancePayload(insuranceItems, insuranceAnswers) } : {}),
        defects: defectsText
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
        advisoryNotes: advisory.trim() || undefined,
        partsUsed: parts,
        engineerNotes,
        // Out of hours is not relevant to a planned service visit.
        ...timesPayload,
        ...travelPayload(),
        ...(signature ? { signatureData: signature } : {}),
        ...(skipReason ? { signatureSkipReason: skipReason } : {}),
        ...(signature && signerName.trim() ? { signerName: signerName.trim() } : {}),
        // The pre-work risk assessment the engineer signed off before the checklist (Session AK Item 9).
        ...(raSignedAt ? { riskAssessment: riskAssessmentPayload() } : {}),
        // Session ALW: an observational visit's three free-text fields ride the call-out-style
        // columns (see fieldLoopPdf.ts for the mapping, stated once): faultFound is "What you
        // found", workCarriedOut is "Recommendation". engineerNotes above already carries "Anything
        // else". They are added to the SERVICE-shaped payload rather than the call-out one because
        // an observational visit records a checklist (a condition report's survey areas) and its
        // times the service way, and has no out-of-hours basis or labour charge to compute.
        ...(isObservational && faultFound.trim() ? { faultFound: faultFound.trim() } : {}),
        ...(isObservational && workCarriedOut.trim() ? { workCarriedOut: workCarriedOut.trim() } : {}),
        completedAt: now,
        ...(visitStamp ?? {}),
        ramsAcknowledged, // Session AFZ: records the acknowledgement server-side, incl. an offline-only visit.
        accepted, // Session ALW: records the acceptance server-side, incl. an offline-only visit.
      };
    }
    try {
      await complete(job.id, payload); // queues locally; never throws offline
      // Session APF: the finished sheet is in the outbox now, so the draft has done its job. Cleared
      // only AFTER complete() resolves, never before: until the outbox holds the sheet the draft is
      // the only copy of it, and if complete() ever did throw, this line is not reached and the
      // draft stands. Not awaited, so a slow IndexedDB cannot keep the engineer on a finished sheet.
      discardDraft(job.id, true);
      // Session APF (PWA audit finding 7 / fix 9): SAY THAT IT WORKED. Submitting used to drop the
      // engineer back on Today with the job silently gone and nothing said about it. The home screen
      // shows a "Job sheet submitted" banner (or "Job sheet saved" with no signal) from exactly this
      // router state: EngineerHome.tsx's readSubmittedState is the contract, { ref, site, online },
      // and it reads the state once and clears it, so a refresh never replays the banner. `online` is
      // what the phone believed at the moment of submitting, which is the honest thing to tell them:
      // with a signal the sheet is on its way to the office, without one it is safe on this device.
      // The decline above deliberately carries no such state: nothing was submitted.
      navigate("/engineer", {
        replace: true,
        state: { submitted: { ref: job.eleNumber ?? "This job", site: job.siteName, online } },
      });
    } finally {
      setSaving(false);
    }
  }

  // ---- Session APF: what the submit bar shows (the bar itself is SubmitBar, further down) ---------
  //
  // The bar belongs to the ACTIVE sheet only: accepted, and not yet submitted. Behind the acceptance
  // gate there is no sheet to submit, and a finished job has its own "Job submitted" panel.
  const sheetActive = !needsAcceptance && !done;
  // The per-type name of the submit button, exactly as it has always read. ONE place now, because the
  // bar is the one home of the button (it used to be spelt out inline at the foot of the sheet).
  const submitLabel = isRepair
    ? `Submit ${remedialWord}`
    : isCallout
      ? "Submit call-out"
      : isConditionReport
        ? "Submit condition report"
        : isSiteInspection
          ? "Submit site inspection"
          : isInsuranceCheck
            ? "Submit insurance check"
            : serviceSubmitLabel;
  // Until there is a signature or a reason there is none, the bar cannot offer the submit, so it
  // offers the way to get one instead (see toSignature). This is the question the bar turns on.
  const signatureSettled = isInsuranceCheck || !!signature || !!skipReason;
  // Is the leaving POP tag step ON THE SCREEN and still waiting for its tap? All three have to hold:
  // the lift has a tag (`=== true`, as where the step is rendered), this phone can read one (the
  // step renders nothing where it cannot), and no leaving tap has been made or put back from a draft.
  const leavingTagPending = job.hasPopTag === true && popTagSupported && tapDeparture == null;

  // The bar's first button: take the engineer to the foot of the sheet in ONE tap. The PWA audit
  // measured the signature at 9 to 15 flicks below the top of a sheet (4.9 screens on a service
  // visit, 3.7 on a call-out). When the leaving tag step is still waiting it scrolls to THAT, because
  // the Director's order of events is tag, then signature, and the signature pad sits directly under
  // the step, so both are then on the screen together. Otherwise it scrolls to the signature itself.
  // scrollIntoView is OPTIONAL-CALLED, the house pattern: jsdom does not implement it, and a scroll
  // that cannot happen must never be the thing that takes the sheet down.
  function toSignature() {
    const target = (leavingTagPending ? leavingTagRef.current : null) ?? signatureSectionRef.current;
    target?.scrollIntoView?.({ behavior: scrollBehaviour(), block: "start" });
  }

  return (
    // Session APF: the root carries the focus listeners that move the bar aside while the engineer
    // types (setSheetRoot, above), and the room the bar needs. The layout's own pb-24 only clears
    // the bottom tab bar; the submit bar stands on top of that, so an active sheet takes more
    // padding, or the last lines of the sheet (the signature's own hints) could never be scrolled
    // clear of it. More again while the bar holds the confirm step, which is the tallest it gets.
    // The padding goes with the SHEET being active, not with the bar being on the screen at that
    // instant: taking it away each time a box was focused would shift the page under the engineer's
    // thumb just as the keyboard opened.
    <div ref={setSheetRoot} className={sheetActive ? (confirmingSubmit ? "pb-56" : "pb-28") : "pb-8"}>
      <BackLink />

      <header className="mt-3 mb-5">
        <JobTypeBadge type={job.type} equipmentAsset={isEquipment} />
        {/* Session SCR1 (SC9A follow-up): a job on hold says so, with the reason, in the same look as the Home list and the office
            (the one pill from the status registry). The engineer is told why work stopped without leaving the sheet. */}
        {onHold && <StatusPill state={onHold} className="ml-2 mt-1" />}
        <h1 className="mt-3 font-heading text-[28px] font-bold leading-tight tracking-wide text-ele-strong">{job.eleNumber ?? "Lift"}</h1>
        <p className="mt-0.5 text-sm text-ele-muted">{job.assetType}{job.liftType ? `, ${job.liftType}` : ""}</p>
        <p className="mt-3 font-heading text-lg font-semibold leading-snug text-ele-strong">{job.siteName}</p>
        {job.siteAddress && (
          <p className="text-sm text-ele-muted">{siteAddressWithPostcode(job.siteAddress, job.sitePostcode)}</p>
        )}
        {job.siteAddress && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
              siteAddressWithPostcode(job.siteAddress, job.sitePostcode),
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-ele-green px-4 py-2.5 font-heading text-base tracking-wide text-ele-green transition-colors hover:bg-ele-green hover:text-ele-bg"
          >
            <MapPin className="h-4 w-4" aria-hidden="true" />
            Navigate to site
          </a>
        )}
        {job.description && <p className="mt-3 text-[15px] leading-relaxed text-ele-body">{job.description}</p>}
        {/* Session ALW: a site inspection's REASON. Shown right under the description because it is
            the single most useful line on the screen -- "site inspection" alone does not tell the
            engineer whether they are surveying for a quote or checking work after an insurance
            claim, and the two need completely different things looked at. */}
        {isSiteInspection && job.inspectionReason && (
          <p className="mt-2 rounded-ele border border-ele-purple bg-ele-card px-3 py-2 text-sm text-ele-strong">
            <span className="font-menu text-[10px] uppercase tracking-wider text-ele-purple">Why you are going</span>
            <span className="mt-0.5 block">{job.inspectionReason}</span>
          </p>
        )}
      </header>

      {/* Session ALW: the office moved this job while it was open on this phone. The sheet below is
          UNTOUCHED and everything typed into it is still there -- that is the whole point of the
          fix. It stays usable on purpose: if the work was actually done, it should be submitted,
          and the server's refusal escalates to the office rather than discarding it. */}
      {removedFromMyList && (
        <div role="alert" className="mb-5 rounded-ele border border-amber-500 bg-amber-500/10 p-4">
          <p className="font-heading text-sm uppercase tracking-wide text-amber-400">The office has moved this job</p>
          <p className="mt-1 text-sm text-ele-strong">
            It is no longer on your list. Anything you have already filled in is still here and has not been lost.
          </p>
          <p className="mt-1 text-sm text-ele-body">
            If you carried out this visit, finish and submit it as normal, then call the office. If you did not, leave it.
          </p>
          <ContactValue
            value={OFFICE_PHONE}
            kind="phone"
            className="mt-3 inline-flex min-h-11 items-center rounded-ele border border-ele-green px-4 font-menu text-xs uppercase tracking-wide text-ele-green"
          />
        </div>
      )}

      {/* Site access (Session AFW): internal instructions the engineer needs before starting -- door
          codes, key location, parking, concierge. The FIRST operational block, above the checklist,
          carried offline in the job payload so it reads with no signal. Amber wash, full border (not a
          left accent stripe), own background so it reads in either theme. Absent when nothing is
          recorded (never an empty box). Mirrors the engineer site-history panel (Session AFR). */}
      {job.accessDetails && (
        <section className="mb-5 rounded-ele border border-[#b8860b] bg-[#fbf3dd] p-4">
          <h2 className="font-menu text-xs uppercase tracking-wider text-ele-amber-wash-ink">Site access</h2>
          <p className="mt-1 whitespace-pre-wrap text-sm text-ele-amber-wash-ink">{job.accessDetails}</p>
        </section>
      )}

      {/* Fault Assistant (Session AFC): a reactive call-out is where a diagnosis matters most, so
          the engineer can ask about the fault, drawing on the knowledge base and this lift's own
          history, before starting work. Call-outs only. */}
      {isCallout && (
        <Link
          to={`/engineer/fault-assistant?jobId=${job.id}`}
          className="mb-5 flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-blue py-3 text-center font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
        >
          Fault Assistant
        </Link>
      )}

      {/* Session BDM: a job over several working days lists the days this engineer is on, and each day's arrival and departure is
          recorded here. Not a gate: the job sheet below is unchanged and is completed once, on the last day. */}
      {job.workDays && job.workDays.length > 0 && <WorkDaysPanel job={job} />}

      {job.crewMember ? (
        /* ---- Session ASM (Phase 6, Director decision D83): this engineer is ADDITIONAL crew ----
           They attend with the primary engineer, read the RAMS first, and do not keep the job sheet:
           it is one record, and the primary's. So there is no Accept, no sheet and no submit here, only
           who leads the visit and the RAMS. Their hours reach their own timesheet when the office
           approves the primary's sheet. The server refuses every write from them regardless. */
        <section className="rounded-ele border border-ele-blue bg-ele-card p-5">
          <h2 className="font-heading text-lg text-ele-strong">You are on this job as crew</h2>
          <p className="mt-1 text-sm text-ele-body">
            {job.leadEngineerName
              ? `${job.leadEngineerName} is leading this visit and keeps the job sheet.`
              : "Another engineer is leading this visit and keeps the job sheet."}{" "}
            Read the RAMS before you attend. Your time on site is added to your timesheet when the office approves the visit.
          </p>
          {job.scheduledFor && <p className="mt-2 text-sm text-ele-strong">{scheduledForLabel(job.scheduledFor)}</p>}
          {job.ramsDoc && (
            <div className="mt-3 rounded-ele border border-ele-card-border bg-ele-inset p-3">
              <h3 className="font-menu text-[11px] uppercase tracking-wider text-ele-body">Risk assessment and method statement (RAMS)</h3>
              <p className="mt-1 font-body text-[13px] text-ele-body">
                Applicable RAMS for this visit: <span className="text-ele-strong">{job.ramsDoc.code}</span> {job.ramsDoc.label}
              </p>
              <a
                href={api.jobs.ramsHref(job.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-blue px-4 font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
              >
                Open the RAMS
              </a>
            </div>
          )}
        </section>
      ) : needsAcceptance ? (
        /* ---- Session ALW: the acceptance gate ----------------------------------------------
           Everything below this point -- the RAMS card, the checklist, the report fields, the
           photos, the signature and the submit button -- is behind this. That is the gate the
           brief asks for, built as a real early branch rather than a disabled button: the job
           sheet UI does not open until the engineer has said yes.
           It renders ONLY here, on the engineer's own phone, and nowhere else in the platform.

           Session APF (Director decision, 21 September 2026): THIS IS ALSO WHERE THE RAMS IS
           CONFIRMED. Accepting and ticking the RAMS were two taps on two screens that every
           engineer made one after the other, so they are one step now, and the RAMS is shown HERE
           rather than dropped: which document applies, a link to open it BEFORE saying yes, and
           the sentence being agreed to. The record itself is unchanged (the same two columns and
           the same audit action as the tick has always written); only the separate step has gone.
           See ramsToConfirm for the two cases in which this stays a plain acceptance. */
        <section className="rounded-ele border border-ele-purple bg-ele-card p-5">
          <h2 className="font-heading text-lg text-ele-strong">{mine ? "Accept this job?" : "Waiting to be accepted"}</h2>
          <p className="mt-1 text-sm text-ele-body">
            {mine
              ? "The office has put this on your schedule. Accept it to open the job sheet, or decline it to send it straight back to them."
              : "The engineer this job is assigned to has not accepted it yet. You can send it back to the office, but you cannot accept it for them."}
          </p>
          {job.scheduledFor && (
            <p className="mt-2 text-sm text-ele-strong">{scheduledForLabel(job.scheduledFor)}</p>
          )}
          {/* Session APF: the RAMS being confirmed, shown with the Accept button and ONLY with it.
              The declining panel below takes the button away, and the block goes with it: "By
              accepting, you confirm..." beside "Yes, decline this job" would be a sentence about a
              control that is not on the screen. A full border and the card's own inset ground, never
              a left stripe; it is information to read, so it takes no status colour of its own. The
              sentence carries the id the button points at, so a screen reader announces what the tap
              confirms along with the button's name. */}
          {ramsToConfirm && !decliningOpen && (
            <div className="mt-3 rounded-ele border border-ele-card-border bg-ele-inset p-3">
              <h3 className="font-menu text-[11px] uppercase tracking-wider text-ele-body">Risk assessment and method statement (RAMS)</h3>
              <p className="mt-1 font-body text-[13px] text-ele-body">
                Applicable RAMS for this visit: <span className="text-ele-strong">{ramsToConfirm.code}</span> {ramsToConfirm.label}
              </p>
              <a
                href={api.jobs.ramsHref(job.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-blue px-4 font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
              >
                Open the RAMS
              </a>
              <p id="accept-rams-statement" className="mt-2 text-sm text-ele-strong">
                By accepting, you confirm you have read and understood the applicable RAMS for this job.
              </p>
            </div>
          )}
          {acceptError && (
            <p role="alert" className="mt-3 text-sm text-[var(--ele-amber-text-deep)]">
              {acceptError}
            </p>
          )}
          {decliningOpen ? (
            <div className="mt-4">
              <label htmlFor="decline-reason" className="block font-menu text-[11px] uppercase tracking-wider text-ele-body">
                Why are you declining? (optional)
              </label>
              <textarea
                id="decline-reason"
                rows={3}
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="e.g. I am not certified on this equipment"
                className="mt-1 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
              />
              <p className="mt-1 text-xs text-ele-body opacity-70">
                This goes straight back to the office and comes off your schedule.
              </p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  disabled={acceptBusy}
                  onClick={() => void declineJob()}
                  className="min-h-11 flex-1 rounded-ele border border-ele-danger px-4 font-heading text-sm tracking-wide text-ele-danger disabled:opacity-50"
                >
                  {acceptBusy ? "Sending..." : "Yes, decline this job"}
                </button>
                <button
                  type="button"
                  onClick={() => setDecliningOpen(false)}
                  className="min-h-11 flex-1 rounded-ele border border-ele-field-border px-4 font-heading text-sm tracking-wide text-ele-body"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              {mine ? (
                // Session APF: ONE tap. The name says both things the tap records, and it changes
                // with the block above rather than on its own: no RAMS on the screen, no mention of
                // it on the button, and no acknowledgement recorded (see ramsToConfirm).
                <button
                  type="button"
                  disabled={acceptBusy}
                  onClick={() => void acceptJob()}
                  aria-describedby={ramsToConfirm ? "accept-rams-statement" : undefined}
                  className="min-h-11 flex-1 rounded-ele bg-ele-green px-4 font-heading text-sm tracking-wide text-ele-bg disabled:opacity-50"
                >
                  {ramsToConfirm ? "Accept and confirm RAMS" : "Accept job"}
                </button>
              ) : (
                // Session AOL: not this account's job, so there is no Accept button to press. The
                // explanation replaces it rather than a disabled button, because a disabled control
                // reads as a fault to fix; this is a fact about who can answer the question.
                <p className="flex-1 rounded-ele border border-ele-field-border px-4 py-3 text-sm text-ele-body">
                  This job is assigned to another engineer, so only they can accept it. Accepting is their own
                  confirmation that they have seen the work.
                </p>
              )}
              <button
                type="button"
                onClick={() => setDecliningOpen(true)}
                className="min-h-11 flex-1 rounded-ele border border-ele-field-border px-4 font-heading text-sm tracking-wide text-ele-body"
              >
                Decline
              </button>
            </div>
          )}
        </section>
      ) : done ? (
        job.syncEscalated ? (
          // Session AEK (D32): this sheet has stopped retrying. Amber, not red: there is nothing
          // here the engineer can fix, so the message hands it over rather than blaming them. The
          // reason is plain English only, never the HTTP status or the server's own message.
          <div role="alert" className="rounded-ele border border-amber-500 bg-amber-500/10 p-6 text-center">
            <p className="font-heading text-lg text-ele-strong">This job sheet could not sync automatically</p>
            <p className="mt-1 text-sm text-ele-strong">{job.syncEscalatedReason}</p>
            <p className="mt-2 text-sm text-ele-body">
              Your work is saved on this device and has not been lost. Please contact the office.
            </p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
              <ContactValue
                value={OFFICE_PHONE}
                kind="phone"
                className="inline-flex min-h-11 items-center rounded-ele border border-ele-green px-4 font-menu text-xs uppercase tracking-wide text-ele-green"
              />
              {/* The way back. Without this the escalated state is a one-way door and a signed,
                  completed sheet could sit here forever. Worded so it is used AFTER the office has
                  sorted the record, not as a hopeful re-tap. */}
              <button
                type="button"
                onClick={() => void retryEscalated(job.id).then(() => syncNow())}
                className="inline-flex min-h-11 items-center rounded-ele border border-ele-field-border px-4 font-menu text-xs tracking-wide text-ele-body"
              >
                Office sorted it? Try sending again
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-ele border border-ele-green bg-ele-surface p-6 text-center">
            <p className="font-heading text-lg text-ele-strong">
              {job.pendingSync ? "Submitted, waiting to sync" : "Job submitted"}
            </p>
            <p className="mt-1 text-sm text-ele-body">
              {job.pendingSync
                ? "Your job sheet is saved on this device and will upload automatically when you are back online."
                : isInsuranceCheck
                  ? "The office will review your answers. Nothing is sent to the customer."
                  : "Closed. The office will check and release the report. Nothing goes to the customer until then."}
            </p>
            {job.syncError && <p className="mt-2 text-sm text-ele-danger">Last sync attempt failed. It will retry.</p>}
          </div>
        )
      ) : (
        <>
          {/* Session APF: said once, at the top, when the sheet below came back from a draft. An
              engineer who has just watched the app reload mid-visit needs to know their work is
              still there BEFORE scrolling four screens to check. One slim line, dismissible, so it
              does not push the check-in below it off the screen. The status role is on the sentence
              alone: a live region that also held the button would read the button out with it. */}
          {draftRestored && (
            <div className="mb-5 flex items-center justify-between gap-3 rounded-ele border border-ele-green bg-ele-green/10 px-3 py-1">
              <p role="status" className="text-sm text-ele-strong">
                We kept what you had already entered on this job.
              </p>
              <button
                type="button"
                onClick={() => setDraftRestored(false)}
                className="min-h-11 shrink-0 font-menu text-[11px] tracking-wider text-ele-body underline"
              >
                Dismiss
              </button>
            </div>
          )}
          {/* Lone worker safety (Session AOB). FIRST on an accepted, active job, above the RAMS:
              checking in is the thing you do when you arrive, before you open anything else, and a
              control an engineer has to scroll past the whole sheet to find is one that does not get
              tapped. Live only, and it says so when it cannot reach the server. */}
          <LoneWorkerCheckIn jobId={job.id} />
          {job.returnReason && (
            <div role="alert" className="mb-5 rounded-ele border border-amber-500 bg-amber-500/10 p-4">
              <p className="font-heading text-sm uppercase tracking-wide text-amber-400">Job returned for review</p>
              <p className="mt-1 text-sm text-ele-strong">{job.returnReason}</p>
              <p className="mt-1 text-xs text-ele-body opacity-70">Make the changes below and submit again.</p>
            </div>
          )}
          {/* Session APF: the POP tag, arriving. The first half of the ON/OFF pair the Director asked
              for, on every job type: "tap the tag to confirm you've arrived". It is a way of keeping
              time and nothing else, and it is NOT part of the panel above it: that one is a
              deliberate, manual act, this one pre-fills a time field, and neither reads, sets or
              implies the other. It sits here because arriving is when it is done; the Time on site
              card it fills in is just below, after the RAMS card.
              `=== true`, never truthiness: a job cached before this shipped carries no such field
              and must show nothing, and no lift has a tag fitted on the day this ships. The step
              renders nothing on a phone that cannot read a tag, and it never gates anything: the
              Arrived field below works exactly as it always has without it. Keyed by job, so a tap
              answered for one job can never be shown on the next. */}
          {job.hasPopTag === true && (
            <PopTagStep
              key={`${job.id}:arrival`}
              jobId={job.id}
              direction="arrival"
              expectedRef={job.eleNumber}
              stamp={tapArrival}
              onStamp={onArrivalStamp}
            />
          )}
          {/* Compliance (Session AFZ): the applicable RAMS for this visit, any site documents, and a
              mandatory acknowledgement the engineer must tick before the sheet can be submitted. Shown
              for every job type (a call-out uses the repair RAMS). The border is functional: amber
              until acknowledged, green after. The tick is offline-safe (it records locally at once and
              posts when online; an offline submit carries it too), so it never wedges an engineer.

              Session APF: THE CARD HAS TWO STATES, AND THE FIRST IS NOW THE NORMAL ONE.
              ACKNOWLEDGED, which is how nearly every sheet opens since accepting the job confirms
              its RAMS: a compact green card that says so, and keeps the document and the site
              documents one tap away for the visit itself. There is no tick, because there is nothing
              left to ask.
              NOT ACKNOWLEDGED: the card exactly as it has always been, tick included. That is a job
              accepted before the fold shipped, a job the office returned that never had one, an
              office account working a sheet it could not itself accept, or a job cached with no RAMS
              to show on the accept screen. The submit gate below still insists on an acknowledgement
              for every one of them, so this tick is what stops that gate wedging anybody, and it
              keeps its offline behaviour for the same reason. One card rather than two, so the
              document link and the site documents cannot drift apart between the states. */}
          <section className="mb-5">
            <div className={`rounded-lg border ${ramsAcknowledged ? "border-[#63C41F]" : "border-[#b8860b]"} bg-ele-card`}>
              <div className="px-4 py-3">
                <p className="font-heading text-base text-ele-strong">{ramsAcknowledged ? "Compliance: RAMS acknowledged" : "Compliance: RAMS"}</p>
                {job.ramsDoc ? (
                  <>
                    <p className="mt-0.5 font-body text-[13px] text-ele-body">
                      Applicable RAMS for this visit: <span className="text-ele-strong">{job.ramsDoc.code}</span> {job.ramsDoc.label}
                    </p>
                    <a
                      href={api.jobs.ramsHref(job.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-blue px-4 font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
                    >
                      Open the RAMS
                    </a>
                  </>
                ) : (
                  <p className="mt-0.5 font-body text-[13px] text-ele-body">The RAMS for this visit will be available when this job next syncs.</p>
                )}

                {siteDocs && siteDocs.length > 0 && (
                  <div className="mt-3">
                    <p className="font-menu text-[11px] uppercase tracking-wider text-ele-body opacity-80">Site documents</p>
                    <ul className="mt-1 space-y-1">
                      {siteDocs.map((d) => (
                        <li key={d.id}>
                          <a
                            href={api.jobs.siteDocumentHref(job.id, d.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex min-h-11 items-center gap-2 rounded-ele border border-ele-card-border px-3 text-sm text-ele-strong transition hover:border-ele-blue"
                          >
                            <span className="min-w-0 flex-1 truncate">{d.label}</span>
                            <span className="flex-none text-xs text-ele-body opacity-70">{d.categoryLabel}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* The tick, for a sheet that opened without an acknowledgement (see above). Once it is
                    given, by this tick or by the accept screen, the heading says so and the tick has
                    nothing left to ask, so it goes. It is therefore only ever rendered unticked: the
                    ticked-and-locked state it used to sit in afterwards IS the acknowledged card. */}
                {!ramsAcknowledged && (
                  <label className="mt-3 flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={false}
                      disabled={ramsBusy}
                      onChange={() => {
                        void acknowledgeRams();
                      }}
                      className="mt-0.5 h-5 w-5 flex-none accent-ele-green"
                    />
                    <span className="font-body text-sm text-ele-strong">I confirm I have read and understood the applicable RAMS for this job.</span>
                  </label>
                )}
                {/* Shown in BOTH states: by the time a live record fails, the card is already the
                    acknowledged one (the local flag is set before the request is made). */}
                {ramsError && <p className="mt-1 text-xs text-[var(--ele-amber-text-deep)]">{ramsError}</p>}
              </div>
            </div>
          </section>
          {/* Time on site (Session ADJ; moved above the checklist in Session AFF): one-tap NOW when
              you arrive and when you leave, or a typed time, or the time a POP tag tap pre-filled.
              Rides the completion payload, so it survives offline like everything else.
              EVERY JOB TYPE SINCE SESSION APF. A call-out and a repair used to pick their times from
              two 65-option 15-minute dropdowns inside their report, which the PWA audit measured at
              about 16 seconds a sheet; they now use this one card like everything else. What a blank
              field means still differs, and is decided in resolveVisitTimes, not here: a service or
              observational visit falls back to when the job was opened and when it was submitted,
              while a call-out-style sheet with no Arrived time records no times at all (its arrival
              is the SLA attendance stop, which is never invented). The confirm step in the submit
              bar states which of those is about to happen. */}
          <TimeOnSiteCard
            arrival={svcArrival}
            departure={svcDeparture}
            onArrival={(v) => { setSvcArrival(v); if (checklistError) setChecklistError(null); }}
            onDeparture={(v) => { setSvcDeparture(v); if (checklistError) setChecklistError(null); }}
            crossesMidnight={usesCalloutFields && ooh}
            visitYmd={visitYmd}
            todayYmd={todayYmd}
            bookedYmd={isoToLocalYmd(job.scheduledFor)}
            visitDayChosen={!visitDayUnchosen}
            bookedTime={isoToLocalHHMM(job.scheduledFor)}
            onVisitDate={(v) => { setVisitDateChoice(v); if (checklistError) setChecklistError(null); }}
            lateReason={lateReason}
            lateReasonText={lateReasonText}
            onLateReason={(v) => { setLateReason(v); if (checklistError) setChecklistError(null); }}
            onLateReasonText={(v) => { setLateReasonText(v); if (checklistError) setChecklistError(null); }}
          />
          {/* Travel (Session ANT), immediately after the arrival capture it belongs with. Every job
              type carries it; a call-out and a repair get the same card below their own report,
              which is where it was placed when THEIR arrival was captured there. Session APF moved
              that capture up to the card above and deliberately left each travel card where it was. */}
          {!usesCalloutFields && (
            <TravelCard
              minutes={travelMinsText}
              onMinutes={(v) => { setTravelMinsText(v); if (checklistError) setChecklistError(null); }}
              returnMinutes={returnTravelMinsText}
              onReturnMinutes={(v) => { setReturnTravelMinsText(v); if (checklistError) setChecklistError(null); }}
              askReturn={askReturnTravel}
            />
          )}
          {/* Site risk assessment (Session AK Item 9; non-blocking since Session AEH D10): the generic
              RAMS (ELE-RAMS-SVC-001) covers planned visits, so a per-visit assessment is not a gate.
              The card border is functional: amber until recorded, green once signed off. A call-out
              or a repair is not offered it. */}
          {!usesCalloutFields && (
            <section className="mb-5">
              {raSignedAt ? (
                <div className="rounded-lg border border-[#63C41F] bg-ele-card px-4 py-3">
                  <p className="font-menu text-xs uppercase tracking-wider text-ele-green">Site risk assessment recorded</p>
                </div>
              ) : (
                <div className="rounded-lg border border-ele-card-border bg-ele-card">
                  <button
                    type="button"
                    onClick={() => setRaOpen((o) => !o)}
                    aria-expanded={raOpen}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                  >
                    <span>
                      <span className="block font-heading text-base text-ele-strong">Site risk assessment</span>
                      {/* Session APF (audit finding 8 / fix 10). This card is OPTIONAL (Session AEH,
                          Director decision D10), but the amber border and "Complete before starting
                          work" caption read as a gate: the PWA audit found 2 of the 3 live engineers
                          filling it in as though it were one. The un-recorded state is now a neutral
                          card border, matching every other optional card on the sheet, and a caption
                          that says plainly it is optional, in the ordinary muted text colour. The
                          RECORDED (green) state below, and the form itself, are unchanged. */}
                      <span className="mt-0.5 block font-body text-[13px] text-ele-muted">Optional. Add one if this visit needs its own assessment.</span>
                    </span>
                    <span className="font-menu text-xs tracking-wide text-ele-green">{raOpen ? "Hide" : "Add"}</span>
                  </button>
                  {raOpen && (
                    <div className="border-t border-ele-card-border p-3">
                      <RiskAssessmentForm
                        hazards={raHazards}
                        onToggleHazard={(k) => { setRaHazards((h) => ({ ...h, [k]: !h[k] })); if (raError) setRaError(null); }}
                        other={raOther}
                        onOther={(v) => { setRaOther(v); if (raError) setRaError(null); }}
                        control={raControl}
                        onControl={(v) => { setRaControl(v); if (raError) setRaError(null); }}
                        error={raError}
                        onSignOff={signOffRiskAssessment}
                      />
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
          {/* Session ALW: the observational report. Purpose-named labels over the SAME three columns
              the call-out sheet writes (faultFound / workCarriedOut / engineerNotes -- the mapping
              is stated once, in fieldLoopPdf.ts, where the PDF reads them back). No out-of-hours
              toggle, no labour times, no indicative charge: nobody is being billed by the hour for
              a look, and a site inspection has no standard rate at all. */}
          {isObservational && (
            <section className="mb-5">
              <h2 className="mb-3 font-heading text-lg text-ele-blue">Your report</h2>
              {/* Explicit htmlFor/id pairs rather than the shared <Field> helper beside them: Field
                  renders a <label> with no `for` and the control as a SIBLING, so the two are not
                  programmatically associated and a screen reader never announces the label with the
                  box. That is a pre-existing gap across the whole sheet and is flagged rather than
                  swept here, but new fields are written correctly. */}
              <div className="mb-4">
                <label htmlFor="obs-found" className="mb-1 block text-sm text-ele-body">
                  {isConditionReport ? "Overall condition of the lift" : "What you found"}
                </label>
                <textarea
                  id="obs-found"
                  value={faultFound}
                  onChange={(e) => setFaultFound(e.target.value)}
                  rows={4}
                  placeholder={
                    isConditionReport
                      ? "The summary that opens the report the customer receives."
                      : "What you saw. Plain English: the office reads this, and often the customer does too."
                  }
                  className="w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="obs-recommend" className="mb-1 block text-sm text-ele-body">
                  {isConditionReport ? "Works you recommend" : "Your recommendation"}
                </label>
                <textarea
                  id="obs-recommend"
                  value={workCarriedOut}
                  onChange={(e) => setWorkCarriedOut(e.target.value)}
                  rows={4}
                  placeholder={
                    isConditionReport
                      ? "What should be done to give this lift a reliable future service. The office prices this."
                      : "What you would do next, and why."
                  }
                  className="w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
                />
              </div>
            </section>
          )}
          {isInsuranceCheck ? (
            // Session BBF: the insurer's findings are the whole of this sheet's body. The Time on site and Travel cards sit above, the
            // photos below, and nothing here asks for a price, a customer signature or a part: an insurance check is never charged.
            <InsuranceCheckItems
              items={insuranceItems}
              answers={insuranceAnswers}
              onChange={(id, next) => {
                setInsuranceAnswers((cur) => ({ ...cur, [id]: next }));
                if (checklistError) setChecklistError(null);
              }}
              uploadedPhotoCount={job.photos.length}
            />
          ) : usesCalloutFields ? (
            <>
            <CalloutSheetFields
              heading={isRepair ? `${remedialWord.charAt(0).toUpperCase()}${remedialWord.slice(1)} report` : "Call-out report"}
              isRepair={isRepair}
              isReturnVisit={job.type === "repair"}
              faultReported={job.description}
              ooh={ooh}
              onOoh={(v) => { setOoh(v); if (checklistError) setChecklistError(null); }}
              faultFound={faultFound}
              onFaultFound={setFaultFound}
              workCarriedOut={workCarriedOut}
              onWorkCarriedOut={setWorkCarriedOut}
              liftStatus={liftStatus}
              onLiftStatus={setLiftStatus}
            />
            {/* The same travel card the service branch renders. One component, two placements: this
                one was put here (Session ANT) because a call-out or repair captured its arrival
                inside the report above. Session APF moved that capture up to the Time on site card
                and left this card where it is. */}
            <TravelCard
              minutes={travelMinsText}
              onMinutes={(v) => { setTravelMinsText(v); if (checklistError) setChecklistError(null); }}
              returnMinutes={returnTravelMinsText}
              onReturnMinutes={(v) => { setReturnTravelMinsText(v); if (checklistError) setChecklistError(null); }}
              askReturn={askReturnTravel}
            />
            </>
          ) : (
          <>
          <section className="mb-5">
            <div className="mb-1 flex items-baseline justify-between">
              <h2 className="font-heading text-lg text-ele-blue">
                {isObservational
                  ? isConditionReport
                    ? "Condition survey"
                    : "Site inspection"
                  : !hasChecklist
                    ? isLolerFollowUp
                      ? "LOLER items visit"
                      : "Service visit"
                    : isEquipment
                      ? "Equipment checklist"
                      : "Maintenance checklist"}
              </h2>
              <span className="font-menu text-[10px] uppercase tracking-wider text-ele-body opacity-70">{template.name}</span>
            </div>
            <p className="mb-3 text-sm text-ele-body">
              {isConditionReport
                ? "Work through each area of the lift and record what you found. Every area starts on Satisfactory. Tap an area to mark it Advisory or Defect and type the condition; mark anything not fitted to this lift as N/A. A tap cycles Satisfactory, Defect, Advisory, N/A."
                : isSiteInspection
                  ? "There is no checklist for an inspection. Record what you found and what you would recommend, in your own words, below. Photos help the office more than anything else here."
                  : !hasChecklist
                ? isLolerFollowUp
                  ? "Work through the LOLER items below, then record any other defects and your notes. Photos and the customer signature complete the record."
                  : "Record any defects found and your notes below. Photos and the customer signature complete the service record."
                : isEquipment
                  ? "Every check starts on Pass. Tap only the items that need attention, and mark anything that does not apply to this equipment as N/A. A tap cycles Pass, Fail, Advisory, N/A."
                  : "Work through the inspection areas below. A clean visit is one tap: confirm all areas checked and passed. Found a problem? Open its area and record it against the exact check."}
            </p>
            {useAreaFlow && scopeLine && (
              <p className="mb-3 font-heading text-sm text-[var(--ele-loler-ink)]">{scopeLine}</p>
            )}
            {useAreaFlow ? (
              <AreaChecklist
                areas={areas}
                confirmed={areaConfirmed}
                statuses={statuses}
                notes={notes}
                areaNotes={areaNotes}
                onConfirmedChange={(next) => {
                  setAreaConfirmed(next);
                  if (checklistError) setChecklistError(null);
                }}
                onStatusChange={setItemStatus}
                onNoteChange={setNote}
                onAreaNoteChange={(key, v) => setAreaNotes((n) => ({ ...n, [key]: v }))}
                onAddPhoto={openPhotoPicker}
              />
            ) : (
              <>
                {!confirmingSubmit && (
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={requestSubmit}
                      disabled={submitBlocked}
                      className="w-full rounded-ele bg-ele-green py-3 font-heading text-base tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-50"
                    >
                      {serviceSubmitLabel}
                    </button>
                    {!signature && !skipReason && (
                      <p className="mt-1 text-center text-xs text-ele-body opacity-70">Capture the customer signature below, or a reason it could not be taken, to submit.</p>
                    )}
                  </div>
                )}
                {groups.map((g) => (
                  <div key={g.key} className="mb-4">
                    <h3 className="mb-2 font-menu text-[11px] uppercase tracking-wider text-ele-body opacity-80">{g.label}</h3>
                    <ul className="space-y-1.5">
                      {g.items.map((item) => {
                        const status = statuses[item.id] ?? "pass";
                        const isPass = status === "pass";
                        const isFail = status === "fail";
                        const showNote = status === "fail" || status === "advisory";
                        return (
                          <li
                            key={item.id}
                            className={`rounded-ele border ${isPass ? "border-ele-hairline bg-ele-surface-soft" : `${STATUS_STYLE[status].border} bg-ele-inset`} p-2.5`}
                          >
                            <button
                              type="button"
                              onClick={() => cycleStatus(item.id)}
                              aria-label={`${item.description}: ${STATUS_STYLE[status].label}. Tap to change.`}
                              className="flex min-h-11 w-full items-center justify-between gap-3 text-left"
                            >
                              <span className={`text-sm ${isPass ? "text-ele-body" : "text-ele-strong"}`}>{item.description}</span>
                              <StatusChip status={status} />
                            </button>
                            {showNote && (
                              <input
                                value={notes[item.id] ?? ""}
                                onChange={(e) => setNote(item.id, e.target.value)}
                                placeholder={isFail ? "Describe the fault (required)" : "Add a note (optional)"}
                                className={`mt-2 w-full rounded-ele border bg-ele-field px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[var(--ele-field-ring)] ${
                                  isFail && !(notes[item.id]?.trim()) ? "border-ele-danger focus:border-ele-danger" : "border-ele-field-border focus:border-ele-field-focus"
                                }`}
                              />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </>
            )}
            {flaggedCount > 0 && (
              <p className="mt-2 text-sm text-amber-400">
                {flaggedCount} item{flaggedCount === 1 ? "" : "s"} flagged for attention. Everything else will be recorded as a pass
                {useAreaFlow ? " once its area is confirmed" : ""}.
              </p>
            )}
          </section>

          {coveredItems.length > 0 && (
            <section className="rounded-ele border border-ele-field-border bg-ele-field/40 p-3">
              <h2 className="font-heading text-base text-ele-blue">Also covered by this contract</h2>
              <p className="mt-0.5 text-sm text-ele-muted">
                Serviced with this visit. Everything is recorded as checked unless you say otherwise.
              </p>
              <ul className="mt-2 space-y-2">
                {coveredItems.map((item) => {
                  const status = coveredStatuses[item.id] ?? "pass";
                  return (
                    <li key={item.id} className="rounded-ele border border-ele-field-border bg-ele-field px-3 py-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm">
                          {item.description}
                          {item.qty > 1 ? ` (x${item.qty})` : ""}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {COVERED_STATUS_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            aria-pressed={status === opt.value}
                            onClick={() => setCoveredStatuses((cur) => ({ ...cur, [item.id]: opt.value }))}
                            className={`min-h-[40px] rounded-ele border px-3 text-sm ${
                              status === opt.value ? opt.on : "border-ele-field-border text-ele-muted"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {status !== "pass" && (
                        <input
                          value={coveredNotes[item.id] ?? ""}
                          onChange={(e) => setCoveredNotes((cur) => ({ ...cur, [item.id]: e.target.value }))}
                          placeholder="What did you find?"
                          className="mt-2 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-field-focus"
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {lolerItems.length > 0 && (
            <section className="rounded-ele border border-ele-field-border bg-ele-field/40 p-3" aria-label="LOLER items to check at this lift">
              <h2 className="font-heading text-base text-ele-blue">LOLER items to check at this lift</h2>
              <p className="mt-0.5 text-sm text-ele-muted">
                Raised by the examiner. Answer only the items you looked at. Anything you leave alone is not recorded.
              </p>
              <ul className="mt-2 space-y-2">
                {lolerItems.map((item) => {
                  const answer = lolerAnswers[item.id];
                  const deadlineMs = item.deadline ? Date.parse(item.deadline) : Number.NaN;
                  const urgent = item.category === "immediate" || (Number.isFinite(deadlineMs) && deadlineMs < Date.now());
                  const deadlineText = item.deadline ? formatShortDate(item.deadline) : "";
                  return (
                    <li key={item.id} className="rounded-ele border border-ele-field-border bg-ele-field px-3 py-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm">{item.description}</span>
                        {urgent && (
                          <span className="shrink-0 rounded-ele border border-ele-danger px-1.5 py-0.5 text-xs font-semibold uppercase text-ele-danger">
                            Urgent
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-ele-muted">
                        {item.reportRef ? `Report ${item.reportRef}` : ""}
                        {item.reportRef && deadlineText ? " | " : ""}
                        {deadlineText ? `Fix by ${deadlineText}` : ""}
                      </p>
                      {item.lastOutcome && (
                        <p className="mt-1 text-xs text-ele-muted">
                          Last time: {LOLER_OUTCOME_LABEL[item.lastOutcome]}
                          {item.lastNote ? ` (${item.lastNote})` : ""}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {LOLER_OUTCOME_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            aria-pressed={answer === opt.value}
                            onClick={() => setLolerAnswers((cur) => ({ ...cur, [item.id]: opt.value }))}
                            className={`min-h-[40px] rounded-ele border px-3 text-sm ${
                              answer === opt.value ? opt.on : "border-ele-field-border text-ele-muted"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {answer !== undefined && (
                        <input
                          value={lolerNotes[item.id] ?? ""}
                          maxLength={2000}
                          onChange={(e) => setLolerNotes((cur) => ({ ...cur, [item.id]: e.target.value }))}
                          placeholder="Add a note (optional)"
                          aria-label={`Note for: ${item.description}`}
                          className="mt-2 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-field-focus"
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <Field label="Other defects found (one per line)">
            <textarea
              value={defectsText}
              onChange={(e) => setDefectsText(e.target.value)}
              rows={3}
              className="w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
            />
          </Field>

          <Field label="Advisory notes">
            <textarea
              value={advisory}
              onChange={(e) => setAdvisory(e.target.value)}
              rows={2}
              className="w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
            />
          </Field>

          <Field label="Engineer notes">
            <textarea
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              rows={2}
              className="w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
            />
          </Field>
          </>
          )}

          {!isInsuranceCheck && <PartsUsed value={parts} onChange={setParts} />}

          {/* Photos above the signature and submit (Session AFF): capture the visit's photos before
              taking the customer signature, so the sheet's evidence is attached before it is signed. */}
          <JobPhotos job={job} />

          {/* Session ANU: below the photos deliberately. The flag's evidence is picked from the
              photos the engineer has just captured, so the tick list only makes sense once they are
              on the screen above it. Common to every job type (a service visit, a call-out, a
              repair, an inspection), which is why it sits in the trunk rather than in either
              branch: a worn part is worth flagging whatever brought the engineer to the lift. */}
          {/* Session BAS: the three extras (flag a defect, raise extra work, claim an expense) are off
              the main flow, so they sit behind ONE "More for this job" row rather than three full
              cards between the photos and the signature. A <details> keeps them in the page (a flag
              half written survives closing it) and opens with one tap. */}
          <details className="group mt-5 rounded-lg border border-ele-card-border bg-ele-card" data-testid="job-more">
            <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between px-4 font-menu text-sm tracking-wide text-ele-strong">
              <span>More for this job</span>
              <span className="text-xs text-ele-muted group-open:hidden">{isInsuranceCheck ? "Claim an expense" : "Flag a defect, extra work, expenses"}</span>
              <span className="hidden text-xs text-ele-muted group-open:inline">Hide</span>
            </summary>
            <div className="px-3 pb-3">
          {/* Session BBF: neither belongs on an insurance check. A present finding becomes a defect, and work it needs an estimate, by the
              office's decision after the check; the engineer's answers are what start both. */}
          {!isInsuranceCheck && (
            <>
          <FlagDefect
            job={job}
            openPhotoPicker={openPhotoPicker}
            onAddToSheet={(text) => setDefectsText((cur) => appendDefectLine(cur, text))}
          />

          {/* Session ATF: extra work found on this visit, raised for the office to price. Below "Flag something"
              because the two are siblings (a defect is what is wrong; this is the work to put it right). */}
          <RaiseRepairEstimate job={job} />
            </>
          )}

          {/* Session ATE: claim mileage, materials or another cost against THIS job. Opens the
              engineer's own Expenses screen with the job pre-selected, rather than a second form
              built into the job sheet -- one place to submit and see the status of a claim, whether
              it started here or from Home. */}
          <Link
            to={`/engineer/expenses?jobId=${encodeURIComponent(job.id)}`}
            className="mt-3 flex min-h-11 items-center justify-center rounded-ele border border-ele-card-border px-4 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
          >
            Claim an expense for this job
          </Link>
            </div>
          </details>

          {/* Session APF: the POP tag, leaving. The second half of the ON/OFF pair, directly above
              the customer signature because that is the Director's order of events: tap the tag as
              you leave the motor room, then the customer signs, then submit. Same condition and the
              same rules as the arriving step near the top of the sheet: it pre-fills the Left field
              if that is empty, never replaces a time the engineer entered, and never gates the
              signature below it or the submit. The wrapper is spacing (the card carries a bottom
              margin of its own and the section above it carries none), and it is also where the
              submit bar's "Leaving: tag and signature" button scrolls to, hence the ref, the stable
              id and the scroll margin that keeps the card off the very top edge of the screen. */}
          {job.hasPopTag === true && (
            <div ref={leavingTagRef} id="job-leaving-tag" className="mt-5 scroll-mt-4">
              <PopTagStep
                key={`${job.id}:departure`}
                jobId={job.id}
                direction="departure"
                expectedRef={job.eleNumber}
                stamp={tapDeparture}
                onStamp={onDepartureStamp}
              />
            </div>
          )}

          {/* Session APF: the ref, the stable id and the scroll margin are for the submit bar, whose
              "Customer signature" button brings this section to the top of the screen in one tap. */}
          {!isInsuranceCheck && (
          <section ref={signatureSectionRef} id="job-signature" className="mt-5 mb-2 scroll-mt-4">
            <h2 className="mb-2 font-heading text-lg text-ele-blue">Customer signature</h2>
            {!skipping ? (
              <>
                {/* Session APF (audit finding 10 / fix 12). Moved ABOVE the pad: all three of
                    September's live signatures carried no name, because the field sat below the pad,
                    after the customer had already handed the phone back to the engineer -- by the
                    time it was on screen, nobody was still looking at it. Asking before the pad puts
                    it in front of the engineer while the phone is still in their own hand. Still
                    optional; the state and the payload behaviour (`signerName`) are unchanged, only
                    its position, its label and its label association moved. */}
                <label htmlFor="signer-name" className="mb-1 block text-sm text-ele-body">
                  Name of person signing (please print)
                </label>
                <input
                  id="signer-name"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="e.g. building manager's name"
                  className="mb-3 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
                />
                {signature !== null && signature === restoredSignature ? (
                  /* Session APF: a signature that came back from a draft. The pad is an uncontrolled
                     canvas and cannot be handed an image, so it would sit EMPTY while the sheet held
                     a signature and the submit button was live. The saved image is shown in the pad's
                     own box instead (white, because the ink is dark and the PNG is transparent), and
                     "Sign again" clears it and puts the empty pad back. */
                  <div>
                    <img
                      src={signature}
                      alt="Customer signature"
                      className="h-40 w-full rounded-ele border border-ele-field-border bg-white object-contain"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSignature(null);
                        setRestoredSignature(null);
                      }}
                      className="mt-1 flex min-h-11 items-center font-menu text-xs tracking-wider text-ele-green"
                    >
                      Sign again
                    </button>
                  </div>
                ) : (
                  <SignaturePad
                    key={job.id}
                    onChange={(d) => {
                      setSignature(d);
                      if (d) setSkipReason("");
                    }}
                  />
                )}
                <p className="mt-1 text-xs text-ele-body opacity-70">
                  Ask the customer to sign above to confirm the work is complete.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSkipping(true);
                    setSignature(null);
                  }}
                  className="mt-1 flex min-h-11 items-center font-menu text-xs tracking-wider text-ele-body underline opacity-80"
                >
                  Customer cannot sign?
                </button>
              </>
            ) : (
              <div className="rounded-lg border border-ele-card-border bg-ele-card p-3">
                <p className="mb-1 block text-sm text-ele-body">Reason no signature was taken</p>
                <ChoiceChips
                  label="Reason no signature was taken"
                  value={skipReason}
                  onChange={(v) => setSkipReason(v as SignatureSkipReason | "")}
                  options={[
                    { value: "customer_unavailable", label: "Customer unavailable" },
                    { value: "remote_site", label: "Remote site (no one to sign)" },
                    { value: "emergency", label: "Emergency (no time to sign)" },
                  ]}
                />
                <button
                  type="button"
                  onClick={() => {
                    setSkipping(false);
                    setSkipReason("");
                  }}
                  className="mt-1 flex min-h-11 items-center font-menu text-xs tracking-wider text-ele-green"
                >
                  Back to signing
                </button>
              </div>
            )}
          </section>
          )}

          {/* Session APF: THE SUBMIT BUTTON AND THE CONFIRM STEP USED TO BE HERE, at the very foot of
              the sheet, and are now in the fixed SubmitBar rendered just below (one home for each
              control). What stays in the page flow is what explains them: the refusal, and the three
              small hint lines. The refusal keeps the alert role HERE, so it is announced once; the
              bar repeats the same words, without the role, where the engineer has just tapped. */}
          {checklistError && (
            <p role="alert" className="mt-4 rounded-ele border border-ele-danger bg-ele-danger/10 px-3 py-2 text-sm text-ele-danger">
              {checklistError}
            </p>
          )}
          {!isInsuranceCheck && !signature && !skipReason && (
            <p className="mt-2 text-center text-xs text-ele-body opacity-70">
              Capture the customer signature, or choose a reason it could not be taken, to submit.
            </p>
          )}
          {!ramsAcknowledged && (
            <p className="mt-1 text-center text-xs text-[var(--ele-amber-text-deep)]">Confirm you have read the RAMS above to submit.</p>
          )}
          <p className="mt-2 text-center text-xs text-ele-body opacity-70">
            Saved to this device straight away. It uploads automatically when you are back online.
          </p>

          {/* The bar steps aside while the engineer is typing (typingFocus, above), EXCEPT while it
              holds the confirm step: the time fields stay live above an open confirm so a time can be
              corrected, and correcting one must not make "Confirm and submit" vanish mid-decision. */}
          {(!barSteppedAside || confirmingSubmit) && (
            <SubmitBar
              error={checklistError}
              confirming={confirmingSubmit}
              signatureSettled={signatureSettled}
              toSignatureLabel={leavingTagPending ? "Leaving: tag and signature" : "Customer signature"}
              onToSignature={toSignature}
              submitLabel={submitLabel}
              submitDisabled={submitBlocked}
              onSubmit={requestSubmit}
              confirmTimes={confirmTimes}
              confirmVisitLine={confirmTimes ? confirmVisitLine : null}
              // Session AFZ: the RAMS gate applies at the confirm step too (defense-in-depth).
              confirmDisabled={saving || (!isInsuranceCheck && !signature && !skipReason) || !ramsAcknowledged}
              saving={saving}
              onConfirm={() => void onComplete()}
              onCancel={() => setConfirmingSubmit(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

// ---- Session APF: the submit bar (PWA audit finding 4 / fix 6) -------------------------------------
//
// WHAT WAS WRONG, measured by the audit at 390 x 844. A service sheet is 4.9 screens tall and a
// call-out 3.7, and the customer signature, the submit button and the confirm step were all at the
// very foot of it: 9 to 15 flicks from wherever the engineer had been working. Worse, the confirm
// step's "Confirm and submit" button opened UNDERNEATH the shell's fixed bottom tab bar, so the last
// tap of every visit was on a button the engineer first had to scroll out from under the navigation.
//
// WHAT THIS IS. One bar, fixed directly ABOVE the bottom tab bar on an active sheet, that is always
// in reach and always shows the next thing to do:
//
//   1. NO SIGNATURE AND NO REASON THERE IS NONE YET: one button that scrolls the foot of the sheet
//      into view. One tap instead of nine to fifteen flicks. It reads "Customer signature", or
//      "Leaving: tag and signature" while the leaving POP tag step is on the screen and still waiting
//      (the parent decides which, and where it scrolls). It is an outline, not a fill: it takes the
//      engineer somewhere, it does not submit anything. Pressing it is never REQUIRED: the moment a
//      signature or a reason exists, however the engineer got there, the bar moves on by itself.
//   2. ONCE THERE IS ONE: the submit button, under the exact per-type name it has always had, wired
//      to the same requestSubmit and disabled by the same predicate.
//   3. WHILE CONFIRMING: the confirm step itself, in the bar. The question, the times that are about
//      to become the record, "Confirm and submit" and "Cancel". So it can never open under the tab
//      bar again.
//
// THE TWO-STEP CONFIRM STAYS. The branded-confirm rule requires a consequential, irreversible action
// to confirm in two steps, and a sheet cannot be edited once it is submitted. With both steps in the
// bar it costs one more tap and no scrolling. It also puts the two buttons in the same place on the
// screen, so the second one refuses to be the tail of a double-tap (see its onClick).
//
// ONE HOME FOR EACH CONTROL. This REPLACES the button and the confirm panel that stood at the foot of
// the sheet; they are not kept as well. (The equipment and condition-report checklists keep their own
// fast-path button at the top of the checklist, as they always have.)
//
// WHERE IT SITS. `bottom` is the tab bar's own height: EngineerLayout gives each tab min-h-[56px] and
// pads the bar by the safe-area inset, and this adds the same two figures. The tab bar's hairline
// makes it that much taller again, so this bar tucks UNDER it by the width of the hairline rather
// than leaving a strip of page showing between the two, which is why it is z-30 to the tab bar's
// z-40. (Measured in a real browser at 390 x 844: tab bar 787 to 844px, this bar 709 to 788px, its
// button 54px tall; holding the confirm step it grows UPWARDS, to 193px at its tallest, and "Confirm
// and submit" stays at 728 to 776px, clear of the tab bar.) Both are below the shell's z-50 layer
// (the "Needs the office" panel, the document viewer), and above the sheet. It is painted the way the
// tab bar paints itself, the shell ground and the card hairline, so it is right in the light theme
// and the dark one. While it holds the confirm step the hairline turns green, the colour the confirm
// panel's border always was.
//
// IT IS NOT PORTALLED to the document body, deliberately: the light theme's tokens bind only inside
// .engineer-ui (src/styles/ele-theme.css), and a bar lifted out of that scope would stay dark on a
// light sheet.
//
// IF YOU EVER MEASURE IT AND FIND IT AT THE FOOT OF THE DOCUMENT INSTEAD OF THE SCREEN, look at the
// harness before the code. The shell's <main> carries .ele-fade-in, which animates a transform for
// its first 0.2 seconds, and an element with a transform is what a position:fixed child is placed
// against. In a browser that paints, that is over before the job has loaded. In one that does not
// advance its animation clock (the desktop app's Browser pane while it is not being drawn) the
// animation sits at its first frame for ever, and this bar reads as thousands of pixels down the
// page. Finish the animation (getAnimations() then finish()) and measure again. The diary's drawer
// and the timesheet's confirm sheet are fixed inside the same <main> and behave the same way.
//
// The parent decides WHETHER the bar is on the screen at all (an active sheet, and not while the
// engineer is typing); this component only decides what it shows.
function SubmitBar({
  error,
  confirming,
  signatureSettled,
  toSignatureLabel,
  onToSignature,
  submitLabel,
  submitDisabled,
  onSubmit,
  confirmTimes,
  confirmVisitLine,
  confirmDisabled,
  saving,
  onConfirm,
  onCancel,
}: {
  // The sheet's refusal, repeated here so it is seen where the engineer just tapped. The alert ROLE
  // stays on the copy in the page flow, so a screen reader announces it once and not twice.
  error: string | null;
  confirming: boolean;
  signatureSettled: boolean;
  toSignatureLabel: string;
  onToSignature: () => void;
  submitLabel: string;
  submitDisabled: boolean;
  onSubmit: () => void;
  // What the confirm step states is about to be recorded (see confirmTimes in the parent), or null.
  confirmTimes: ResolvedVisitTimes | null;
  // Session BAR: the visit day being recorded, stated before the times, or null.
  confirmVisitLine: string | null;
  confirmDisabled: boolean;
  saving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const timesWillBeRecorded = !!confirmTimes && (!!confirmTimes.arrivalISO || !!confirmTimes.departureISO);
  return (
    <div
      role="region"
      aria-label="Submit this job"
      className={`fixed inset-x-0 z-30 border-t bg-[var(--ele-shell)] px-4 py-3 shadow-[0_-4px_12px_var(--ele-shadow)] ${confirming ? "border-ele-green" : "border-ele-card-border"}`}
      style={{ bottom: "calc(56px + env(safe-area-inset-bottom))" }}
    >
      {error && <p className="mb-2 rounded-lg border border-ele-danger bg-ele-danger/10 px-3 py-2 text-sm text-ele-danger">{error}</p>}
      {confirming ? (
        <div className="text-center">
          <p className="text-sm text-ele-strong">Submit this job? You will not be able to edit it after submission.</p>
          {/* Session APF: the honest counterpart of pre-filling. The times are about to become the
              record, some of them set by a tap or a fallback rather than typed, so the engineer is
              shown them first (see confirmTimes). Amber when no times will be recorded at all,
              because that is the one case they may want to go back and fix. */}
          {confirmVisitLine && <p className="mt-3 font-heading text-lg font-semibold text-ele-strong">{confirmVisitLine}</p>}
          {confirmTimes && (
            <p
              className={`mt-2 text-sm ${timesWillBeRecorded ? "text-ele-body" : ""}`}
              style={timesWillBeRecorded ? undefined : { color: "var(--ele-amber-text)" }}
            >
              {visitTimesLine(confirmTimes)}
            </p>
          )}
          <div className="mt-3 flex gap-3">
            <button
              type="button"
              // A DOUBLE-TAP ON THE SUBMIT BUTTON MUST NOT ALSO BE THE CONFIRM. The bar is anchored
              // at its foot, so this button opens almost exactly where the submit button was a
              // moment before (measured at 390 x 844: the submit at 724 to 776px, this at 728 to
              // 776px). At the foot of the sheet the old panel opened its button a good way BELOW the
              // first tap, so a second tap landed on the question; here it would land on this, and
              // a gloved double-tap would send a sheet nobody had been shown the times for. The
              // browser counts the taps of one gesture in `detail`, so the second (or third) tap of
              // a burst is ignored, and a tap made after a pause counts as 1 and goes through. A
              // keyboard press, and a test's click, carry 0.
              onClick={(e) => {
                if (e.detail > 1) return;
                onConfirm();
              }}
              disabled={confirmDisabled}
              className="min-h-11 flex-1 rounded-xl bg-ele-green py-3 font-heading text-lg tracking-wide text-ele-bg shadow-[0_1px_3px_var(--ele-shadow)] transition hover:brightness-110 active:brightness-95 disabled:opacity-50"
            >
              {saving ? "Submitting..." : "Confirm and submit"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="min-h-11 rounded-xl border border-ele-hairline-strong px-5 py-3 font-menu text-sm tracking-wide text-ele-strong transition-colors hover:bg-ele-row-hover disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : signatureSettled ? (
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitDisabled}
          className="min-h-11 w-full rounded-xl bg-ele-green py-3 font-heading text-lg tracking-wide text-ele-bg shadow-[0_1px_3px_var(--ele-shadow)] transition hover:brightness-110 active:brightness-95 disabled:opacity-50"
        >
          {submitLabel}
        </button>
      ) : (
        <button
          type="button"
          onClick={onToSignature}
          className="min-h-11 w-full rounded-xl border border-ele-green py-3 font-heading text-lg tracking-wide text-ele-green transition-colors hover:bg-ele-green hover:text-ele-bg"
        >
          {toSignatureLabel}
        </button>
      )}
    </div>
  );
}

// Photos section: capture from the device camera (or pick from the library), show
// each as a thumbnail with its upload status, and upload immediately when online.
// Offline captures are saved to the IndexedDB photo outbox and upload automatically
// on reconnect via the shared sync loop -- so a photo taken in a no-signal basement
// is never lost.
interface PhotoTile {
  id: string;
  previewUrl: string;
  attempts: number;
  lastError?: string;
}

// ---- "Flag something": raise a defect from the job screen (Session ANU) ------------------------
//
// ELE's Service Standards document, already with a customer, tells engineers to flag anything
// genuinely chargeable -- door shoes, belts, car rollers -- WITH A PHOTO TO BACK IT UP. The words
// have always had a home (the "Other defects found" box below raises real Defect rows on office
// approval, and works with no signal). The photo never did: Defect.photos has existed since the
// field loop shipped and nothing anywhere had ever written to it.
//
// THERE IS STILL EXACTLY ONE PHOTO CAPTURE PATH ON THIS SCREEN. This does not add a second. It
// calls the SAME hidden picker the Photos section owns (openPhotoPicker, already shared with the
// area checklist's own "Add photo"), so every image still rides queuePhoto, the offline outbox and
// POST /:id/photos. The engineer then ticks which of the job's photos back this flag up, and the
// defect stores those. A photo taken for the flag is therefore also on the service record, which is
// correct: it is evidence of the visit either way.
//
// OFFLINE IT FALLS BACK RATHER THAN BLOCKING. Flagging writes to the platform straight away, which
// needs a signal, and a queued photo has no Drive URL to attach until it uploads. So with no
// connection the one control changes what it says and what it does: it appends the text to the
// sheet's own defects box, which has always worked offline and raises the same Defect record on
// approval. Nothing is lost, nothing is duplicated (online writes the defect and never touches the
// box; offline touches the box and never writes a defect), and the engineer is told plainly which
// of the two just happened.
function FlagDefect({ job, onAddToSheet, openPhotoPicker }: { job: JobView; onAddToSheet: (text: string) => void; openPhotoPicker: () => void }) {
  const { online, syncNow } = useEngineerData();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [picked, setPicked] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const fieldId = useId();

  function reset() {
    setDescription("");
    setPicked([]);
    setError(null);
  }

  function togglePhoto(i: number) {
    setPicked((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]));
  }

  async function submit() {
    const text = description.trim();
    if (!text) {
      setError("Say what you found before flagging it.");
      return;
    }
    if (!online) {
      // The offline path: the sheet's own defects box, which raises the same Defect record when the
      // completed sheet is approved. Deliberately additive to whatever is already typed there.
      onAddToSheet(text);
      reset();
      setOpen(false);
      setDone("Added to the job sheet. It will be raised as a defect when you submit the sheet.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.jobs.flagDefect(job.id, { description: text, photoIndexes: picked.length > 0 ? picked : undefined });
      reset();
      setOpen(false);
      setDone(
        res.photoCount > 0
          ? `Flagged to the office with ${res.photoCount} photo${res.photoCount === 1 ? "" : "s"}.`
          : "Flagged to the office.",
      );
      // The office Defects page reads the platform, not this device, so nothing here needs the
      // response. This refreshes the job so a photo uploaded moments ago shows up in the tick list
      // for the next flag.
      void syncNow();
    } catch {
      setError("Could not flag that just now. Add it to the defects box on the sheet below instead, and it will be raised when you submit.");
    } finally {
      setBusy(false);
    }
  }

  // hasLift is optional on a job cached before this shipped, and absent reads as TRUE (see
  // CachedJob.hasLift): withholding the control from every already-cached job would take the
  // feature away from exactly the engineer who is offline. Placed after the hooks, never before
  // them, so the hook order is identical on every render.
  if (job.hasLift === false) return null;

  return (
    <section className="mt-5 rounded-ele border border-amber-500 bg-ele-surface p-4">
      <h2 className="font-heading text-lg text-ele-blue">Flag something</h2>
      <p className="mt-1 text-sm text-ele-body">
        Noticed something the office should quote for? Worn door shoes, a belt, car rollers. Flag it here with a photo and it goes straight
        onto the office defects list against this lift.
      </p>

      {done && (
        <p role="status" className="mt-3 rounded-ele border border-ele-green bg-ele-green/10 px-3 py-2 text-sm text-ele-strong">
          {done}
        </p>
      )}

      {!open ? (
        <button
          type="button"
          onClick={() => {
            setDone(null);
            setOpen(true);
          }}
          className="mt-3 flex min-h-11 w-full items-center justify-center rounded-ele border border-amber-500 py-3 font-heading text-sm tracking-wide text-[var(--ele-amber-text)] transition hover:bg-amber-500/10"
        >
          Flag something
        </button>
      ) : (
        <div className="mt-3">
          <label htmlFor={fieldId} className="mb-1 block text-sm text-ele-body">
            What did you find?
          </label>
          <textarea
            id={fieldId}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (error) setError(null);
            }}
            rows={3}
            placeholder="e.g. Car door shoes worn, running on the metal. Needs replacing."
            className="w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
          />

          {online && (
            <>
              <p className="mt-3 font-menu text-[11px] uppercase tracking-wider text-ele-body opacity-80">Photos backing this up</p>
              {job.photos.length > 0 ? (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {job.photos.map((url, i) => {
                    const on = picked.includes(i);
                    return (
                      <button
                        key={`fd-${i}`}
                        type="button"
                        aria-pressed={on}
                        aria-label={`Photo ${i + 1}${on ? ", attached to this flag" : ""}`}
                        onClick={() => togglePhoto(i)}
                        className={`relative block aspect-square overflow-hidden rounded-ele border-2 ${on ? "border-ele-green" : "border-ele-field-border"}`}
                      >
                        <img src={url} alt="" className={`h-full w-full object-cover ${on ? "" : "opacity-60"}`} />
                        {on && (
                          <span className="absolute bottom-0 left-0 right-0 bg-ele-green py-0.5 text-center font-menu text-[9px] uppercase tracking-wide text-ele-bg">
                            Attached
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-1 text-xs text-ele-body opacity-70">
                  No photos on this job yet. Take one with Add photo below, then tick it here.
                </p>
              )}
              <button
                type="button"
                onClick={openPhotoPicker}
                className="mt-2 flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-blue py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
              >
                Add photo
              </button>
            </>
          )}

          {!online && (
            <p className="mt-3 text-xs text-ele-body opacity-80">
              You are offline. This will be added to the defects box on the sheet below and raised when you submit the visit. Photos you take
              stay on the job sheet.
            </p>
          )}

          {error && (
            <p role="alert" className="mt-2 text-sm text-ele-danger">
              {error}
            </p>
          )}

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="flex min-h-11 flex-1 items-center justify-center rounded-ele bg-ele-green py-2 font-heading text-sm tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-50"
            >
              {busy ? "Flagging..." : online ? "Flag it" : "Add to the job sheet"}
            </button>
            <button
              type="button"
              onClick={() => {
                reset();
                setOpen(false);
              }}
              disabled={busy}
              className="flex min-h-11 items-center justify-center rounded-ele border border-ele-field-border px-4 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ---- "Found more work?": raise a repair estimate from the job screen (Session ATF) --------------------
//
// An engineer who finds work beyond this visit (a worn part, a fault beyond the call-out) records it here, where they
// stand, instead of ringing the office. Director decision, 29 September 2026: THE ENGINEER NEVER PRICES IT. They say
// what the work is, what parts it needs and roughly how long it would take on site; the office sets the price, checks
// the wording and decides whether to send it. So this card carries NO money field and says so (the Session APF rule,
// pinned for this card by JobDetailRaiseEstimate.test.tsx), and it says plainly that nothing goes to the customer,
// so it is never read as "sent". The server refuses a price outright and sends nothing (engineerRepairEstimate.routes.ts).
//
// ONLINE ONLY, and said so rather than hidden: the estimate is written to the platform straight away and is not queued
// through the offline outbox. With no signal the button is disabled and the words typed so far stay in the form; the
// engineer can also put it in the sheet's own notes, which the office reads on approval.
export function RaiseRepairEstimate({ job }: { job: JobView }) {
  const { online } = useEngineerData();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [parts, setParts] = useState("");
  const [hours, setHours] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const workId = useId();
  const partsId = useId();
  const hoursId = useId();

  function reset() {
    setDescription("");
    setParts("");
    setHours("");
    setError(null);
  }

  async function submit() {
    const text = description.trim();
    if (!text) {
      setError("Say what work is needed before sending it to the office.");
      return;
    }
    let estimatedHours: number | undefined;
    if (hours.trim()) {
      const n = Number(hours.trim());
      if (!Number.isFinite(n) || n <= 0 || n > 200) {
        setError("Give the time on site as a number of hours, for example 2 or 1.5.");
        return;
      }
      estimatedHours = n;
    }
    if (!online) {
      setError("You are offline. This needs a signal to reach the office. What you have typed is kept here.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.jobs.raiseRepairEstimate(job.id, {
        description: text,
        ...(parts.trim() ? { partsNeeded: parts.trim() } : {}),
        ...(estimatedHours != null ? { estimatedHours } : {}),
      });
      reset();
      setOpen(false);
      setDone(res.message || `Sent to the office as ${res.reference}. Nothing has gone to the customer.`);
    } catch (err) {
      const msg = err instanceof Error && err.message && err.message.includes(" ") ? err.message : null;
      setError(msg ?? "Could not send that to the office just now. What you typed is kept here: try again when you have a signal.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-5 rounded-ele border border-ele-card-border bg-ele-surface p-4">
      <h2 className="font-heading text-lg text-ele-blue">Found more work?</h2>
      <p className="mt-1 text-sm text-ele-body">
        Work this visit does not cover? Describe it here for a repair estimate. The office prices it and decides whether to send it to the
        customer. Nothing goes to the customer from here.
      </p>

      {done && (
        <p role="status" className="mt-3 rounded-ele border border-ele-green bg-ele-green/10 px-3 py-2 text-sm text-ele-strong">
          {done}
        </p>
      )}

      {!open ? (
        <button
          type="button"
          onClick={() => {
            setDone(null);
            setOpen(true);
          }}
          className="mt-3 flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-blue py-3 font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
        >
          Raise a repair estimate
        </button>
      ) : (
        <div className="mt-3">
          <label htmlFor={workId} className="mb-1 block text-sm text-ele-body">
            What work is needed?
          </label>
          <textarea
            id={workId}
            value={description}
            maxLength={2000}
            onChange={(e) => {
              setDescription(e.target.value);
              if (error) setError(null);
            }}
            rows={3}
            placeholder="e.g. Car door shoes worn through on both doors. Replace both sets."
            className="w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
          />
          <label htmlFor={partsId} className="mb-1 mt-3 block text-sm text-ele-body">
            Parts needed (optional)
          </label>
          <textarea
            id={partsId}
            value={parts}
            maxLength={1000}
            onChange={(e) => setParts(e.target.value)}
            rows={2}
            placeholder="e.g. 4 x car door shoes"
            className="w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
          />
          <label htmlFor={hoursId} className="mb-1 mt-3 block text-sm text-ele-body">
            Roughly how long on site, in hours (optional)
          </label>
          <input
            id={hoursId}
            inputMode="decimal"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="e.g. 2"
            className="w-32 rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
          />
          <p className="mt-3 text-xs text-ele-body opacity-80">
            You do not put a figure on it: the office does that. {online ? "This goes to the office straight away." : "You are offline: this needs a signal to send."}
          </p>

          {error && (
            <p role="alert" className="mt-2 text-sm text-ele-danger">
              {error}
            </p>
          )}

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={busy || !online}
              className="flex min-h-11 flex-1 items-center justify-center rounded-ele bg-ele-green py-2 font-heading text-sm tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-50"
            >
              {busy ? "Sending..." : "Send to the office"}
            </button>
            <button
              type="button"
              onClick={() => {
                reset();
                setOpen(false);
              }}
              disabled={busy}
              className="flex min-h-11 items-center justify-center rounded-ele border border-ele-field-border px-4 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function JobPhotos({ job }: { job: JobView }) {
  const { online, syncNow } = useEngineerData();
  const [queued, setQueued] = useState<PhotoTile[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const urlsRef = useRef<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  // Desktop file-drop highlight (Session ADE). Depth counter: dragenter/dragleave fire for every
  // child the drag crosses, so the highlight only clears when the drag leaves the whole section.
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);
  const [reorderBusy, setReorderBusy] = useState(false);
  // Long-press to pick a photo up, so an ordinary scroll over the grid is never hijacked; a plain
  // tap still opens the photo.
  const reorderSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { delay: 250, tolerance: 6 } }));
  // Suppress the synthetic click a completed drag leaves behind, so dropping a photo never also
  // opens it full-size.
  const justDragged = useRef(false);

  const reloadQueued = useCallback(async () => {
    const items = await queuedPhotosForJob(job.id);
    const tiles = items.map((it) => ({
      id: it.id,
      previewUrl: URL.createObjectURL(it.blob),
      attempts: it.attempts,
      lastError: it.lastError,
    }));
    // Swap state and revoke the URLs we are REPLACING in one step, so concurrent
    // reloads never strand a minted-but-untracked URL (no leak). urlsRef tracks the
    // live set for unmount cleanup.
    setQueued((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      urlsRef.current = tiles.map((t) => t.previewUrl);
      return tiles;
    });
  }, [job.id]);

  useEffect(() => {
    void reloadQueued();
    return () => {
      urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      urlsRef.current = [];
    };
  }, [reloadQueued]);

  // When connectivity returns, the shared loop uploads queued photos -- refresh the
  // pending tiles so uploaded ones drop off and move into the job's photo list.
  useEffect(() => {
    if (online) void reloadQueued();
  }, [online, reloadQueued]);

  function onSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (inputRef.current) inputRef.current.value = ""; // allow re-selecting the same file
    void addFiles(files);
  }

  // The ONE capture path for both the file picker and a desktop file drop: every photo goes
  // through the offline outbox (queuePhoto) so it survives no-signal and syncs on reconnect.
  // A drop never bypasses the outbox.
  async function addFiles(selected: File[]) {
    let files = selected.filter(isImageFile);
    if (files.length === 0) return;
    setError(null);
    // Cap at MAX_JOB_PHOTOS in total (already-uploaded + queued + this selection), matching
    // the PDF's 6-photo embed limit. Trim the selection and tell the engineer when it is full.
    const already = job.photos.length + queued.length;
    const room = MAX_JOB_PHOTOS - already;
    if (room <= 0) {
      setError(`You can attach up to ${MAX_JOB_PHOTOS} photos. Remove one before adding more.`);
      return;
    }
    if (files.length > room) {
      files = files.slice(0, room);
      setError(`Only ${MAX_JOB_PHOTOS} photos are kept per job, so the first ${room} were added.`);
    }
    setBusy(true);
    try {
      // Persist every photo to the outbox IMMEDIATELY, stamped with its capture time, so it
      // survives the app being killed underground (the never-lost invariant) before anything
      // slow happens. THEN read the device GPS (best-effort; it can take a moment) and attach
      // it to the stored photos before they upload. A denied/unavailable location resolves to
      // null and is silently skipped, so the photo is still captured and submission proceeds.
      const capturedAt = new Date().toISOString();
      const queuedItems: QueuedPhoto[] = [];
      for (const original of files) {
        // A library photo can be HEIC or very large: made a JPEG of sensible size before it is stored,
        // so the report can always draw it. Falls back to the original if the phone cannot decode it.
        const f = await prepareImageForUpload(original);
        queuedItems.push(await queuePhoto(job.id, f, f.type, { capturedAt }));
      }
      await reloadQueued();
      const pos = await getPositionSafe();
      if (pos) await attachPhotoGeo(queuedItems, pos);
      if (navigator.onLine) {
        await syncNow(); // uploads the queued photos (with their geotag) and refreshes job.photos
        await reloadQueued();
      }
    } catch {
      setError("Could not save a photo. It is kept on this device and will retry.");
    } finally {
      setBusy(false);
    }
  }

  function statusLabel(tile: PhotoTile): string {
    if (tile.lastError) return "Upload failed, will retry";
    return online ? "Uploading..." : "Saved, uploads when back online";
  }

  // Persist a drag-reordered display order (Session ADE; UPLOADED photos only -- a queued tile
  // has no server index yet and uploads to the end). The saved order is the server's
  // JobSheet.photos array itself (the office view and the customer PDF follow it), so reordering
  // needs a connection: offline, the tiles simply are not draggable.
  async function onReorderDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    justDragged.current = true;
    setTimeout(() => {
      justDragged.current = false;
    }, 0);
    if (!over || active.id === over.id) return;
    const order = photoOrderFromDrag(job.photos.length, String(active.id), String(over.id));
    if (!order) return;
    setReorderBusy(true);
    setError(null);
    try {
      await api.jobs.reorderPhotos(job.id, order);
      await syncNow(); // refreshes job.photos so the tiles re-render in the saved order
    } catch {
      setError("Could not save the new photo order. Check your connection and try again.");
    } finally {
      setReorderBusy(false);
    }
  }

  const sortable = online && job.photos.length > 1 && !reorderBusy;
  const uploadedTiles = job.photos.map((url, i) => (
    <UploadedPhotoTile key={`up-${i}`} id={`p${i}`} url={url} index={i} sortable={sortable} justDragged={justDragged} />
  ));

  return (
    <section
      className={`mt-5 rounded-ele border-2 border-dashed p-1 transition-colors ${dragOver ? "border-ele-green bg-ele-green/5" : "border-transparent"}`}
      onDragEnter={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        dragDepth.current += 1;
        setDragOver(true);
      }}
      onDragOver={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault(); // required, or the browser navigates to the dropped file
      }}
      onDragLeave={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragOver(false);
      }}
      onDrop={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        dragDepth.current = 0;
        setDragOver(false);
        // A dropped file takes the SAME offline-outbox path as a picked one (queuePhoto ->
        // sync on reconnect); a drop never bypasses the outbox.
        if (!busy) void addFiles(Array.from(e.dataTransfer.files));
      }}
    >
      <h2 className="mb-3 font-heading text-lg text-ele-blue">Photos</h2>

      {(job.photos.length > 0 || queued.length > 0) && (
        <div className="mb-3 grid grid-cols-3 gap-2">
          {sortable ? (
            <DndContext sensors={reorderSensors} collisionDetection={closestCenter} onDragEnd={onReorderDragEnd}>
              <SortableContext items={job.photos.map((_, i) => `p${i}`)} strategy={rectSortingStrategy}>
                {uploadedTiles}
              </SortableContext>
            </DndContext>
          ) : (
            uploadedTiles
          )}
          {queued.map((tile) => (
            <div
              key={tile.id}
              className="relative block aspect-square overflow-hidden rounded-ele border border-ele-field-border bg-ele-inset"
              title={statusLabel(tile)}
            >
              <img src={tile.previewUrl} alt="Pending photo" className="h-full w-full object-cover opacity-80" />
              {/* This caption sits ON the photo, so the whole overlay is theme-independent: the
                  scrim stays black in both themes, and its ink therefore stays light too. The
                  literal is the dark --ele-body value, kept as a literal deliberately -- the token
                  would flip to near-black on light and vanish into the scrim. */}
              <span
                className={`absolute bottom-0 left-0 right-0 py-0.5 text-center font-menu text-[9px] uppercase tracking-wide ${
                  tile.lastError ? "bg-ele-danger/80 text-ele-white" : "bg-black/70 text-[#CACACA]"
                }`}
              >
                {tile.lastError ? "Failed" : online ? "Uploading" : "Queued"}
              </span>
            </div>
          ))}
        </div>
      )}
      {sortable && (
        <p className="mb-2 text-xs text-ele-body opacity-60">Press and hold a photo, then drag to change the order. The order is saved and used on the report.</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onSelect}
        className="hidden"
        id={`photo-input-${job.id}`}
      />
      {job.photos.length + queued.length >= MAX_JOB_PHOTOS ? (
        <p className="rounded-lg border border-ele-card-border bg-ele-card py-3 text-center font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">
          Maximum of {MAX_JOB_PHOTOS} photos reached
        </p>
      ) : (
        <label
          htmlFor={`photo-input-${job.id}`}
          className={`block w-full cursor-pointer rounded-ele border border-ele-blue py-3 text-center font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10 ${
            busy ? "opacity-50" : ""
          }`}
        >
          {busy ? "Saving..." : "Add photo"}
        </label>
      )}
      {error && <p role="alert" className="mt-2 text-sm text-ele-danger">{error}</p>}
      {!online && (
        <p className="mt-2 text-xs text-ele-body opacity-70">
          Photos taken offline are saved to this device and upload automatically when you are back online.
        </p>
      )}
    </section>
  );
}

// One uploaded photo tile. When sortable, the whole tile is a long-press drag handle (useSortable);
// a completed drag suppresses the synthetic click it leaves behind (via justDragged) so dropping a
// photo never also opens it full-size. Not sortable offline (the saved order lives server-side).
function UploadedPhotoTile({
  id,
  url,
  index,
  sortable,
  justDragged,
}: {
  id: string;
  url: string;
  index: number;
  sortable: boolean;
  justDragged: React.MutableRefObject<boolean>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !sortable });
  return (
    <a
      ref={setNodeRef}
      {...(sortable ? { ...attributes, ...listeners } : {})}
      style={sortable ? { transform: CSS.Transform.toString(transform), transition, ...(isDragging ? { zIndex: 10, opacity: 0.85 } : {}) } : undefined}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      draggable={false}
      onClick={(e) => {
        if (justDragged.current) e.preventDefault();
      }}
      className={`relative block aspect-square overflow-hidden rounded-ele border bg-ele-inset ${
        isDragging ? "border-ele-green ring-2 ring-ele-green" : "border-ele-green"
      }`}
      title="Uploaded"
    >
      <img src={url} alt={`Job photo ${index + 1}`} draggable={false} className="h-full w-full object-cover" />
      <span className="absolute bottom-0 left-0 right-0 bg-ele-green/80 py-0.5 text-center font-menu text-[9px] uppercase tracking-wide text-ele-bg">
        Uploaded
      </span>
    </a>
  );
}

function BackLink() {
  return (
    <Link to="/engineer" className="inline-flex min-h-11 items-center font-menu text-xs tracking-wider text-ele-green">
      &larr; Back to jobs
    </Link>
  );
}

// The job sheet's shared labelled field (Session AMF fixed its label association; flagged by
// Session ALW, which hit it while writing a test that could not query by label).
//
// WHAT WAS WRONG. It rendered `<label>{label}</label>` with the control as a SIBLING and no
// `htmlFor` anywhere, so on EVERY field of the sheet -- the call-out report, the arrival and
// departure times, the travel hours, the defects, the advisory and engineer notes -- the label and
// its box were not programmatically associated. A screen reader landing in the textarea announced
// nothing about what it was for, and tapping the label text did not focus the control. On a phone,
// on a job sheet an engineer fills in standing in a lift lobby, that is the least forgiving place
// on the platform for it.
//
// THE FIX IS IN THE HELPER, NOT AT TEN CALL SITES. `useId` mints a stable, collision-free id per
// rendered field (two Fields side by side, the arrival and departure times, get different ones),
// the label carries `htmlFor`, and the id is handed to the child element. So every existing call
// site is corrected by this one change and any future one inherits it. (Session APF retired those
// two time selects in favour of the Time on site card; TimeColumn below gives the fields that
// replaced them the same one-id-per-field association.)
//
// WHY cloneElement RATHER THAN WRAPPING THE CONTROL IN THE LABEL. Implicit association (a `<label>`
// containing its control) would also have worked and would have needed no cloning -- but the
// accessible-name computation for a wrapping label folds in the embedded control's own VALUE, so
// "Lift status on leaving site" would announce as "Lift status on leaving site Working normally"
// once a selection was made, and the arrival-time select likewise. An explicit htmlFor/id pair
// gives the control exactly the name the label says and nothing else.
//
// A CHILD THAT ALREADY CARRIES AN id KEEPS IT, and the label points at it. That is what lets a call
// site opt out (or pass an id it also references from elsewhere) without a second component.
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  const generated = useId();
  const child = isValidElement(children) ? (children as React.ReactElement<{ id?: string }>) : null;
  const id = child?.props.id ?? generated;
  return (
    <div className="mb-4">
      <label htmlFor={id} className="mb-1 block text-sm text-ele-body">
        {label}
      </label>
      {child && !child.props.id ? cloneElement(child, { id }) : children}
    </div>
  );
}

// Session BAS: a short list of choices as large tap targets instead of a dropdown, on the phone's main
// flow (the no-signature reason, the lift status on leaving). One tap, no native picker sheet, every
// option visible at once and at least 48px tall. Tapping the chosen one again leaves it chosen; the
// value is the same string the dropdown sent, so nothing about the record changes.
function ChoiceChips({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-col gap-2">
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={`min-h-12 w-full rounded-ele border px-3 py-2 text-left font-body text-sm transition ${
              on ? "border-[#7d21c7] bg-[#7d21c7] text-white" : "border-ele-field-border bg-ele-field text-ele-strong"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// A labelled READ-ONLY value on the job sheet, visually identical to a Field but semantically
// honest (Session AMF). A `<label>` labels a form control; the reported fault is a fact being
// displayed, not a box being filled in, so pointing a label's `htmlFor` at the div that shows it
// would be invalid markup and would give a screen reader a label with nothing to label. This is
// the one Field call site that was never a field.
function ReadOnlyField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <p className="mb-1 block text-sm text-ele-body">{label}</p>
      {children}
    </div>
  );
}

// "2h 15m" / "45m" from a whole number of minutes (never negative). Pure (unit tested).
export function formatDuration(totalMinutes: number): string {
  const m = Math.max(0, Math.round(totalMinutes));
  const h = Math.floor(m / 60);
  const mins = m % 60;
  return h > 0 ? `${h}h ${mins}m` : `${mins}m`;
}

// Parse "HH:MM" to minutes-since-midnight, or null when malformed / out of range. Pure.
function parseHHMM(v: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

// Minutes between two same-day "HH:MM" times, or null when either is missing/invalid. Pure.
export function minutesBetweenHHMM(from: string, to: string): number | null {
  const a = parseHHMM(from);
  const b = parseHHMM(to);
  if (a == null || b == null) return null;
  return b - a;
}

// The line the confirm panel shows under "Submit this job?" (Session APF): the times that are about
// to become the record, in the engineer's own clock, before they are sent. Pure (unit tested), and
// fed by resolveVisitTimes, so it can only ever describe what the submit will actually send.
//
// The duration is counted between the two times AS SHOWN, to the minute, so "arrived 10:31, left
// 11:24" always reads 53 minutes even when the two taps behind it fell at different seconds. It is
// left off when the pair does not run forwards: that is a data slip the server declines to bill,
// and a made-up duration beside it would only make it look deliberate.
//
// With neither time, the sheet is a call-out, a repair or a tech visit with no Arrived time, and
// the engineer is told plainly that no times will go on the report and how to change that. It asks
// for the time they ARRIVED, never for a tap of Now: at this point "now" is when they are leaving,
// and an arrival recorded as the moment of submission is exactly the invented attendance time the
// call-out SLA clock rule forbids.
export function visitTimesLine(times: { arrivalISO: string | null; departureISO: string | null }): string {
  const arrived = isoToLocalHHMM(times.arrivalISO);
  const left = isoToLocalHHMM(times.departureISO);
  if (arrived && left) {
    const toTheMinute = (iso: string) => Math.floor(new Date(iso).getTime() / 60_000);
    const mins = toTheMinute(times.departureISO!) - toTheMinute(times.arrivalISO!);
    return mins > 0
      ? `Times being recorded: arrived ${arrived}, left ${left} (${formatDuration(mins)} on site).`
      : `Times being recorded: arrived ${arrived}, left ${left}.`;
  }
  if (left) return `Times being recorded: left ${left}.`;
  if (arrived) return `Times being recorded: arrived ${arrived}.`;
  return "No arrival time is set, so no times will be recorded. Cancel and enter the time you arrived if you want them on the report.";
}

// ---- Session BAR: one time field, with no clock dial -------------------------------------------
//
// THE DIRECTOR'S WORDS: "the clock dial when choosing time is horrible and not intuitive". This used
// to be a native <input type="time">, which a phone draws as the operating system's own clock dial
// (or spinning wheels, with AM and PM). It is now three ways to set a time, none of them a dial:
//
//   1. NOW, first and filled green: the one tap an engineer makes on arriving and on leaving.
//   2. Quick picks under the field: while it is empty, three round times near the visit (the time it
//      was booked for, or now); once it holds a time, steppers of 15 and 5 minutes either way, and on
//      Left, "Same as arrived" to start from the arrival.
//   3. Typing, on the numeric keypad: four digits ("0930") become 09:30 as the fourth is typed, a
//      colon is accepted, and the field is 24-hour throughout. A value that is not a time is put back.
//
// THE FIELD'S CONTRACT IS UNCHANGED: `value` is "HH:MM" (or "" when empty), `onChange` is called
// with one, and the field still carries aria-label "<label> time", tied to its visible label, so
// everything that reads or sets it (the draft, the POP tag pre-fill, the tests) works as before.
function TimeColumn({
  label,
  value,
  onChange,
  showNow = true,
  anchor,
  sameAs,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  // Session BAR: Now is today's clock, so a visit on an earlier day does not offer it.
  showNow?: boolean;
  // A "HH:MM" the quick picks are built round while the field is empty.
  anchor?: string;
  // Left only: the arrival, offered as "Same as arrived".
  sameAs?: string;
}) {
  const id = useId();
  const hintId = useId();
  // What is in the box while the engineer types. It differs from `value` only mid-entry ("93"), and
  // is brought back into line whenever the value changes from outside (Now, a tap, a draft restore).
  const [text, setText] = useState(value);
  const [hint, setHint] = useState(false);
  useEffect(() => {
    setText((current) => (parseTypedTime(current) === value && current !== "" ? current : value));
  }, [value]);

  function commit(next: string) {
    setHint(false);
    setText(next);
    onChange(next);
  }
  function markNow() {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    commit(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
  }
  function onType(raw: string) {
    const cleaned = raw.replace(/[^\d:.]/g, "").slice(0, 5);
    if (cleaned === "") {
      commit("");
      return;
    }
    const digits = cleaned.replace(/\D/g, "");
    // A colon, or a fourth digit, makes it a whole time: it is committed (and shown "HH:MM") at once.
    if (/[:.]\d{2}$/.test(cleaned) || (digits.length === 4 && !/[:.]/.test(cleaned))) {
      const parsed = parseTypedTime(cleaned);
      if (parsed) {
        commit(parsed);
        return;
      }
    }
    setText(cleaned);
  }
  function onBlur() {
    if (text === value) return;
    const parsed = parseTypedTime(text);
    if (parsed) {
      commit(parsed);
    } else {
      setText(value);
      setHint(true);
    }
  }

  const round = (hhmm: string, step: number) => {
    const [h, m] = hhmm.split(":").map(Number);
    const total = h! * 60 + m!;
    return shiftHHMM("00:00", total - (total % step));
  };
  const base = anchor && /^\d{2}:\d{2}$/.test(anchor) ? round(anchor, 15) : null;
  const picks = base ? [shiftHHMM(base, -30), shiftHHMM(base, -15), base] : [];
  const stepBtn =
    "min-h-11 min-w-11 flex-1 rounded-xl border border-ele-card-border bg-ele-field px-1 py-2 font-heading text-base text-ele-strong transition-colors hover:bg-ele-row-hover active:bg-ele-inset";

  return (
    <div>
      <label htmlFor={id} className="mb-1 block font-menu text-[11px] uppercase tracking-wider text-ele-muted">{label}</label>
      <div className="flex items-stretch gap-2">
        {showNow && (
          <button
            type="button"
            onClick={markNow}
            className="min-h-12 shrink-0 rounded-xl bg-ele-green px-5 font-heading text-lg font-semibold tracking-wide text-ele-bg shadow-[0_1px_3px_var(--ele-shadow)] transition active:brightness-95"
          >
            Now
          </button>
        )}
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          enterKeyHint="done"
          placeholder="HH:MM"
          maxLength={5}
          value={text}
          onChange={(e) => onType(e.target.value)}
          onBlur={onBlur}
          aria-label={`${label} time`}
          aria-describedby={hint ? hintId : undefined}
          className="min-h-12 min-w-0 flex-1 rounded-xl border border-ele-field-border bg-ele-field px-3 py-2 text-center font-heading text-2xl tracking-wider text-ele-strong outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
        />
      </div>
      {hint && (
        <p id={hintId} className="mt-1 text-xs" style={{ color: "var(--ele-amber-text)" }}>
          Type the time as four digits, for example 0930 or 1745.
        </p>
      )}
      <div className="mt-2 flex gap-2">
        {value ? (
          <>
            <button type="button" className={stepBtn} aria-label={`${label} 15 minutes earlier`} onClick={() => commit(shiftHHMM(value, -15))}>
              -15
            </button>
            <button type="button" className={stepBtn} aria-label={`${label} 5 minutes earlier`} onClick={() => commit(shiftHHMM(value, -5))}>
              -5
            </button>
            <button type="button" className={stepBtn} aria-label={`${label} 5 minutes later`} onClick={() => commit(shiftHHMM(value, 5))}>
              +5
            </button>
            <button type="button" className={stepBtn} aria-label={`${label} 15 minutes later`} onClick={() => commit(shiftHHMM(value, 15))}>
              +15
            </button>
          </>
        ) : (
          <>
            {sameAs && (
              <button type="button" className={stepBtn} onClick={() => commit(sameAs)}>
                Same as arrived
              </button>
            )}
            {picks.map((t) => (
              <button key={t} type="button" className={stepBtn} aria-label={`Set ${label} to ${t}`} onClick={() => commit(t)}>
                {t}
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ---- Session BAR: which day the visit was ------------------------------------------------------
//
// Three ways, in the order they are needed: Today, Yesterday, and "Pick date" for anything earlier,
// which opens the phone's own calendar with no future day on it. The chosen day is printed large so
// it is never missed.
//
// AN OVERDUE JOB (booked for an earlier day) opens with NO day chosen (the BAR follow-up): opening it
// on the booked day recorded the wrong day whenever the overdue work was done today, and nobody
// noticed. It shows Today, Yesterday (when that is not the booked day) and the booked day by its date
// as large chips, and the sheet cannot be sent until one is tapped ("Tap the day you did this job.").
function VisitDateRow({
  visitYmd,
  todayYmd,
  bookedYmd,
  chosen = true,
  onVisitDate,
}: {
  visitYmd: string;
  todayYmd: string;
  bookedYmd: string;
  chosen?: boolean;
  onVisitDate: (ymd: string) => void;
}) {
  const [picking, setPicking] = useState(false);
  const yesterday = addDaysYmd(todayYmd, -1);
  const overdue = !!bookedYmd && bookedYmd < todayYmd;
  const chip = (active: boolean) =>
    `min-h-12 flex-1 rounded-xl border px-2 py-2 font-menu text-sm tracking-wide transition-colors ${
      active ? "border-[#7d21c7] bg-[#7d21c7] text-white" : "border-ele-card-border bg-ele-field text-ele-strong"
    }`;
  const isToday = chosen && visitYmd === todayYmd;
  const isYesterday = chosen && visitYmd === yesterday;
  const isBooked = chosen && overdue && visitYmd === bookedYmd;
  const showYesterday = !overdue || bookedYmd !== yesterday;
  const isOther = chosen && !isToday && !isYesterday && !isBooked;
  const pick = (ymd: string) => { setPicking(false); onVisitDate(ymd); };
  // Session BBC: the earliest day the app takes, and whether the booked day is still inside the window.
  const earliest = addDaysYmd(todayYmd, -VISIT_DATE_HARD_DAYS);
  const bookedInWindow = overdue && bookedYmd >= earliest;
  const bookedLate = bookedInWindow && calendarDaysBetween(bookedYmd, todayYmd) > VISIT_DATE_SOFT_DAYS;
  const pickableDays: string[] = [];
  for (let n = 2; n <= VISIT_DATE_HARD_DAYS; n += 1) pickableDays.push(addDaysYmd(todayYmd, -n));
  return (
    <div className="mb-4">
      <p className="mb-1 flex items-center gap-2 font-menu text-[11px] uppercase tracking-wider text-ele-muted">
        Visit date
        {/* A job booked for an earlier day and still open: the same word, and the same red, the job
            lists use, so the engineer knows this is the overdue job they tapped. */}
        {overdue && (
          <span className="rounded-full px-2 py-0.5 leading-none" style={{ color: "var(--ele-danger)", backgroundColor: "var(--ele-pill-tint-red)" }}>
            Overdue
          </span>
        )}
      </p>
      <p className="font-heading text-xl text-ele-strong" data-testid="visit-date-label">
        {chosen ? (
          <>
            {visitDayLabel(visitYmd)}
            {isToday ? " · today" : isYesterday ? " · yesterday" : ""}
            {calendarDaysBetween(visitYmd, todayYmd) > VISIT_DATE_SOFT_DAYS && (
              <span className="ml-2 rounded-full px-2 py-0.5 align-middle font-menu text-[11px] leading-none" style={{ color: "var(--ele-amber-text)", backgroundColor: "var(--ele-pill-tint-amber)" }}>
                Late
              </span>
            )}
          </>
        ) : (
          "Tap the day you did this job"
        )}
      </p>
      {!chosen && (
        <p className="mt-1 text-xs text-ele-muted">
          Booked for {visitDayLabel(bookedYmd)}. Choose the day you were actually on site.
          {!bookedInWindow && " That day is more than 2 weeks ago: if you were there then, contact the office, who will record it."}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" aria-pressed={isToday} className={chip(isToday)} onClick={() => pick(todayYmd)}>
          Today
        </button>
        {showYesterday && (
          <button type="button" aria-pressed={isYesterday} className={chip(isYesterday)} onClick={() => pick(yesterday)}>
            Yesterday
          </button>
        )}
        {bookedInWindow && (
          <button type="button" aria-pressed={isBooked} className={chip(isBooked)} onClick={() => pick(bookedYmd)}>
            {visitDayLabel(bookedYmd)}
            {bookedLate && <span className="ml-1.5 text-[10px]">Late</span>}
          </button>
        )}
        {!overdue && (
          <button type="button" aria-pressed={isOther} className={chip(isOther)} onClick={() => setPicking((v) => !v)}>
            Pick date
          </button>
        )}
      </div>
      {overdue && (
        <button
          type="button"
          aria-pressed={isOther}
          onClick={() => setPicking((v) => !v)}
          className={`mt-2 min-h-11 w-full rounded-xl border px-2 py-2 font-menu text-sm transition-colors ${
            isOther ? "border-[#7d21c7] bg-[#7d21c7] text-white" : "border-ele-card-border bg-ele-field text-ele-muted"
          }`}
        >
          Pick date
        </button>
      )}
      {/* Session BBC: Pick date is a list of the days the app can take, two to VISIT_DATE_HARD_DAYS back (Today
          and Yesterday are the chips above), never the phone's calendar: a calendar cannot mark the days that
          need a reason, and an older day is not offered at all. */}
      {picking && (
        <div role="group" aria-label="Visit date" className="mt-2 grid grid-cols-2 gap-2">
          {pickableDays.map((ymd) => {
            const late = calendarDaysBetween(ymd, todayYmd) > VISIT_DATE_SOFT_DAYS;
            const active = chosen && visitYmd === ymd;
            return (
              <button key={ymd} type="button" aria-pressed={active} className={chip(active)} onClick={() => pick(ymd)}>
                {visitDayLabel(ymd)}
                {late && (
                  <span
                    className="ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] leading-none"
                    style={active ? undefined : { color: "var(--ele-amber-text)", backgroundColor: "var(--ele-pill-tint-amber)" }}
                  >
                    Late
                  </span>
                )}
              </button>
            );
          })}
          <p className="col-span-2 text-xs text-ele-muted">
            A day marked Late needs a reason. For a visit more than 2 weeks ago, contact the office, who will record it.
          </p>
        </div>
      )}
      {chosen && visitYmd < todayYmd && (
        <p className="mt-2 text-xs" style={{ color: "var(--ele-amber-text)" }}>
          A visit on an earlier day: enter the time you arrived and the time you left.
        </p>
      )}
    </div>
  );
}

// Session BBC: why a visit more than VISIT_DATE_SOFT_DAYS back is being sent late (Director, 8 October
// 2026). Four large choices; Other opens a short box for their own words. Submit stays disabled until one
// is given (lateReasonGiven). The office sees the reason beside "Sent N days after the visit".
function LateReasonStep({
  days,
  reason,
  text,
  onReason,
  onText,
}: {
  days: number;
  reason: string;
  text: string;
  onReason: (code: string) => void;
  onText: (text: string) => void;
}) {
  const id = useId();
  return (
    <div className="mb-4 rounded-xl border border-ele-card-border p-3" style={{ backgroundColor: "var(--ele-pill-tint-amber)" }}>
      <p id={id} className="font-heading text-base text-ele-strong">
        Why is this being sent late?
      </p>
      <p className="mb-2 text-xs text-ele-body">This visit was {days} days ago. The office sees your answer.</p>
      <div role="radiogroup" aria-labelledby={id} className="flex flex-col gap-2">
        {LATE_REASON_CODES.map((code) => {
          const active = reason === code;
          return (
            <button
              key={code}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onReason(code)}
              className={`min-h-12 rounded-xl border px-3 py-2 text-left font-menu text-sm transition-colors ${
                active ? "border-[#7d21c7] bg-[#7d21c7] text-white" : "border-ele-card-border bg-ele-field text-ele-strong"
              }`}
            >
              {LATE_REASONS[code]}
            </button>
          );
        })}
      </div>
      {reason === "other" && (
        <textarea
          aria-label="Reason for sending late"
          maxLength={LATE_REASON_TEXT_MAX}
          rows={2}
          value={text}
          onChange={(e) => onText(e.target.value)}
          placeholder="A few words"
          className="mt-2 w-full rounded-xl border border-ele-field-border bg-ele-field px-3 py-2 text-base text-ele-strong outline-none focus:border-ele-field-focus"
        />
      )}
    </div>
  );
}

// Time on site (Session ADJ; redesigned + moved above the checklist in Session AFF): two columns
// (Arrived / Left) with one-tap NOW, and a live duration line -- the total once both are set, a
// running "and counting" while only arrival is set, or a prompt when neither is. Optional on every
// job type, and since Session APF the ONE time capture for all of them. What a blank field comes to
// depends on the job (a service visit falls back to when it was opened and submitted; a call-out,
// a repair or a tech visit with no Arrived time records no times at all) and is decided by
// resolveVisitTimes, never in here. The card only collects the two values.
//
// Session BAR: it also says which DAY (VisitDateRow, above the times), and the two times stack one
// above the other so each has room for Now, the field and its quick picks at a phone's width.
//
// `crossesMidnight` (Session APF) is the out-of-hours flag of a call-out-style sheet, and it exists
// only so the duration line tells the truth. The line is worked out from two clock faces, and an
// out-of-hours call-out can run from 23:00 to 00:30, where the later time is the SMALLER number.
// Before call-outs used this card that could not happen; without this the line would read "0m on
// site" over a visit of an hour and a half. Same rule as the record itself: only out of hours, and
// never for two equal times.
export function TimeOnSiteCard({
  arrival,
  departure,
  onArrival,
  onDeparture,
  crossesMidnight = false,
  visitYmd,
  todayYmd,
  bookedYmd,
  visitDayChosen = true,
  bookedTime = "",
  onVisitDate,
  lateReason = "",
  lateReasonText = "",
  onLateReason = () => {},
  onLateReasonText = () => {},
}: {
  arrival: string;
  departure: string;
  onArrival: (v: string) => void;
  onDeparture: (v: string) => void;
  crossesMidnight?: boolean;
  visitYmd: string;
  todayYmd: string;
  bookedYmd: string;
  // False while an overdue job's day has not been tapped yet (no day is preselected for one).
  visitDayChosen?: boolean;
  // The time of day the job was booked for, "HH:MM", or "" (a midnight booking means no time).
  bookedTime?: string;
  onVisitDate: (ymd: string) => void;
  // Session BBC: the late reason, asked here for a visit more than a week back.
  lateReason?: string;
  lateReasonText?: string;
  onLateReason?: (code: string) => void;
  onLateReasonText?: (text: string) => void;
}) {
  const pastVisit = visitYmd < todayYmd;
  const counting = !!arrival && !departure && !pastVisit;
  // Re-render every minute while counting, so the running duration ticks up. Cleaned up on unmount
  // and when counting stops.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!counting) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, [counting]);

  // Minutes from one clock face to the next, carried over midnight only where a visit may cross it.
  const span = (from: string, to: string): number | null => {
    const diff = minutesBetweenHHMM(from, to);
    return diff != null && diff < 0 && crossesMidnight ? diff + 24 * 60 : diff;
  };

  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const nowHHMM = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  let durationLine: React.ReactNode;
  if (arrival && departure) {
    const diff = span(arrival, departure);
    durationLine = <span className="font-heading text-[var(--ele-green-ink)]">{formatDuration(diff ?? 0)} on site</span>;
  } else if (arrival && !pastVisit) {
    const elapsed = span(arrival, nowHHMM);
    durationLine = <span className="text-[var(--ele-loler-ink)]">{formatDuration(Math.max(0, elapsed ?? 0))} and counting</span>;
  } else if (pastVisit) {
    durationLine = <span className="text-ele-muted">Enter the times for {visitDayLabel(visitYmd)}</span>;
  } else {
    durationLine = <span className="text-ele-muted">Tap NOW when you arrive</span>;
  }

  // The quick picks are built round the visit: now for today, otherwise the booked time of day.
  const arrivalAnchor = pastVisit ? (bookedTime && bookedTime !== "00:00" ? bookedTime : "09:00") : nowHHMM;
  const leftAnchor = pastVisit ? (arrival ? shiftHHMM(arrival, 60) : "10:00") : nowHHMM;

  return (
    <section className="mb-5 rounded-xl border border-ele-card-border bg-ele-card p-4 shadow-[0_1px_3px_var(--ele-shadow)]">
      <h2 className="mb-3 font-heading text-lg text-ele-blue">Time on site</h2>
      <VisitDateRow visitYmd={visitYmd} todayYmd={todayYmd} bookedYmd={bookedYmd} chosen={visitDayChosen} onVisitDate={onVisitDate} />
      {visitDayChosen && visitAgeBand(calendarDaysBetween(visitYmd, todayYmd)) === "late" && (
        <LateReasonStep
          days={calendarDaysBetween(visitYmd, todayYmd)}
          reason={lateReason}
          text={lateReasonText}
          onReason={onLateReason}
          onText={onLateReasonText}
        />
      )}
      <div className="grid grid-cols-1 gap-5 border-t border-ele-hairline pt-4">
        <TimeColumn label="Arrived" value={arrival} onChange={onArrival} showNow={!pastVisit} anchor={arrivalAnchor} />
        <TimeColumn label="Left" value={departure} onChange={onDeparture} showNow={!pastVisit} anchor={leftAnchor} sameAs={arrival || undefined} />
      </div>
      <p className="mt-4 rounded-lg bg-ele-inset px-3 py-2 text-sm">{durationLine}</p>
    </section>
  );
}

// TRAVEL TO THIS JOB (Session ANT). One number, typed once, at the point the engineer opens the
// job: "how long did it take you to get here". It rides the completion payload like every other
// field on this screen, so it survives a basement with no signal.
//
// DELIBERATELY NOT A START/STOP TIMER. Simpro and Joblogic both capture travel as a live time block
// the engineer starts and stops on the job card, which solves a problem ELE does not have (real-time
// location and an automatic duration). The Director described the actual working pattern as a number
// somebody states: "logging on to a job... how long did it take you to get there", 30, 45, whatever
// they type. A running clock would also be wrong more often than not here, since it only measures
// travel the engineer remembered to start.
//
// THE RETURN LEG IS ASKED FOR, NEVER EXPLAINED. It appears only when the platform's own record of
// the day says this is the engineer's only job, so nobody on site has to know the rule or work out
// whether it applies. When it does not appear, that is the answer: the drive between two jobs is the
// NEXT job's outbound leg. The server re-decides at office approval over the day's finished record,
// so a call-out raised after this phone last synced changes what is recorded, not what was asked.
// Session BAS: the last travel figure this phone sent, so the next sheet can offer it as one tap
// ("Same as last job") instead of making the engineer type it again. Per device and best-effort: it
// is a convenience, never a record, and it is only ever OFFERED, never filled in on its own.
const LAST_TRAVEL_KEY = "ele.engineer.lastTravelMins";
export function rememberLastTravelMins(mins: number): void {
  try {
    localStorage.setItem(LAST_TRAVEL_KEY, String(mins));
  } catch {
    /* private mode or storage full: nothing to do */
  }
}
export function readLastTravelMins(): number | null {
  try {
    const n = Number(localStorage.getItem(LAST_TRAVEL_KEY));
    return Number.isInteger(n) && n >= 1 && n <= 1440 ? n : null;
  } catch {
    return null;
  }
}

function TravelCard({
  minutes,
  onMinutes,
  returnMinutes,
  onReturnMinutes,
  askReturn,
}: {
  minutes: string;
  onMinutes: (v: string) => void;
  returnMinutes: string;
  onReturnMinutes: (v: string) => void;
  askReturn: boolean;
}) {
  const [lastMins] = useState(readLastTravelMins);
  // While the box is empty, the common answers are one tap each (and the last job's, when known).
  const picks = [15, 30, 45, 60];
  const pickChip = "min-h-11 flex-1 rounded-ele border border-ele-card-border bg-ele-field px-2 font-menu text-sm text-ele-strong";
  return (
    <section className="mb-5 rounded-lg border border-ele-card-border bg-ele-card p-4">
      <h2 className="mb-1 font-heading text-lg text-ele-blue">Travel</h2>
      <label className="mb-1 mt-3 block font-menu text-[11px] uppercase tracking-wider text-ele-muted" htmlFor="job-travel-mins">
        How long did it take you to get here?
      </label>
      <div className="flex items-center gap-2">
        <input
          id="job-travel-mins"
          type="number"
          inputMode="numeric"
          min={1}
          max={1440}
          value={minutes}
          onChange={(e) => onMinutes(e.target.value)}
          placeholder="30"
          className="min-w-0 flex-1 rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 font-heading text-xl text-ele-strong outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
        />
        <span className="shrink-0 font-menu text-xs uppercase tracking-wider text-ele-muted">minutes</span>
      </div>
      {minutes.trim() === "" && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="travel-picks">
          {lastMins != null && (
            <button type="button" className={`${pickChip} basis-full`} onClick={() => onMinutes(String(lastMins))}>
              Same as last job: {lastMins} min
            </button>
          )}
          {picks.map((m) => (
            <button key={m} type="button" className={pickChip} onClick={() => onMinutes(String(m))} aria-label={`${m} minutes`}>
              {m}
            </button>
          ))}
        </div>
      )}
      {askReturn && (
        <>
          <label className="mb-1 mt-4 block font-menu text-[11px] uppercase tracking-wider text-ele-muted" htmlFor="job-return-travel-mins">
            And how long to get home?
          </label>
          <div className="flex items-center gap-2">
            <input
              id="job-return-travel-mins"
              type="number"
              inputMode="numeric"
              min={1}
              max={1440}
              value={returnMinutes}
              onChange={(e) => onReturnMinutes(e.target.value)}
              placeholder="30"
              className="min-w-0 flex-1 rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 font-heading text-xl text-ele-strong outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
            />
            <span className="shrink-0 font-menu text-xs uppercase tracking-wider text-ele-muted">minutes</span>
          </div>
          {/* Says WHY it is being asked here and not on every job, so the engineer is never left
              guessing whether they should have filled it in on the last one. */}
          {/* Session RC1: from 26 October 2026 a subcontractor's journey home is never paid (travel is one way), so this line
              says only why the field appears and states no pay rule. */}
          <p className="mt-2 text-xs text-ele-muted">This is your only job today, so we also ask how long your journey home took.</p>
        </>
      )}
    </section>
  );
}

// The reactive call-out job sheet: the reported fault (read-only), what the engineer
// found and did, the lift status on leaving, and whether the visit was out of hours.
//
// Session APF: THE ON-SITE TIMES ARE NO LONGER CAPTURED IN HERE. They were two 15-minute dropdowns
// of 65 options each (96 out of hours), which the PWA audit measured at about 16 seconds a sheet and
// which could not hold a real time such as 10:31 at all. A call-out and a repair now use the same
// Time on site card as every other job, near the top of the sheet, where a POP tag tap can pre-fill
// it. The out-of-hours tick stays here because it is a fact about the VISIT, not about a time field:
// it no longer has any picked times to clear, and its one remaining effect on the times is to allow
// a visit to cross midnight (see timesOutOfOrder and resolveVisitTimes).
//
// Session APF, THE DIRECTOR RULE (21 September 2026): "No price, charge, rate or cost figure on any
// engineer- or customer-facing job sheet ... in any of the three flows." This form used to work out
// an "Indicative charge" from a "Travel hours (for the estimate)" field of its own (never part of
// the completion payload), and the audit found it priced a REPAIR as a call-out -- the fix is not a
// truer figure, it is no figure at all, so both are gone along with the closing paragraph that
// explained them. Removing the travel-hours field also closes audit item 11 (travel was being asked
// twice): the real travel question is TravelCard's "How long did it take you to get here?", which
// stays, on every job type, exactly where it always was. The out-of-hours checkbox stays too, and is
// NOT a price: it is a fact about when the visit happened, which the office's draft invoice for a
// repair or a tech visit is priced from, so removing the control would silently under-record weekend
// work. Its label no longer says "Doubles labour" -- that was the rate statement.
//
// Session APF, THE REPAIR FORM (audit finding 6 / fix 8): a repair and a tech visit ride this same
// form (`isRepair`, true for either: the parent's own shared "is this remedial work" question), and
// it used to open by asking "Fault found on site" for work the office had already described when it
// raised the job. isRepair swaps the read-only box to "Work planned" and reorders the fields to lead
// with what actually needs recording -- the work carried out, the lift status -- ahead of an
// OPTIONAL "anything else you found", rather than opening with a question the office can already
// answer for them. A call-out keeps its own order and labels exactly as before: it has no planned
// work to display, only a fault the customer reported. Every field stays FREE TEXT in both shapes
// (Director decision, 21 September 2026: no preset fault list, no dropdown of common faults), and
// the payload is unchanged: `faultFound` is simply optional, as it always was.
function CalloutSheetFields({
  heading = "Call-out report",
  isRepair = false,
  isReturnVisit = false,
  faultReported,
  ooh,
  onOoh,
  faultFound,
  onFaultFound,
  workCarriedOut,
  onWorkCarriedOut,
  liftStatus,
  onLiftStatus,
}: {
  // Session AJW: a planned repair rides this SAME form, headed "Repair report" instead of "Call-out
  // report". Optional and defaults to the original heading, so the one other caller (none currently
  // omit it, but a future one might) is unaffected.
  heading?: string;
  // Session APF: true for a repair or a tech visit (the parent's own `isRepair`, which asks the
  // shared "is this remedial work" question and so is true for both). Changes the read-only box's
  // label and empty text, and the order the fields render in; never the payload shape.
  isRepair?: boolean;
  // Session RC2: true for a REPAIR only (a return visit), never a tech visit (isRepair is true for both, lib/jobType.ts).
  // A repair is out of hours from 18:00 on a weekday, at weekends and on bank holidays (Session RC1); a call-out and a
  // tech visit keep the 09:00 to 17:00 rule. Changes the label only, never what the toggle stores.
  isReturnVisit?: boolean;
  faultReported: string | null;
  ooh: boolean;
  onOoh: (v: boolean) => void;
  faultFound: string;
  onFaultFound: (v: string) => void;
  workCarriedOut: string;
  onWorkCarriedOut: (v: string) => void;
  liftStatus: string;
  onLiftStatus: (v: string) => void;
}) {
  const inputClass = "w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]";

  // Identical in both shapes, so declared once rather than risking the two copies drifting apart.
  const workCarriedOutField = (
    <Field label="Work carried out">
      <textarea value={workCarriedOut} onChange={(e) => onWorkCarriedOut(e.target.value)} rows={3} className={inputClass} />
    </Field>
  );
  const liftStatusField = (
    <ReadOnlyField label="Lift status on leaving site">
      <ChoiceChips
        label="Lift status on leaving site"
        value={liftStatus}
        onChange={onLiftStatus}
        options={LIFT_STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
      />
    </ReadOnlyField>
  );

  return (
    <section className="mb-5">
      <h2 className="mb-3 font-heading text-lg text-ele-blue">{heading}</h2>

      {/* Not a Field: this DISPLAYS the fault reported or the work planned, it does not collect
          anything. See ReadOnlyField's own note (Session AMF). */}
      <ReadOnlyField label={isRepair ? "Work planned" : "Fault reported"}>
        <div className="rounded-ele border border-ele-card-border bg-ele-card px-3 py-2 text-sm text-ele-strong">
          {faultReported?.trim() ? faultReported : isRepair ? "No description recorded." : "No fault description recorded."}
        </div>
      </ReadOnlyField>

      {isRepair ? (
        <>
          {workCarriedOutField}
          {liftStatusField}
          <Field label="Anything else you found (optional)">
            <textarea value={faultFound} onChange={(e) => onFaultFound(e.target.value)} rows={3} className={inputClass} />
          </Field>
        </>
      ) : (
        <>
          <Field label="Fault found on site">
            <textarea value={faultFound} onChange={(e) => onFaultFound(e.target.value)} rows={3} className={inputClass} />
          </Field>
          {workCarriedOutField}
          {liftStatusField}
        </>
      )}

      <label htmlFor="callout-ooh" className="mb-4 flex items-center gap-2 rounded-ele border border-ele-card-border bg-ele-card px-3 py-2">
        <input
          id="callout-ooh"
          type="checkbox"
          checked={ooh}
          onChange={(e) => onOoh(e.target.checked)}
        />
        {/* Session APF: "Doubles labour" was a rate statement on an engineer's screen and is gone
            with the price it described. The checkbox itself stays -- it records a FACT (when the
            visit happened), and the office's draft invoice for a repair or a tech visit is priced
            from it, so removing the control would silently under-record weekend work rather than
            merely hide what it costs. */}
        <span className="text-sm text-ele-body">
          {isReturnVisit ? "Out of hours (weekdays from 18:00, weekends, bank holidays)." : "Out of hours (weekends, bank holidays, before 09:00 or after 17:00)."}
        </span>
      </label>
    </section>
  );
}

// Parts used on the job. The engineer records each part as free text (description and quantity);
// the parts ride the completion payload, so they are recorded even when the job is completed
// offline. (The van-stock picker was retired from this sheet in Session AFF; the engineer's van
// stock list lives on the home screen and the office van-stock pages.)
//
// Session APF, THE DIRECTOR RULE (21 September 2026): no price figure belongs on an engineer's job
// sheet, so the "Unit cost (optional)" input, the " at £x" suffix on a listed part and the "Parts
// total" line are gone. THE COST IS NOT LOST, IT MOVES: the office's Service Record panel already
// edits a submitted part's unit cost when it reviews the sheet, which is the right moment to price a
// part (the office knows the negotiated or catalogue figure; the engineer on site does not), and the
// engineer's own history view (server/src/modules/engineer/jobSheetDetail.ts) has deliberately
// dropped `unitCost` since it shipped, on exactly this reasoning: "what was fitted, never what it
// cost." `PartUsedEntry.unitCost` therefore stays OPTIONAL on the type (lib/offline/types.ts,
// untouched by this change): a draft or a queued completion written by an earlier build of this
// screen may still carry one, and it must keep loading and submitting exactly as it always has. This
// component simply never writes one, and never shows one if a restored draft still has one.
function PartsUsed({ value, onChange }: { value: PartUsedEntry[]; onChange: (parts: PartUsedEntry[]) => void }) {
  const [adding, setAdding] = useState(false);
  const [ftDesc, setFtDesc] = useState("");
  const [ftQty, setFtQty] = useState(1);

  function addPart() {
    const desc = ftDesc.trim();
    if (!desc) return;
    const qty = Math.max(1, Math.trunc(ftQty || 1));
    onChange([...value, { description: desc, quantity: qty }]);
    setFtDesc("");
    setFtQty(1);
    setAdding(false);
  }

  function remove(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  return (
    <section className="mb-4">
      <h2 className="mb-2 font-heading text-lg text-ele-blue">Parts used</h2>

      {value.length > 0 ? (
        <ul className="mb-3 space-y-1">
          {value.map((p, i) => (
            <li key={i} className="flex items-center justify-between gap-2 rounded-ele border border-ele-card-border bg-ele-card px-3 py-2 text-sm">
              <span className="text-ele-strong">
                {p.quantity} x {p.description}
              </span>
              <button type="button" onClick={() => remove(i)} className="flex min-h-11 shrink-0 items-center px-2 font-menu text-[10px] tracking-wider text-ele-danger">
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        !adding && <p className="mb-3 text-sm text-ele-muted">No parts recorded.</p>
      )}

      {!adding ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="min-h-11 w-full rounded-ele border border-ele-blue py-2.5 text-center font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
        >
          + Add part
        </button>
      ) : (
        <div className="rounded-lg border border-ele-card-border bg-ele-card p-3">
          <label className="mb-1 block text-xs text-ele-body">Part description</label>
          <input
            value={ftDesc}
            onChange={(e) => setFtDesc(e.target.value)}
            placeholder="e.g. Door operator belt"
            className="mb-2 w-full rounded-ele border border-ele-field-border bg-ele-field px-2 py-2 text-sm outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
          />
          <div className="flex items-end gap-2">
            <div className="w-16">
              <label className="mb-1 block text-xs text-ele-body">Qty</label>
              <input
                type="number"
                min={1}
                value={ftQty}
                onChange={(e) => setFtQty(Number(e.target.value))}
                className="w-full rounded-ele border border-ele-field-border bg-ele-field px-2 py-2 text-sm outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
              />
            </div>
            <button
              type="button"
              onClick={addPart}
              disabled={!ftDesc.trim()}
              className="min-h-11 flex-1 rounded-ele bg-ele-green px-3 py-2 font-menu text-xs tracking-wide text-ele-bg disabled:opacity-50"
            >
              Add
            </button>
          </div>
          <button type="button" onClick={() => { setAdding(false); setFtDesc(""); setFtQty(1); }} className="mt-1 flex min-h-11 items-center font-menu text-xs tracking-wider text-ele-body underline opacity-80">
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}

// Pre-work risk assessment (Session AK Item 9; OPTIONAL since Session AEH D10). Offered above the
// service checklist but never a gate: the generic RAMS (ELE-RAMS-SVC-001) covers planned visits, so
// this is opt-in. If the engineer records one, they tick the applicable hazards, record control
// measures (mandatory once any hazard is ticked) and sign off ("I confirm I have assessed the risks").
const HAZARD_OPTIONS: Array<{ key: string; label: string }> = [
  { key: "hazardElectrical", label: "Electrical" },
  { key: "hazardFalling", label: "Working at height / falling" },
  { key: "hazardTrapped", label: "Entrapment / moving parts" },
  { key: "hazardManualHandling", label: "Manual handling" },
  { key: "hazardSlip", label: "Slips, trips and falls" },
  { key: "hazardPublic", label: "Public / occupied area" },
  { key: "hazardConfined", label: "Confined space" },
  { key: "hazardNoise", label: "Noise" },
];

function RiskAssessmentForm({
  hazards,
  onToggleHazard,
  other,
  onOther,
  control,
  onControl,
  error,
  onSignOff,
}: {
  hazards: Record<string, boolean>;
  onToggleHazard: (key: string) => void;
  other: string;
  onOther: (v: string) => void;
  control: string;
  onControl: (v: string) => void;
  error: string | null;
  onSignOff: () => void;
}) {
  return (
    <section className="mb-5">
      <h2 className="mb-1 font-heading text-lg text-ele-blue">Risk assessment</h2>
      <p className="mb-3 text-xs text-ele-body opacity-80">
        The generic RAMS (ELE-RAMS-SVC-001) covers planned visits. Add a site-specific assessment when this visit needs one: tick any hazards present, record the control measures, then sign off.
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {HAZARD_OPTIONS.map((h) => {
          const on = !!hazards[h.key];
          return (
            <button
              key={h.key}
              type="button"
              onClick={() => onToggleHazard(h.key)}
              aria-pressed={on}
              className={`flex items-center justify-between gap-2 rounded-ele border p-2.5 text-left text-sm ${
                on ? "border-amber-400 bg-ele-inset text-ele-strong" : "border-ele-hairline bg-ele-surface-soft text-ele-body"
              }`}
            >
              <span>{h.label}</span>
              <span className={`font-menu text-[10px] uppercase tracking-wide ${on ? "text-amber-400" : "text-ele-body opacity-60"}`}>{on ? "Yes" : "No"}</span>
            </button>
          );
        })}
      </div>
      <label className="mt-3 block text-sm text-ele-body">Other hazards</label>
      <input
        value={other}
        onChange={(e) => onOther(e.target.value)}
        className="mt-1 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
      />
      <label className="mt-3 block text-sm text-ele-body">Control measures</label>
      <textarea
        value={control}
        onChange={(e) => onControl(e.target.value)}
        rows={3}
        placeholder="How are the hazards being controlled?"
        className="mt-1 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
      />
      {error && (
        <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-danger/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      <button
        type="button"
        onClick={onSignOff}
        className="mt-3 w-full rounded-ele bg-ele-green py-3 font-heading text-base tracking-wide text-ele-bg transition hover:brightness-110"
      >
        I confirm I have assessed the risks
      </button>
    </section>
  );
}

// The status indicator on a checklist item: a subtle green tick when passing, a
// prominent coloured chip otherwise. Tapping the item (the parent button) cycles it.
function StatusChip({ status }: { status: ChecklistStatus }) {
  if (status === "pass") {
    return (
      <span className="flex shrink-0 items-center gap-1 font-menu text-[11px] uppercase tracking-wide text-ele-green opacity-80">
        <svg aria-hidden viewBox="0 0 20 20" className="h-3.5 w-3.5 fill-current">
          <path d="M8.3 13.7 4.6 10l1.4-1.4 2.3 2.3 5.7-5.7L15.4 6.6z" />
        </svg>
        Pass
      </span>
    );
  }
  const s = STATUS_STYLE[status];
  return (
    <span className={`shrink-0 rounded px-2.5 py-1 font-menu text-[11px] uppercase tracking-wide ${s.chip} ${s.chipText}`}>{s.label}</span>
  );
}
